import type { FontStyle, WidgetConfig, WidgetApiConfig, Service, Addon, BookingCredit } from './types';
import { fitPanelToViewport, stopFittingPanel } from './viewport-fit';
import { captureFocus, restoreFocus } from './focus-keep';
import { PortalState } from './state';
import { clientConfirmBooking, clientCheckinBooking, clientApplyOffer, fetchAvailability,
  loadWidgetConfig, loadWidgetData, loadPackageTemplates, loadMembershipPlans, loadProviders,
  submitBooking, validatePromoCode, fetchPendingBooking, lookupPhone, payDepositLink, textDepositLink, resolveDepositLink, cancelDepositLink,
  fetchClientProfile,
  fetchBookings, fetchSales, fetchSaleDetail, fetchWallet,
  fetchClientPackages, fetchClientMemberships,
  fetchFormSubmissions, fetchFormSubmissionDetail,
  requestOtp, verifyOtp, magicLogin, verifyBirthday, saveBookingBirthday, registerClient, setClientToken, getStoredSession, clearClientSession,
  clientCancelBooking, clientRescheduleBooking, clientRequestChange, clientWithdrawRequest,
  portalPurchasePackage, portalEnrollMembership, addCreditCard, setDefaultCreditCard, deleteCreditCard, payAccountBalance,
  fetchFormFillLink, createPortalFormSubmission, emailMyReceipt, fetchFormSubmissions as fetchPortalForms, savePortalNotes, saveMarketingPrefs,
  initAnalytics, trackWidgetEvent, trackSitePageView, stopWidgetAnalytics, getAnalyticsSessionId,
  fetchRecurringSeries, fetchRecurringDetail, skipRecurringOccurrence, cancelRecurringSeries,
  fetchReferralCode,
  fetchLoyaltyPoints,
  fetchPunchCards,
  sendBookingCallback,
  fetchBookingIcs,
  savePortalDetails, PORTAL_DETAIL_FIELDS,
  fetchClientPushStatus, subscribeClientPush, unsubscribeClientPush, testClientPush, fetchOwnPortalLink, vapidKeyBytes,
  postReviewPrompt, fetchPortalTags, setPortalTag, fetchReferralQr,
} from './api';
import type { ClientProfile } from './types';

/** The 'My details' draft from the record: every editable field as a string. */
function detailsDraftFrom(c: ClientProfile | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of PORTAL_DETAIL_FIELDS) out[k] = String((c as any)?.[k] ?? '');
  return out;
}

import { promotionCanBook, promotionMenuScope, offerCountdown, formatDate, formatCurrency, renderHeader, renderBreadcrumb, renderBody, renderFooter, renderModal, registrationNeedsPhone, lateChangeNote, depositRefundNote, policyEmbedMarkup, rescheduleCart } from './render';
import { paintEmbedPlaceholder, clearEmbedPlaceholder, embedLogoUrl } from './embed-placeholder';
import { initStripe, mountCardElement, destroyCardElement, createPaymentMethod, isStripeEnabled, mountWalletButton, destroyWalletButton, clearPendingWallet, withCardAssumedComplete, paymentUiReady, prewarmWallet } from './stripe';
import { saveDraft, loadDraft, clearDraft, applyDraft, DRAFT_STEPS } from './draft';
import { TIMING } from './constants';
import { awaitDepositLink, depositFromResponse, holdCountdown, holdSecondsLeft } from './deposit-step';
import { mountFormEmbed, openFormModal, closeFormModal, hostFont } from './form-embed';
import { mountMembershipEmbed } from './membership-embed';
import { serviceVerdict, providerVerdict, enhancementVerdict, type Verdict } from './membership-eligibility';
import { saveRecognition, loadRecognition, forgetRecognition, forgetRemembered } from './device-memory';
import { dismissedToday, fetchBanner, removeBanner, showBanner } from './welcome-banner';
import styles from './styles.css';

declare global {
  interface Window {
    BookingWidget: WidgetConfig;
  }
}

// ── CSS variable defaults injected once ───────────────────────────────────

const BASE_VARS = `
:root {
  --accent:        #B8623E;
  --accent-hover:  #A3552F;
  --accent-light:  #F2E4DC;
  --cream:         #F8F5F0;
  --cream-dark:    #F0EBE3;
  --espresso:      #1E1813;
  --brown-mid:     #5C4A3A;
  --sage:          #6B8F71;
  --border:        #E5DDD6;
  --border-dark:   #D4C8BE;
  --muted:         #8A7D73;
  --font-serif:    'Cormorant Garamond', Georgia, serif;
  --font-sans:     'DM Sans', system-ui, sans-serif;
  --radius:        8px;
  --sp-xs: 0.308em;
  --sp-sm: 0.615em;
  --sp-md: 1.077em;
  --sp-lg: 1.538em;
  --sp-xl: 2.154em;
  --radius-sm: 0.462em;
  --radius-lg: 0.923em;
  --radius-xl: 1.538em;
  --panel-width:   560px;
  --transition:    0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
`;

// ── Font presets ──────────────────────────────────────────────────────────

const FONT_PRESETS: Record<FontStyle, { fontSerif: string; fontSans: string; googleFontsUrl: string }> = {
  classic: {
    fontSerif: "'Cormorant Garamond', Georgia, serif",
    fontSans:  "'DM Sans', system-ui, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap',
  },
  modern: {
    fontSerif: "'Playfair Display', Georgia, serif",
    fontSans:  "'Inter', system-ui, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400&family=Inter:wght@300;400;500&display=swap',
  },
  luxe: {
    fontSerif: "'Libre Baskerville', Georgia, serif",
    fontSans:  "'Nunito Sans', system-ui, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito+Sans:opsz,wght@6..12,300;6..12,400;6..12,500&display=swap',
  },
  minimal: {
    fontSerif: "'Raleway', system-ui, sans-serif",
    fontSans:  "'Raleway', system-ui, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap',
  },
  organic: {
    fontSerif: "'Lora', Georgia, serif",
    fontSans:  "'Source Sans 3', system-ui, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;1,400&family=Source+Sans+3:wght@300;400;500&display=swap',
  },
};

// ── BookingWidget ─────────────────────────────────────────────────────────

/** Host of the page the widget is running on (analytics: hosted /book/ page vs an embed on the spa's site). */
function _pageHost(): string {
  try { return (window.location.host || '').replace(/^www\./, ''); } catch { return ''; }
}

/** True on the spa's own website: not our API host, not in a frame. */
function _onSpaSite(apiBase: string): boolean {
  try {
    if (window.top !== window.self) return false;
    const apiHost = new URL(apiBase, window.location.href).host;
    return !!window.location.host && window.location.host !== apiHost;
  } catch { return false; }
}

/** The plan ids behind a verdict — what analytics records, not what a client sees. */
function coveredPlanIds(v: Verdict): number[] {
  if (v.kind === 'advertise') return v.plans.map(p => Number(p.id));
  if (v.kind === 'covered') return [Number(v.plan.id)];
  return [];
}

/** The full-screen panel layout (styles.css `@media (max-width: 500px)`). */
function phoneLayout(): boolean {
  try { return window.matchMedia('(max-width: 500px)').matches; } catch { return false; }
}

class BookingWidget {
  private state!: PortalState;
  private cfg: WidgetConfig;
  private panelEl!: HTMLElement;
  private backdropEl!: HTMLElement;
  private headerEl!: HTMLElement;
  private breadcrumbEl!: HTMLElement;
  private bodyEl!: HTMLElement;
  private footerEl!: HTMLElement;
  private modalContainerEl!: HTMLElement;
  private _escHandler: ((e: KeyboardEvent) => void) | null = null;
  private _linkInterceptHandler: ((e: MouseEvent) => void) | null = null;

  /** Contact step: once a full number is typed, ask (anonymously) whether it
   *  books here. Known → the form collapses to the phone (their details are
   *  on file) and the deposit preview can honour a card on file. */
  private _phoneLookupSeq = 0;
  private _checkPhoneLookup(phone: string): void {
    const digits = phone.replace(/\D/g, '');
    if (this.state.clientId) return;                      // already signed in
    if (digits.length < 10) {
      if (this.state.knownClient) { this.state.knownClient = null; this.render(); }
      return;
    }
    if (this.state.knownClient && this.state.knownClient.phone.replace(/\D/g, '').slice(-10) === digits.slice(-10)) return;
    // The answer is about a number AND the service/provider it was asked about:
    // going back to switch provider has to re-ask, or a stale "doesn't cover
    // David" line survives the very fix it prompted.
    const svcId = this.state.curr.service?.id ?? '';
    const provId = this.state.curr.provider?.id ?? '';
    const lookupKey = `${digits}:${svcId}:${provId}`;
    if ((this.state as any)._phoneLookupDone === lookupKey) return;
    (this.state as any)._phoneLookupDone = lookupKey;

    // Concierge chat sessions keep their own verify flow.
    if (this.state.aiConversationId && this.state.aiSessionKey) {
      const base = this.state.apiBase.replace(/\/+$/, '');
      fetch(`${base}/api/v1/ai-booking/poll/?conversationId=${this.state.aiConversationId}&sessionKey=${this.state.aiSessionKey}&phone=${encodeURIComponent(phone)}&action=lookup`, {
        method: 'POST',
      }).then(r => r.json()).then(data => {
        if (data.found && data.clientId) {
          (this.state as any)._contactVerifyShow = true;
          (this.state as any)._contactVerifyName = data.maskedName;
          (this.state as any)._contactVerifyClientId = data.clientId;
          this.render();
        }
      }).catch(() => {});
      return;
    }

    if (!this.cfg.apiBase || this.cfg.apiBase === 'mock') return;
    const seq = ++this._phoneLookupSeq;
    this.state.phoneLookupBusy = true;
    this.updatePartial('lookup');
    const isFilterProvider = String(this.state.curr.provider?.type || '').startsWith('filter');
    lookupPhone(this.cfg.apiBase, phone, {
      serviceId: svcId || null,
      staffId: (provId && !isFilterProvider) ? provId : null,
    }).then(res => {
      if (seq !== this._phoneLookupSeq) return;           // a newer number was typed
      this.state.phoneLookupBusy = false;
      this.state.knownClient = res.known ? { phone, cardOnFile: !!res.cardOnFile } : null;
      // false = they hold a membership covering this service, and it does NOT
      // cover the provider they picked. null = no membership bears on it.
      this.state.membershipProviderMismatch = res.membershipCoversProvider === false;
      if (this.state.membershipProviderMismatch) {
        trackWidgetEvent('membership_provider_mismatch', { serviceId: svcId, staffId: provId });
      }
      if (res.known) trackWidgetEvent('returning_client_recognized', {});
      this.render();
    }).catch(() => {
      if (seq !== this._phoneLookupSeq) return;
      this.state.phoneLookupBusy = false;
      this.render();
    });
  }

  /** Cheap re-render of just the "checking your number…" line. */
  private updatePartial(_what: string): void {
    const el = this.panelEl.querySelector<HTMLElement>('.pp-bk-lookup-busy');
    if (el) el.textContent = this.state.phoneLookupBusy ? 'Checking your number…' : '';
  }

  private _verifyChatSession(): void {
    if (!this.state.aiConversationId || !this.state.aiSessionKey) return;
    const base = this.state.apiBase.replace(/\/+$/, '');
    fetch(`${base}/api/v1/ai-booking/poll/?conversationId=${this.state.aiConversationId}&sessionKey=${this.state.aiSessionKey}&since=0`)
      .then(r => r.json())
      .then(data => {
        if (data.closed) {
          // Conversation was closed/archived — clear local session
          this.state.clearChatSession();
          this.render();
        }
      })
      .catch(() => {});
  }

  private _createChatBubble(): void {
    const cfg = this.cfg.chatBubble!;
    const pos = cfg.position || 'right';
    const size = cfg.size || 'medium';
    const sizes: Record<string, number> = { small: 48, medium: 56, large: 64 };
    const iconSizes: Record<string, number> = { small: 22, medium: 26, large: 30 };
    const px = sizes[size] || 56;
    const iconPx = iconSizes[size] || 26;
    const accent = this.cfg.accentColor || '#B8623E';

    const wrap = document.createElement('div');
    wrap.style.cssText = `position:fixed;bottom:24px;${pos}:24px;z-index:99997;display:flex;flex-direction:column;align-items:${pos === 'left' ? 'flex-start' : 'flex-end'};gap:8px;`;

    // Greeting message
    if (cfg.greeting) {
      const greet = document.createElement('div');
      greet.style.cssText = 'background:#fff;border-radius:12px;padding:12px 16px;box-shadow:0 4px 16px rgba(0,0,0,0.15);font-size:14px;color:#333;max-width:260px;line-height:1.5;cursor:pointer;display:none;align-items:flex-start;gap:8px;font-family:system-ui,sans-serif;opacity:0;transform:translateY(10px);transition:opacity 0.3s,transform 0.3s;';
      const span = document.createElement('span');
      span.textContent = cfg.greeting;
      const close = document.createElement('button');
      close.textContent = '×';
      close.style.cssText = 'background:none;border:none;font-size:18px;color:#999;cursor:pointer;padding:0;line-height:1;flex-shrink:0;';
      close.addEventListener('click', (e) => { e.stopPropagation(); greet.style.display = 'none'; });
      greet.appendChild(span);
      greet.appendChild(close);
      greet.addEventListener('click', () => { greet.style.display = 'none'; this.cfg.openChat!(); });
      wrap.appendChild(greet);

      setTimeout(() => {
        greet.style.display = 'flex';
        requestAnimationFrame(() => { greet.style.opacity = '1'; greet.style.transform = 'translateY(0)'; });
      }, (cfg.greetingDelay ?? 3) * 1000);
    }

    // Bubble button
    const btn = document.createElement('button');
    btn.setAttribute('aria-label', 'Open chat');
    btn.style.cssText = `width:${px}px;height:${px}px;border-radius:50%;background:${accent};color:#fff;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 16px rgba(0,0,0,0.2);transition:transform 0.15s,box-shadow 0.15s;`;
    btn.innerHTML = `<svg width="${iconPx}" height="${iconPx}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>`;
    btn.addEventListener('mouseenter', () => { btn.style.transform = 'scale(1.08)'; btn.style.boxShadow = '0 6px 24px rgba(0,0,0,0.25)'; });
    btn.addEventListener('mouseleave', () => { btn.style.transform = ''; btn.style.boxShadow = '0 4px 16px rgba(0,0,0,0.2)'; });
    btn.addEventListener('click', () => { this.cfg.openChat!(); });
    wrap.appendChild(btn);

    document.body.appendChild(wrap);
  }

  private _scrollAiChat(): void {
    setTimeout(() => {
      const chat = this.panelEl.querySelector('#pp-ai-chat');
      if (chat) chat.scrollTop = chat.scrollHeight;
    }, 50);
  }

  private _startAiPoll(): void {
    if (this.state._aiPollTimer || !this.state.aiConversationId) return;
    const base = this.state.apiBase.replace(/\/+$/, '');
    this.state._aiPollTimer = setInterval(() => {
      if (!this.state.panelOpen || this.state.step !== 'book-ai') {
        clearInterval(this.state._aiPollTimer);
        this.state._aiPollTimer = null;
        return;
      }
      const url = `${base}/api/v1/ai-booking/poll/?conversationId=${this.state.aiConversationId}&sessionKey=${this.state.aiSessionKey}&since=${this.state.aiLastMsgId}`;
      fetch(url).then(r => r.json()).then(data => {
        if (data.closed) {
          // Staff closed the chat
          this.state.aiMessages.push({ role: 'assistant' as any, text: 'This chat has been closed. Thank you for reaching out!' });
          this.state.clearChatSession();
          clearInterval(this.state._aiPollTimer);
          this.state._aiPollTimer = null;
          this.render();
          this._scrollAiChat();
          return;
        }
        if (data.aiPaused && !this.state.aiPaused) {
          this.state.aiPaused = true;
          this.render();
        }
        if (data.messages?.length) {
          for (const sm of data.messages) {
            if (sm.id > this.state.aiLastMsgId) {
              this.state.aiMessages.push({ role: 'staff' as any, text: sm.body });
              this.state.aiLastMsgId = sm.id;
            }
          }
          this.render();
          this._scrollAiChat();
        }
      }).catch(() => {});
    }, 10000);
  }

  constructor(cfg: WidgetConfig) {
    this.cfg = cfg;
  }

  async init(): Promise<void> {
    try {
      await this._doInit();
    } catch (err) {
      console.error('[BookingWidget] Failed to initialize widget:', err);
      this.injectStyles();
      this.buildDOM();
      const body = this.panelEl?.querySelector('.pp-body');
      if (body) {
        body.innerHTML = `<div style="text-align:center;padding:60px 20px;color:#888;">
          <p style="font-size:15px;margin:0 0 8px">Unable to load widget.</p>
          <p style="font-size:13px;margin:0">Please try again later.</p>
        </div>`;
      }
    }
  }

  /** /book/ (quickBook 'auto'): is the same-day surface worth leading with right now? */
  private async _sameDayHasOpenings(): Promise<boolean> {
    try {
      const base = (this.cfg.apiBase || '').replace(/\/+$/, '');
      if (!base) return false;
      const params = new URLSearchParams();
      if (this.cfg.locationId) params.set('location_id', String(this.cfg.locationId));
      const resp = await fetch(`${base}/api/v1/quick-book/today/${params.toString() ? '?' + params : ''}`);
      if (!resp.ok) return false;
      const d = await resp.json();
      return !!(d && d.enabled && Array.isArray(d.slots) && d.slots.length > 0);
    } catch { return false; }
  }

  /** Once the same-day surface has been swapped out, run the regular flow inline. */
  private _standaloneSwapToFull = false;
  private _returnToToday: (() => void) | null = null;

  /** Hosted page (/book/, /portal/): the panel IS the page. */
  private _startStandalonePage(): void {
    document.body.classList.add('widget-standalone');
    this.state.panelOpen = true;
    // What a hosted page IS: /portal/ asks for the client portal (login or
    // dashboard); everything else (/book/) is the booking flow. Without this
    // the default step ('login') made the shareable /book/ link land on the
    // portal sign-in — a "book now" link that asked for a phone + birthday.
    if (this.cfg.memberships) {
      // Hosted /<tenant>/memberships/ — the public membership purchase page
      // the backoffice "share this link" points at.
      this.state.openMemberships();
      trackWidgetEvent('widget_open', { entryMode: 'membership', surface: 'standalone', pageHost: _pageHost() });
    } else if (this.cfg.giftCard) {
      // Hosted /<tenant>/giftcards/ — the Website Integration gift-card link.
      this.state.openGiftCard();
      trackWidgetEvent('widget_open', { entryMode: 'giftcard', surface: 'standalone', pageHost: _pageHost() });
    } else if (this.cfg.entryMode === 'portal' && this.state.portalEnabled) {
      this.state.entryMode = 'portal';
      this.state.step = this.state.clientId ? 'dashboard' : 'login';
      this.state.stepHistory = [];
      trackWidgetEvent('widget_open', { entryMode: 'portal', surface: 'standalone', pageHost: _pageHost() });
    } else {
      this.state.entryMode = 'booking';
      this.state.startBooking(false);
      this._offerDraft();
      trackWidgetEvent('widget_open', { entryMode: 'booking', surface: 'standalone', pageHost: _pageHost() });
    }
  }

  private async _doInit(): Promise<void> {
    // Embedded hosts sit blank through the config fetch + the on-demand bundle
    // download — paint a lightweight live-loading placeholder right away (the
    // real mount overwrites it; a host with fallback content is left alone).
    document.querySelectorAll<HTMLElement>('[data-calendoula-today]').forEach(el =>
      paintEmbedPlaceholder(el, el.getAttribute('data-calendoula-today') === 'banner' ? 'banner' : 'today', embedLogoUrl(this.cfg.apiBase || '')));
    document.querySelectorAll<HTMLElement>('[data-calendoula-shop]').forEach(el =>
      paintEmbedPlaceholder(el, 'shop', embedLogoUrl(this.cfg.apiBase || '')));
    document.querySelectorAll<HTMLElement>('[data-calendoula-memberships]').forEach(el =>
      paintEmbedPlaceholder(el, 'memberships', embedLogoUrl(this.cfg.apiBase || '')));
    document.querySelectorAll<HTMLElement>('[data-calendoula-menu]').forEach(el =>
      paintEmbedPlaceholder(el, 'menu', embedLogoUrl(this.cfg.apiBase || '')));
    document.querySelectorAll<HTMLElement>('[data-calendoula-form]').forEach(el =>
      paintEmbedPlaceholder(el, 'form', embedLogoUrl(this.cfg.apiBase || '')));

    // Merge remote config (spaName, accent, fonts, etc.). If the fetch fails
    // (e.g. network error, CORS), continue with defaults so the trigger
    // buttons further down still get wired up and the panel can at least
    // open and show an error state.
    let remoteCfg: WidgetApiConfig = {};
    try {
      remoteCfg = await loadWidgetConfig(this.cfg.apiBase);
    } catch (err) {
      console.error('[BookingWidget] Failed to load remote config:', err);
    }
    Object.assign(this.cfg, remoteCfg, this.cfg); // local overrides win

    this.state = new PortalState(this.cfg);
    this.state._onChangeCallback = () => this.render();
    if (remoteCfg.spaName) this.state.spaName = remoteCfg.spaName;
    if (remoteCfg.logoUrl) this.state.spaLogoUrl = remoteCfg.logoUrl;
    if (remoteCfg.businessPhone) this.state.businessPhone = remoteCfg.businessPhone;
    if (remoteCfg.businessPhoneActions) this.state.businessPhoneActions = remoteCfg.businessPhoneActions;
    if (remoteCfg.businessAddress) this.state.businessAddress = remoteCfg.businessAddress;
    if (remoteCfg.reviewPrompt && remoteCfg.reviewPrompt.url) this.state.reviewPrompt = remoteCfg.reviewPrompt;
    if (remoteCfg.onlineBookingSettings) {
      const obs = remoteCfg.onlineBookingSettings;
      // Legacy: allowGroupBookings/maxGroupSize may still be in onlineBookingSettings
      if (obs.allowGroupBookings !== undefined) this.state.allowGroupBookings = !!obs.allowGroupBookings;
      if (obs.maxGroupSize !== undefined) this.state.maxGroupSize = obs.maxGroupSize;
      // Feature toggles
      if (obs.allowMultipleServices !== undefined) this.state.allowMultipleServices = !!obs.allowMultipleServices;
      if (obs.allowSameServiceMultiple !== undefined) this.state.allowSameServiceMultiple = !!obs.allowSameServiceMultiple;
      if (obs.showBookForSomeoneElse !== undefined) this.state.showBookForSomeoneElse = !!obs.showBookForSomeoneElse;
      if (obs.onlyAllowSingleStaff !== undefined) this.state.onlyAllowSingleStaff = !!obs.onlyAllowSingleStaff;
      if (obs.promptBirthdayAfterBooking !== undefined) this.state.promptBirthdayAfterBooking = !!obs.promptBirthdayAfterBooking;
      const qb = (obs as Record<string, unknown>).quickBook as { rememberOnDevice?: boolean } | undefined;
      if (qb && qb.rememberOnDevice !== undefined) this.state.rememberOnDevice = !!qb.rememberOnDevice;
    }
    // Auth method (otp or birthday)
    if ((remoteCfg as any).authMethod) {
      this.state.authMethod = (remoteCfg as any).authMethod;
    }
    // Birthday prompt after booking (top-level fallback)
    if ((remoteCfg as any).promptBirthdayAfterBooking !== undefined) {
      this.state.promptBirthdayAfterBooking = !!(remoteCfg as any).promptBirthdayAfterBooking;
    }
    // Scheduling settings override (canonical location for group booking config)
    if (remoteCfg.schedulingSettings) {
      const ss = remoteCfg.schedulingSettings;
      if (ss.enableGroupAppointments !== undefined && ss.allowOnlineGroupBooking !== undefined) {
        this.state.allowGroupBookings = !!(ss.enableGroupAppointments && ss.allowOnlineGroupBooking);
      }
      if (ss.maxGroupSize !== undefined) this.state.maxGroupSize = ss.maxGroupSize;
      if (Array.isArray(ss.groupBookingLocationIds)) this.state.groupBookingLocationIds = ss.groupBookingLocationIds;
      if (Array.isArray(ss.groupBookingExcludedServiceIds)) this.state.groupBookingExcludedServiceIds = ss.groupBookingExcludedServiceIds;
    }
    if (remoteCfg.cancelRescheduleSettings) {
      const crs = remoteCfg.cancelRescheduleSettings;
      if (crs.selfCancelHours !== undefined) this.state.selfCancelHours = crs.selfCancelHours;
      if (crs.selfRescheduleHours !== undefined) this.state.selfRescheduleHours = crs.selfRescheduleHours;
      if (crs.cancellationPolicyEmail) this.state.cancellationPolicyHtml = crs.cancellationPolicyEmail;
    }
    // Note: Portal session TTL is handled at token creation (verify_otp response includes expiresInDays)

    // Client portal on/off (default on). Off → the widget is a pure booking tool.
    if (remoteCfg.clientPortalEnabled === false) {
      this.state.portalEnabled = false;
    }
    {
      const _ps = (remoteCfg as any).portalSections;
      if (_ps && typeof _ps === 'object') {
        this.state.portalSections = {
          packages: _ps.packages !== false,
          memberships: _ps.memberships !== false,
          accountBalance: _ps.accountBalance !== false,
        };
      }
      this.state.balancePaymentsEnabled = (remoteCfg as any).accountBalancePayments === true;
    }
    // Initialize referral program flag
    if (remoteCfg.referralEnabled) {
      this.state.referralEnabled = true;
    }
    // Initialize loyalty program flag
    if (remoteCfg.loyaltyEnabled) {
      this.state.loyaltyEnabled = true;
    }
    // Initialize calendar sync flag
    if (remoteCfg.calendarSyncEnabled) {
      this.state.calSyncEnabled = true;
    }
    // Initialize checkout products
    if (remoteCfg.checkoutProductsEnabled) {
      this.state.checkoutProductsEnabled = true;
    }
    // Shop-cart pickup: pay for the products now (see productPrepayOffered)
    const _rc = remoteCfg as any;
    if (_rc.retailPickupServiceId) this.state.retailPickupServiceId = Number(_rc.retailPickupServiceId) || null;
    this.state.retailPrepayEnabled = !!_rc.retailPrepayEnabled;
    // Initialize web chat + AI concierge flags
    if (remoteCfg.webChatEnabled) {
      this.state.webChatEnabled = true;
    }
    if (remoteCfg.aiConciergeEnabled) {
      this.state.aiConciergeEnabled = true;
      if (remoteCfg.aiConciergeChips?.length) {
        this.state.aiConciergeChips = remoteCfg.aiConciergeChips;
      }
    }
    // Restore previous chat session from localStorage
    if (this.state.webChatEnabled || this.state.aiConciergeEnabled) {
      if (this.state.restoreChatSession()) {
        // Verify the conversation is still active (not closed/archived)
        this._verifyChatSession();
      }
    }

    // Initialize Stripe if publishable key is available
    if (remoteCfg.stripePublishableKey) {
      this.state.stripePublishableKey = remoteCfg.stripePublishableKey;
      this.state.stripeAccountId = remoteCfg.stripeAccountId || '';
      initStripe(this.state.stripePublishableKey, this.state.stripeAccountId);
    }
    // Deposit collection method (payment_link or venmo)
    if (remoteCfg.depositCollectionMethod) {
      this.state.depositCollectionMethod = remoteCfg.depositCollectionMethod;
    }
    // Custom deposit-screen message + gift-card capture
    if (remoteCfg.depositPageNotice) {
      this.state.depositPageNotice = remoteCfg.depositPageNotice;
    }
    if (remoteCfg.depositGiftCard) {
      this.state.depositGiftCard = {
        enabled: !!remoteCfg.depositGiftCard.enabled,
        agreement: remoteCfg.depositGiftCard.agreement || '',
      };
    }

    // Initialize analytics
    initAnalytics(this.cfg.apiBase || '', this.cfg.analyticsSessionId);
    // Website analytics: a page view for each page of the SPA'S OWN site the
    // script runs on — never our hosted pages (/book/, /menu/, the landing
    // page on our host), never inside a frame (builder previews).
    if (remoteCfg.websiteAnalytics !== false && this.cfg.mode !== 'standalone' && _onSpaSite(this.cfg.apiBase || '')) {
      trackSitePageView();
    }

    // Restore persisted portal session — only when the portal is enabled. With
    // it off we never silently log a returning client in, so "Book" stays a
    // clean booking flow instead of hijacking them to their account.
    if (this.state.portalEnabled) {
      // A texted portal link (/portal/?magic=…) signs the client straight in
      // — the secret is traded for a normal session and scrubbed from the URL.
      await this.loginFromMagicLink();
      if (!this.state.clientId) await this.restoreSession(remoteCfg);
      // No portal session? A device that booked here before may still tell us
      // which plans this client holds — enough for the eligibility labels.
      if (!this.state.clientId) void this.loadRecognisedMemberships();
    }

    this.injectStyles();
    this.buildDOM();
    this.bindEvents();

    // /book/ with quickBook 'auto': lead with the same-day ("Open Today")
    // surface when the tenant runs Open Today rules AND something is actually
    // open right now; its "full menu" button/link swaps to the regular flow in
    // place. Otherwise the page is simply the regular booking flow.
    // A link that names a service (`?serviceId=` / `?service=` — a service's
    // own 'Book link', Tim 2026-10-20: 'the service links just load the same
    // day booking') is a request for THAT service: the regular flow opens on
    // it and the same-day surface stands down.
    const _q = new URLSearchParams(window.location.search);
    const _linkedService = !!(_q.get('serviceId') || _q.get('service'));
    if (this.cfg.mode === 'standalone' && this.cfg.quickBook === 'auto') {
      this.cfg.quickBook = (!_linkedService && await this._sameDayHasOpenings()) ? 'today' : undefined;
      if (this.cfg.quickBook === 'today') this._standaloneSwapToFull = true;
    }

    if (this.cfg.mode === 'standalone' && this.cfg.quickBook !== 'today' && !this.cfg.catalog && !this.cfg.priceList) {
      this._startStandalonePage();
    }

    // A restored / magic-link session opens on the home: hold it from the very
    // first paint (prefetchDashboardData releases it) so nothing flashes empty.
    if (this.state.clientId && this.cfg.apiBase && !this.state.dashboardLoaded) this.state.dashboardHolding = true;
    this.render();

    // Load booking data + packages in background
    this.loadBookingDataInBackground();
    this._watchInstallability();
    // A restored or magic-link session lands on the dashboard: fetch what its
    // cards say at a glance (upcoming, sessions left, gift cards, balance).
    if (this.state.clientId) void this.prefetchDashboardData();

    // Expose public API
    this.cfg.open = (opts?: Parameters<NonNullable<WidgetConfig['open']>>[0]) => {
      trackWidgetEvent('widget_open', { entryMode: opts?.section || 'booking', surface: 'embed', pageHost: _pageHost() });
      if (opts?.section === 'packages') {
        this.state.entryMode = 'booking';
        this.state.startBooking(false);
        this.state.step = 'pkg-list';
        this.state.openPanel();
        this.render();
        if (!this.state.bookingDataLoaded) this.ensureBookingData();
        return;
      }
      if (opts?.section === 'memberships') {
        this.state.entryMode = 'booking';
        this.state.startBooking(false);
        this.state.step = 'mem-list';
        // An embed's "Join" names the plan the visitor was reading about —
        // landing them back on the full list would make them pick it twice.
        if (opts.planId) this.state.pickMembership(Number(opts.planId));
        this.state.openPanel();
        this.render();
        if (!this.state.bookingDataLoaded) this.ensureBookingData();
        return;
      }
      // "Book" ALWAYS opens the booking flow — never silently hijack a
      // recognized/logged-in client to their account dashboard (that tangled up
      // returning clients and tanked booking conversion). The portal, when
      // enabled, is reached only via an explicit account link (openPortal).
      // A still-set clientId is fine here: the booking flow can use it to
      // prefill, it just doesn't change WHERE "Book" lands.
      this.state.entryMode = 'booking';
      this.state.startBooking(false);
      if (!opts?.serviceId && !opts?.products?.length && this._offerDraft()) {
        this.state.openPanel();
        this.render();
        if (!this.state.bookingDataLoaded) this.ensureBookingData();
        return;
      }
      if (opts?.categoryId != null) {
        this.state.expandedCat = String(opts.categoryId);
      }
      // A shop cart: the products ride along as the checkout picker's choices,
      // and the retail-pickup service is picked the moment the menu is known.
      if (opts?.products?.length) {
        this.state.selectedProducts = opts.products.map(p => ({
          id: Number(p.id), name: p.name || '', price: Number(p.price) || 0, quantity: Math.max(1, Number(p.quantity) || 1),
        }));
      }
      if (opts?.serviceId != null) {
        // Pick now when the menu is known and no location choice stands in the
        // way; otherwise the pick waits (setBookingData / selectLocation).
        if (this.state.bookingDataLoaded && !this.state.needsLocationSelection) this.state.pickService(String(opts.serviceId));
        else this.state.pendingServiceId = String(opts.serviceId);
      }
      this.state.openPanel();
      this.render();
      if (!this.state.bookingDataLoaded) this.ensureBookingData();
    };
    this.cfg.openPortal = () => {
      // Portal disabled → there is no account surface; fall back to booking so a
      // stray "My Account" link on the host page can't dead-end the visitor.
      if (!this.state.portalEnabled) { this.cfg.open!(); return; }
      trackWidgetEvent('widget_open', { entryMode: 'portal' });
      this.state.entryMode = 'portal';
      if (!this.state.clientId) {
        this.state.step = 'login';
        this.state.stepHistory = [];
      } else if (this._magicOpenedDetail && this.state.step === 'booking-detail') {
        // The page opened on the appointment a 'Send appointment details' link named — keep it.
        this._magicOpenedDetail = false;
      } else {
        // Return to dashboard when reopening portal (don't resume mid-booking)
        this.state.step = 'dashboard';
        this.state.stepHistory = [];
      }
      this.state.openPanel();
      this.render();
    };
    this.cfg.openGiftCard = () => {
      trackWidgetEvent('widget_open', { entryMode: 'giftcard' });
      this.state.entryMode = 'giftcard';
      this.state.openGiftCard();
      this.state.openPanel();
      this.render();
    };
    this.cfg.openChat = () => {
      trackWidgetEvent('widget_open', { entryMode: 'chat' });
      if (this.state.clientId) {
        // Logged into portal — navigate within portal context
        this.state.entryMode = 'portal';
        this.state.navigate('book-ai');
      } else {
        // Not logged in — booking flow context
        this.state.entryMode = 'booking';
        this.state.startBooking(false);
        this.state.step = 'book-ai';
        this.state.stepHistory = [];
      }
      this.state.openPanel();
      this.render();
      if (!this.state.bookingDataLoaded) this.ensureBookingData();
      setTimeout(() => this._scrollAiChat(), 100);
    };
    this.cfg.close = () => {
      this.state.closePanel();
      this.render();
    };
    (this.cfg as Record<string, unknown>).destroy = () => {
      this.destroy();
    };
    this.cfg.openWithLogin = (query: string) => {
      this.state.entryMode = 'portal';
      this.state.signOut();
      this.state.loginQuery = query;
      this.state.openPanel();
      this.render();
      this.handleLogin();
    };

    // ── Same-day quick booking ("Open Today") — on-demand bundle ─────────
    const qbDefaults = () => ({
      apiBase: this.cfg.apiBase || '',
      spaName: this.state.spaName,
      businessPhone: this.state.businessPhone || this.cfg.businessPhone || '',
      accentColor: this.cfg.accentColor,
      analyticsSessionId: getAnalyticsSessionId(),
      onOpenFull: () => { this.cfg.closeQuickBook?.(); this.cfg.open!(); },
    });
    this.cfg.mountQuickBook = async (el: HTMLElement, opts?: { locationId?: number; mode?: 'today'; layout?: 'full' | 'banner'; expanded?: boolean }) => {
      const layout = opts?.layout || 'full';
      paintEmbedPlaceholder(el, layout === 'banner' ? 'banner' : 'today', embedLogoUrl(this.cfg.apiBase || ''));
      const mod = await this._loadQuickBook();
      if (!mod) { clearEmbedPlaceholder(el); return undefined; }
      return mod.mount(el, { ...qbDefaults(), locationId: opts?.locationId ?? this.cfg.locationId ?? null, layout, expanded: !!opts?.expanded, surface: layout === 'banner' ? 'banner' : 'inline' });
    };
    this.cfg.openQuickBook = async (opts?: { locationId?: number }) => {
      const mod = await this._loadQuickBook();
      return mod?.openModal({ ...qbDefaults(), locationId: opts?.locationId ?? this.cfg.locationId ?? null, surface: 'modal' });
    };
    this.cfg.closeQuickBook = () => { window.BookingWidgetQuickBook?.closeModal(); };

    if (this.cfg.quickBook === 'today' && this.cfg.mode === 'standalone') {
      // The /today/ page: the quick-book surface IS the page; the panel stays
      // available (as the normal slide-out) for "see all times". On /book/
      // (auto) the escape hatch instead swaps the page to the regular flow.
      const host = document.createElement('div');
      host.id = 'pp-qb-standalone';
      document.body.prepend(host);
      const swap = this._standaloneSwapToFull;
      const mountToday = () => this._loadQuickBook().then(mod => mod?.mount(host, { ...qbDefaults(), onOpenFull, locationId: this.cfg.locationId ?? null, surface: 'standalone' }));
      const onOpenFull = swap
        ? () => {
            trackWidgetEvent('quick_book_open_full', { surface: 'standalone', swap: true });
            window.BookingWidgetQuickBook?.unmount?.(host);
            host.remove();
            this.cfg.quickBook = undefined;
            this.state.canReturnToToday = true;
            this._startStandalonePage();
            this.render();
            if (!this.state.bookingDataLoaded) this.ensureBookingData();
          }
        : qbDefaults().onOpenFull;
      // "← Today's openings" in the header: put the same-day surface back and
      // hide the regular flow again.
      this._returnToToday = () => {
        trackWidgetEvent('quick_book_return_today', {});
        this.state.canReturnToToday = false;
        this.state.panelOpen = false;
        document.body.classList.remove('widget-standalone');
        this.cfg.quickBook = 'today';
        document.body.prepend(host);
        mountToday();
        this.render();
      };
      mountToday();
    }
    // Retail catalog: <div data-calendoula-shop></div> on any page, or the
    // hosted /<tenant>/shop/ page (cfg.catalog). "Book a visit" opens the
    // normal booking panel in place of navigating away.
    this.cfg.mountCatalog = async (el: HTMLElement, opts?: { hideBookButton?: boolean; hideImages?: boolean; hideCart?: boolean }) => {
      paintEmbedPlaceholder(el, 'shop', embedLogoUrl(this.cfg.apiBase || ''));
      const mod = await this._loadCatalog();
      if (!mod) { clearEmbedPlaceholder(el); return; }
      await mod.mount(el, {
        apiBase: this.cfg.apiBase || '',
        surface: this.cfg.catalog && this.cfg.mode === 'standalone' ? 'standalone' : 'inline',
        hideBookButton: !!opts?.hideBookButton,
        hideImages: !!opts?.hideImages,
        hideCart: !!opts?.hideCart,
        onBook: (categoryId) => {
          trackWidgetEvent('catalog_book', { surface: this.cfg.catalog ? 'standalone' : 'inline', categoryId: categoryId ?? null });
          this.cfg.open!(categoryId != null ? { categoryId } : undefined);
        },
        onBookCart: (items, serviceId) => {
          trackWidgetEvent('catalog_cart_book', { surface: this.cfg.catalog ? 'standalone' : 'inline', itemCount: items.length });
          this.cfg.open!({ serviceId, products: items });
        },
      });
    };
    if (this.cfg.catalog && this.cfg.mode === 'standalone') {
      const host = document.createElement('div');
      host.id = 'pp-catalog-standalone';
      document.body.prepend(host);
      this.cfg.mountCatalog(host);
    }
    document.querySelectorAll<HTMLElement>('[data-calendoula-shop]').forEach(el => {
      this.cfg.mountCatalog!(el, { hideBookButton: el.hasAttribute('data-no-book'), hideImages: el.hasAttribute('data-no-images'), hideCart: el.hasAttribute('data-no-cart') });
    });

    // Public price list: <div data-calendoula-menu></div> on any page
    // (data-category="<id>" pins one category, data-no-book hides the Book
    // buttons, data-expanded opens every row), a pop-up from `.menu-btn` /
    // [data-calendoula-menu-button], or the hosted /<tenant>/menu/ page.
    // "Book" opens the booking panel with that service already picked.
    // The Book click itself is logged by the menu (menu_book_click, with the
    // service's name and any search that led to it); this only opens booking.
    const menuBook = (_surface: string) => (serviceId: number) => {
      this.cfg.open!({ serviceId });
    };
    const catAttr = (el: HTMLElement) => {
      const v = el.getAttribute('data-category');
      return v && /^\d+$/.test(v) ? Number(v) : undefined;
    };
    // `data-search-term="red light"` opens the menu already searched — a site
    // that talks about one treatment links straight to it (Tim, 2026-09-17).
    const searchAttr = (el: HTMLElement) =>
      (el.getAttribute('data-search-term') || el.getAttribute('data-search') || '').trim() || undefined;
    this.cfg.mountMenu = async (el: HTMLElement, opts?: { hideBookButton?: boolean; categoryId?: number; query?: string; expanded?: boolean }) => {
      paintEmbedPlaceholder(el, 'menu', embedLogoUrl(this.cfg.apiBase || ''));
      const mod = await this._loadPriceList();
      if (!mod) { clearEmbedPlaceholder(el); return; }
      const surface = this.cfg.priceList && this.cfg.mode === 'standalone' && el.id === 'pp-menu-standalone' ? 'standalone' : 'inline';
      await mod.mount(el, {
        apiBase: this.cfg.apiBase || '',
        surface,
        hideBookButton: !!opts?.hideBookButton,
        categoryId: opts?.categoryId ?? null,
        query: opts?.query || '',
        expanded: !!opts?.expanded,
        onBook: menuBook(surface),
        onEvent: trackWidgetEvent,
      });
    };
    this.cfg.openMenu = async (opts?: { categoryId?: number; query?: string }) => {
      const mod = await this._loadPriceList();
      if (!mod) { window.location.href = `${(this.cfg.apiBase || '').replace(/\/+$/, '')}/menu/`; return; }
      trackWidgetEvent('menu_open', { surface: 'modal', categoryId: opts?.categoryId ?? null, query: opts?.query || null });
      // `data-category` on the button OPENS on that category; the chips stay,
      // so the pop-up is still the whole menu (2026-09-17).
      mod.openModal({
        apiBase: this.cfg.apiBase || '', categoryId: opts?.categoryId ?? null,
        query: opts?.query || '', lockCategory: false, onBook: menuBook('modal'),
        onEvent: trackWidgetEvent,
      });
    };
    this.cfg.closeMenu = () => { window.BookingWidgetPriceList?.closeModal(); };
    if (this.cfg.priceList && this.cfg.mode === 'standalone') {
      const host = document.createElement('div');
      host.id = 'pp-menu-standalone';
      document.body.prepend(host);
      this.cfg.mountMenu(host);
    }
    document.querySelectorAll<HTMLElement>('[data-calendoula-menu]').forEach(el => {
      this.cfg.mountMenu!(el, {
        hideBookButton: el.hasAttribute('data-no-book'), categoryId: catAttr(el),
        query: searchAttr(el), expanded: el.hasAttribute('data-expanded'),
      });
    });
    document.querySelectorAll<HTMLElement>('.menu-btn, [data-calendoula-menu-button]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.cfg.openMenu!({ categoryId: catAttr(el), query: searchAttr(el) });
      });
    });

    // The cancellation policy, inline on the spa's own site:
    // `<div data-calendoula-policy>` (Tim, 2026-10-12). The same text the
    // hosted /policy/ page shows, from the widget config, so an edit in
    // Settings is live on the next page load. `data-no-title` drops the heading.
    document.querySelectorAll<HTMLElement>('[data-calendoula-policy]').forEach(el => {
      el.innerHTML = policyEmbedMarkup(this.state.cancellationPolicyHtml, {
        title: !el.hasAttribute('data-no-title'),
        pageUrl: this.cfg.apiBase ? `${this.cfg.apiBase.replace(/\/$/, '')}/policy/` : '',
      });
    });

    // "Welcome back" bar for a visitor this device knows (see welcome-banner.ts).
    void this._showWelcomeBanner();

    // Embedded external forms (whoFills='external' templates): inline
    // <div data-calendoula-form="CODE"></div>, or a modal trigger on ANY element
    // <button data-calendoula-form-button="CODE">Give feedback</button>.
    // CODE is the share-link code from Forms → "Link for sharing".
    this.cfg.mountForm = (el: HTMLElement, code: string) => mountFormEmbed(el, this.cfg.apiBase || '', code);
    this.cfg.openForm = (code: string) => openFormModal(this.cfg.apiBase || '', code);
    this.cfg.closeForm = () => closeFormModal();
    document.querySelectorAll<HTMLElement>('[data-calendoula-form]').forEach(el => {
      const code = el.getAttribute('data-calendoula-form');
      if (code) this.cfg.mountForm!(el, code);
    });
    document.querySelectorAll<HTMLElement>('[data-calendoula-form-button]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const code = el.getAttribute('data-calendoula-form-button');
        // The pop-up takes the font where its button sits (or the button's data-font).
        if (code) openFormModal(this.cfg.apiBase || '', code, hostFont(el));
      });
    });

    // Memberships: the plans render INLINE on the host page
    // (<div data-calendoula-memberships>, optionally data-plan="3" /
    // data-no-join), and "Join" hands over to the widget's purchase flow —
    // the embed sells, the widget takes the money.
    this.cfg.mountMemberships = async (el: HTMLElement, opts?: { planId?: number; hideJoin?: boolean; joinLabel?: string }) => {
      paintEmbedPlaceholder(el, 'memberships', embedLogoUrl(this.cfg.apiBase || ''));
      await mountMembershipEmbed(el, this.cfg.apiBase || '', (planId) => {
        trackWidgetEvent('membership_embed_join', { planId });
        this.cfg.openMemberships!(planId);
      }, opts || {});
    };
    this.cfg.openMemberships = (planId?: number) => this.cfg.open!({ section: 'memberships', planId });
    document.querySelectorAll<HTMLElement>('[data-calendoula-memberships]').forEach(el => {
      const plan = el.getAttribute('data-plan');
      this.cfg.mountMemberships!(el, {
        planId: plan && /^\d+$/.test(plan) ? Number(plan) : undefined,
        hideJoin: el.hasAttribute('data-no-join'),
        joinLabel: el.getAttribute('data-join-label') || undefined,
      });
    });
    // Trigger for the membership modal: `.membership-btn` on ANY element (the
    // same convention as `.book-btn`), or the data attribute.
    document.querySelectorAll<HTMLElement>('.membership-btn, [data-calendoula-memberships-button]').forEach(el => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        const plan = el.getAttribute('data-plan');
        this.cfg.openMemberships!(plan && /^\d+$/.test(plan) ? Number(plan) : undefined);
      });
    });

    // Any host element that asks for it: <div data-calendoula-today data-location="2"></div>
    // Banner (slim live strip that expands): <div data-calendoula-today="banner"></div>
    document.querySelectorAll<HTMLElement>('[data-calendoula-today]').forEach(el => {
      const loc = el.getAttribute('data-location');
      const layout = el.getAttribute('data-calendoula-today') === 'banner' ? 'banner' : 'full';
      this.cfg.mountQuickBook!(el, { locationId: loc && /^\d+$/.test(loc) ? Number(loc) : undefined, layout, expanded: el.hasAttribute('data-expanded') });
    });
    // Trigger for the same-day modal: class `.today-btn` on ANY element (button,
    // <a>, span — same convention as `.book-btn`), or the data attribute.
    document.querySelectorAll<HTMLElement>('.today-btn, [data-calendoula-today-button]').forEach(el => {
      el.addEventListener('click', (e) => { e.preventDefault(); this.cfg.openQuickBook!(); });
    });

    // Wire up trigger elements
    // `data-category="<id>"` on a Book trigger opens the flow with that
    // category already expanded — a "Facials" card on the site lands on
    // facials rather than the whole menu (2026-09-17).
    document.querySelectorAll<HTMLElement>('.book-btn').forEach(el => {
      el.addEventListener('click', () => {
        const cat = catAttr(el);
        this.cfg.open!(cat != null ? { categoryId: cat } : undefined);
      });
    });
    document.querySelectorAll<HTMLElement>('[data-portal]').forEach(el => {
      el.addEventListener('click', () => { this.cfg.openPortal!(); });
    });
    document.querySelectorAll<HTMLElement>('[data-gift-card]').forEach(el => {
      el.addEventListener('click', () => { this.cfg.openGiftCard!(); });
    });

    // Auto-create chat bubble if configured
    if (this.cfg.chatBubble?.enabled) {
      this._createChatBubble();
    }

    // Intercept left-clicks on links pointing to the API base URL
    if (this.cfg.apiBase) {
      const apiBase = this.cfg.apiBase;
      this._linkInterceptHandler = (e: MouseEvent) => {
        if (e.button !== 0) return;
        const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href]');
        if (!a) return;
        if (a.href === apiBase || a.getAttribute('href') === apiBase) {
          e.preventDefault();
          this.cfg.open!();
        }
      };
      document.addEventListener('click', this._linkInterceptHandler);
    }

    // Handle URL params for direct navigation
    const params = new URLSearchParams(window.location.search);
    const showOnlySCId = params.get('showOnlySCId');
    const serviceParam = params.get('serviceId') || params.get('service');
    // A friend's referral link (Tim, 2026-11-02): the code is applied as the
    // promo code once there is a cart to price it on (the contact step).
    const refParam = (params.get('ref') || '').trim();
    if (refParam) this._pendingPromo = refParam.slice(0, 40);
    const packageId = params.get('packageId');
    const packagesMode = params.get('packages');
    const locationParam = params.get('location');

    // Pre-select location from URL (shareable booking link)
    if (locationParam) {
      const locId = Number(locationParam);
      if (locId && this.state.bookingLocations.some(l => l.id === locId)) {
        this.state.selectedLocationId = locId;
        this.reloadProvidersForLocation(locId);
      }
    }

    if (serviceParam) {
      // A service's own booking link (Services → Online Booking → 'Book link',
      // Tim 2026-10-19 — texted to a client to book the retail pickup service
      // directly): straight to that service, waiting for the menu / location
      // like open({serviceId}) does. Unknown or not online → just the menu.
      if (this.state.bookingDataLoaded && !this.state.needsLocationSelection) this.state.pickService(String(serviceParam));
      else this.state.pendingServiceId = String(serviceParam);
    } else if (showOnlySCId) {
      const catId = String(showOnlySCId);
      this.state.categories = this.state.categories.filter(c => String(c.id) === catId);
      if (this.state.categories.length === 1) {
        this.state.expandedCat = String(this.state.categories[0].id);
      }
    } else if (packagesMode === 'true' || packagesMode === '1') {
      this.state.step = 'pkg-list';
    } else if (packageId) {
      const pid = Number(packageId);
      const pkg = this.state.packages.find(p => p.id === pid);
      if (pkg) {
        this.state.pickPackage(pid);
      }
    }

    this.render();
  }

  destroy(): void {
    // Remove document-level event listeners
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
      this._escHandler = null;
    }
    if (this._linkInterceptHandler) {
      document.removeEventListener('click', this._linkInterceptHandler);
      this._linkInterceptHandler = null;
    }
    // Stop analytics flush interval
    stopWidgetAnalytics();
    // Destroy Stripe card element
    destroyCardElement();
    destroyWalletButton();
    this.hideBusy();
    stopFittingPanel();
    // Remove DOM elements
    this.panelEl?.remove();
    this.backdropEl?.remove();
  }

  // ── Background data loading ──────────────────────────────────────────

  private async loadBookingDataInBackground(): Promise<void> {
    if (!this.cfg.apiBase || this.state.bookingDataLoaded) return;
    try {
      const [data, allPackages, allMembershipPlans] = await Promise.all([
        loadWidgetData(this.cfg.apiBase),
        loadPackageTemplates(this.cfg.apiBase),
        loadMembershipPlans(this.cfg.apiBase),
      ]);
      this.state.setBookingData(data);
      this.state.packages = allPackages;
      this.state.membershipPlans = allMembershipPlans;
      // Redirect to location picker if needed after data loads
      if (this.state.needsLocationSelection && this.state.step === 'book-categories' && !this.state.selectedLocationId) {
        this.state.step = 'book-location';
      }
      this.render();
    } catch (_e) {
      // Silent fail — data will be loaded on demand
    }
  }

  private async reloadProvidersForLocation(locationId: number): Promise<void> {
    if (!this.cfg.apiBase) return;
    try {
      const providers = await loadProviders(this.cfg.apiBase, locationId);
      this.state.providers = providers;
      this.render();
    } catch (_e) { /* silent */ }
  }

  private async ensureBookingData(): Promise<void> {
    if (this.state.bookingDataLoaded || !this.cfg.apiBase) return;
    this.state.bookingDataLoading = true;
    this.render();
    try {
      const [data, allPackages, allMembershipPlans] = await Promise.all([
        loadWidgetData(this.cfg.apiBase),
        loadPackageTemplates(this.cfg.apiBase),
        loadMembershipPlans(this.cfg.apiBase),
      ]);
      this.state.setBookingData(data);
      this.state.packages = allPackages;
      this.state.membershipPlans = allMembershipPlans;
      // If locations loaded and user is on categories but should be on location picker
      if (this.state.needsLocationSelection && this.state.step === 'book-categories' && !this.state.selectedLocationId) {
        this.state.step = 'book-location';
      }
    } catch (_e) {
      this.state.bookingError = 'Unable to load booking data. Please try again.';
    }
    this.state.bookingDataLoading = false;
    this.render();
  }

  // ── Styles ───────────────────────────────────────────────────────────

  private injectStyles(): void {
    if (document.getElementById('spa-widget-styles')) return;

    // Base variables + reset
    const baseStyle = document.createElement('style');
    baseStyle.id = 'spa-widget-base';
    baseStyle.textContent = BASE_VARS;
    document.head.appendChild(baseStyle);

    // Widget styles (from css-text import)
    const s = document.createElement('style');
    s.id = 'spa-widget-styles';
    s.textContent = styles;
    document.head.appendChild(s);

    // Font handling
    const preset = this.cfg.fontStyle ? FONT_PRESETS[this.cfg.fontStyle] : null;
    const fontsUrl  = this.cfg.googleFontsUrl ?? preset?.googleFontsUrl;
    const fontSerif = this.cfg.fontSerif      ?? preset?.fontSerif;
    const fontSans  = this.cfg.fontSans       ?? preset?.fontSans;

    if (fontsUrl) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = fontsUrl;
      document.head.appendChild(link);
    } else {
      // Default: load DM Sans
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap';
      document.head.appendChild(link);
    }

    const overrides: string[] = [];
    if (fontSerif)          overrides.push(`--font-serif: ${fontSerif};`);
    if (fontSans)           overrides.push(`--font-sans: ${fontSans};`);
    if (this.cfg.accentColor) overrides.push(`--pp-accent: ${this.cfg.accentColor};`);
    // Apply full widget color scheme from remote config
    const wc = this.cfg.widgetColors;
    if (wc) {
      const colorMap: Record<string, string> = {
        accent: '--pp-accent', accentHover: '--pp-accent-hover', accentLight: '--pp-accent-light',
        background: '--pp-cream', backgroundDark: '--pp-cream-dark', text: '--pp-espresso',
        textMuted: '--pp-muted', textMid: '--pp-brown-mid', border: '--pp-border', borderDark: '--pp-border-dark',
      };
      for (const [key, cssVar] of Object.entries(colorMap)) {
        if (wc[key]) overrides.push(`${cssVar}: ${wc[key]};`);
      }
    }
    // Widget color overrides must target #booking-widget-panel (where --pp-* vars are defined)
    const panelOverrides = overrides.filter(o => o.startsWith('--pp-'));
    const rootOverrides = overrides.filter(o => !o.startsWith('--pp-'));
    if (panelOverrides.length || rootOverrides.length) {
      const parts: string[] = [];
      if (rootOverrides.length) parts.push(`:root { ${rootOverrides.join(' ')} }`);
      if (panelOverrides.length) parts.push(`#booking-widget-panel { ${panelOverrides.join(' ')} }`);
      const override = document.createElement('style');
      override.textContent = parts.join('\n');
      document.head.appendChild(override);
    }
  }

  // ── Same-day quick booking bundle loader ─────────────────────────────

  private _qbLoad: Promise<NonNullable<Window['BookingWidgetQuickBook']> | null> | null = null;

  private _loadQuickBook(): Promise<NonNullable<Window['BookingWidgetQuickBook']> | null> {
    if (window.BookingWidgetQuickBook) return Promise.resolve(window.BookingWidgetQuickBook);
    if (this._qbLoad) return this._qbLoad;
    const url = quickBookChunkUrl(findWidgetScriptSrc(), this.cfg.apiBase);
    this._qbLoad = new Promise((resolve) => {
      if (!url) { resolve(null); return; }
      const sc = document.createElement('script');
      sc.src = url;
      sc.async = true;
      sc.onload = () => resolve(window.BookingWidgetQuickBook || null);
      sc.onerror = () => { console.error('[BookingWidget] Could not load quick-book bundle from', url); resolve(null); };
      document.head.appendChild(sc);
    });
    return this._qbLoad;
  }

  private _menuLoad: Promise<NonNullable<Window['BookingWidgetPriceList']> | null> | null = null;

  private _loadPriceList(): Promise<NonNullable<Window['BookingWidgetPriceList']> | null> {
    if (window.BookingWidgetPriceList) return Promise.resolve(window.BookingWidgetPriceList);
    if (this._menuLoad) return this._menuLoad;
    const url = priceListChunkUrl(findWidgetScriptSrc(), this.cfg.apiBase);
    this._menuLoad = new Promise((resolve) => {
      if (!url) { resolve(null); return; }
      const sc = document.createElement('script');
      sc.src = url;
      sc.async = true;
      sc.onload = () => resolve(window.BookingWidgetPriceList || null);
      sc.onerror = () => { console.error('[BookingWidget] Could not load price list bundle from', url); this._menuLoad = null; resolve(null); };
      document.head.appendChild(sc);
    });
    return this._menuLoad;
  }

  private _catLoad: Promise<NonNullable<Window['BookingWidgetCatalog']> | null> | null = null;

  private _loadCatalog(): Promise<NonNullable<Window['BookingWidgetCatalog']> | null> {
    if (window.BookingWidgetCatalog) return Promise.resolve(window.BookingWidgetCatalog);
    if (this._catLoad) return this._catLoad;
    const url = catalogChunkUrl(findWidgetScriptSrc(), this.cfg.apiBase);
    this._catLoad = new Promise((resolve) => {
      if (!url) { resolve(null); return; }
      const sc = document.createElement('script');
      sc.src = url;
      sc.async = true;
      sc.onload = () => resolve(window.BookingWidgetCatalog || null);
      sc.onerror = () => { console.error('[BookingWidget] Could not load catalog bundle from', url); resolve(null); };
      document.head.appendChild(sc);
    });
    return this._catLoad;
  }

  // ── DOM ──────────────────────────────────────────────────────────────

  private buildDOM(): void {
    this.backdropEl = document.createElement('div');
    this.backdropEl.id = 'booking-widget-backdrop';

    this.panelEl = document.createElement('div');
    this.panelEl.id = 'booking-widget-panel';
    if (this.cfg.fontSize) this.panelEl.style.fontSize = this.cfg.fontSize;

    this.headerEl = document.createElement('div');
    this.headerEl.className = 'pp-header';

    this.breadcrumbEl = document.createElement('div');
    this.breadcrumbEl.className = 'pp-breadcrumb';

    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'pp-body';

    this.footerEl = document.createElement('div');
    this.footerEl.className = 'pp-footer';

    this.modalContainerEl = document.createElement('div');

    this.panelEl.append(
      this.headerEl,
      this.breadcrumbEl,
      this.bodyEl,
      this.footerEl,
      this.modalContainerEl,
    );
    document.body.append(this.backdropEl, this.panelEl);
  }

  // ── Render ───────────────────────────────────────────────────────────

  private _prevStep = '';

  /** A booking that owes a deposit goes to the in-widget deposit step.
   *  True = we moved there; false = nothing owed (or the queue was too slow —
   *  the deposit text/email still goes out), show the confirmation screen.
   *
   * The booking is written by a queue, so the deposit link usually is not in
   * the POST response — we wait briefly for the pending endpoint to report it.
   */
  /** Scroll a blocked field into view and focus it (banner item / first tap). */
  private scrollToMissing(field: string): void {
    if (!field) return;
    const el = this.panelEl.querySelector<HTMLElement>(`[data-field="${field}"]`)
      || this.panelEl.querySelector<HTMLElement>(`[data-anchor="${field}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      el.focus({ preventScroll: true });
    }
  }

  /** The contact step's Continue: create the client + the booking (a deposit
   *  hold when one is owed), then the deposit step. Also the re-try after the
   *  'you already have a time held' screen (existingHoldChoice rides on the
   *  payload). */
  private async submitContactBooking(): Promise<void> {
    this.state.bookingSubmitting = true;
    this.state.bookingError = null;
    trackWidgetEvent('booking_submit', { itemCount: this.state.items.length });
    this.render();
    // Creating the booking (and its deposit hold) takes a few seconds; a
    // form that just sits there reads as frozen.
    this.showBusy('Reserving your time…');
    try {
      // The client record and the booking are created HERE, before any
      // payment — someone who gets this far without their card to hand is
      // a hot lead the spa can still reach, not a lost form.
      const bookingResult = await submitBooking(this.cfg.apiBase!, this.state.buildBookingPayload());
      if (bookingResult) {
        this.state.lastBookingId = bookingResult.id || null;
        this.state.lastBookingConfCode = bookingResult.confirmationCode || '';
        this.state.lastBookingClientId = bookingResult.clientId || null;
      }
      trackWidgetEvent('booking_success', this.state.memDim({ itemCount: this.state.items.length }));
      // The appointment just took a slot: every cached grid is now a lie for
      // that window. Clear so a second booking this session refetches.
      this.state._availableSlots.clear();
      this.state.rememberContactOnDevice(bookingResult?.clientId || null);
      this.storeRecognition(bookingResult);
      // The booking now carries the shop cart → the cart is done.
      if (this.state.selectedProducts.length) window.BookingWidgetCatalog?.clearCart?.(this.cfg.apiBase || '');
      // A deposit booking is NOT confirmed yet. Showing "You're all set" here
      // let clients close the tab believing they were booked, miss the text,
      // and lose the slot to deposit expiry — the deposit step comes first.
      if (await this.tryDepositStep(bookingResult)) {
        this.state.bookingSubmitting = false;
        await this.revealPaymentStep();
        return;
      }
      this._clearDraft();
      this.state.setStep('book-success');
    } catch (err) {
      if (this._onBookingRefused(err)) { this.hideBusy(); return; }
      this.state.bookingError = (err as Error).message || 'Booking could not be submitted. Please try again.';
    } finally {
      this.state.bookingSubmitting = false;
    }
    this.hideBusy();
    this.render();
  }

  /** 409 hold_exists: show the hold instead of an error. True when handled. */
  private _onBookingRefused(err: unknown): boolean {
    const e = err as { code?: string; data?: any };
    if (e?.code !== 'hold_exists' || !e.data?.hold) return false;
    this.state.existingHold = e.data.hold;
    this.state.existingHoldFrom = this.state.step;
    this.state.existingHoldBusy = false;
    this.state.existingHoldChoice = null;
    this.state.bookingSubmitting = false;
    this.state.setStep('book-hold-exists');
    trackWidgetEvent('hold_exists_shown', { bookingId: e.data.hold.bookingId });
    this.render();
    return true;
  }

  /** The hold screen's answers. 'pay' goes to THAT hold's deposit; 'release'
   *  and 'keep' re-submit this booking with the answer on the payload. */
  private async answerExistingHold(choice: 'pay' | 'release' | 'keep'): Promise<void> {
    const hold = this.state.existingHold;
    if (!hold || this.state.existingHoldBusy) return;
    trackWidgetEvent('hold_exists_answer', { choice });
    if (choice === 'pay') {
      const link = depositFromResponse({ status: 'pending_deposit', ...(hold.deposit as any) });
      if (!link) { this.state.bookingError = 'That deposit link is no longer available.'; this.state.step = this.state.existingHoldFrom || 'book-contact'; this.render(); return; }
      this.state.lastBookingId = hold.bookingId;
      this.state.lastBookingConfCode = hold.confirmationCode || '';
      this.state.existingHold = null;
      this.state.beginDepositStep(link);
      await this.revealPaymentStep();
      return;
    }
    this.state.existingHoldBusy = true;
    this.render();
    this.state.existingHoldChoice = choice;
    this.state.existingHold = null;
    // Back to the step that submitted, then submit again with the answer.
    this.state.step = this.state.existingHoldFrom || 'book-contact';
    try {
      if (this.state.entryMode === 'portal' && this.state.clientId) await this.handleConfirmBooking();
      else await this.submitContactBooking();
    } finally {
      this.state.existingHoldChoice = null;
      this.state.existingHoldBusy = false;
    }
  }

  private _lastOutcomeCardNeeded = false;

  private async tryDepositStep(result: any): Promise<boolean> {
    // A rejected booking (slot gone, staffing rule) throws here and lands in
    // the caller's bookingError; a slow queue resolves to null instead.
    let completed = !result?.pendingToken || !!result?.id;   // sync paths are already decided
    const link = await awaitDepositLink(result, {
      fetchPending: (token: string) => fetchPendingBooking(this.cfg.apiBase!, token),
      // The 202 never carries the recognition token — it arrives with the
      // completed outcome. Store it here or the device is never recognised.
      onCompleted: (outcome) => { completed = true; this.storeRecognition(outcome); this._lastOutcomeCardNeeded = !!(outcome as any)?.cardOnFileNeeded; },
    });
    // Poll window closed with no outcome: the success screen must not claim a
    // confirmed booking it cannot see. (A slow queue that then FAILS used to
    // show "You're all set" followed by an apology text — incoherent.)
    this.state.bookingOutcomePending = !completed;
    if (!link) return false;
    this.state.beginDepositStep(link);
    trackWidgetEvent('deposit_step_shown', { amount: link.amount, neededNow: link.neededNow });
    return true;
  }

  // ── Hold countdown (deposit screens) ──────────────────────────────────
  private _holdTimer: ReturnType<typeof setInterval> | null = null;

  private syncHoldClock(): void {
    const banners = this.panelEl.querySelectorAll<HTMLElement>('[data-hold-expires]');
    if (!banners.length) {
      if (this._holdTimer) { clearInterval(this._holdTimer); this._holdTimer = null; }
      return;
    }
    const tick = () => {
      let anyLeft = false;
      this.panelEl.querySelectorAll<HTMLElement>('[data-hold-expires]').forEach(b => {
        const exp = b.dataset.holdExpires || '';
        const clock = b.querySelector<HTMLElement>('.pp-bk-hold-clock');
        if (clock) clock.textContent = holdCountdown(exp);
        if (holdSecondsLeft(exp) > 0) anyLeft = true;
      });
      if (!anyLeft && this._holdTimer) {
        clearInterval(this._holdTimer); this._holdTimer = null;
        // The hold has lapsed: the server will have expired the link. Say so
        // instead of letting a pay attempt fail with a bare error.
        if (this.state.step === 'book-payment' || this.state.step === 'book-pending-text') {
          this.state.depositPayError = 'The hold on this appointment has ended and it has been removed from our calendar. Please book again.';
          this.render();
        } else if (this.state.step === 'dashboard') {
          // The deposit-due banner's clock ran out: the hold is gone.
          this.state.portalBookings = null;
          void this.prefetchDashboardData();
        }
      }
    };
    if (!this._holdTimer) this._holdTimer = setInterval(tick, 1000);
    tick();
  }

  /**
   * Send the date step's "get back to me" form. It becomes an inbound message
   * in the spa's inbox; nothing is texted to the client automatically, so the
   * only promise made here is that a person will see it.
   */
  private async submitCallback(): Promise<void> {
    const known = this.state.callbackKnownContact();
    const name = known ? known.name : this.state.callbackName.trim();
    const phone = known ? known.phone : this.state.callbackPhone.trim();
    if (!name || phone.replace(/\D/g, '').length < 10) return;
    if (this.state.callbackStatus === 'sending') return;
    this.state.callbackStatus = 'sending';
    this.render();
    try {
      await sendBookingCallback(this.cfg.apiBase || '', {
        name, phone,
        // Only what was typed here: a recognised visitor's email is on their record already.
        email: known ? '' : this.state.callbackEmail.trim(),
        message: this.state.callbackMessage.trim(),
        preference: this.state.callbackPreference,
        date: this.state.selectedDate || '',
        serviceName: this.state.items.map(i => i.service?.name).filter(Boolean).join(', '),
      });
      this.state.callbackStatus = 'sent';
      trackWidgetEvent('callback_sent', { date: this.state.selectedDate });
    } catch (err) {
      console.error('[BookingWidget] callback request failed', err);
      this.state.callbackStatus = 'error';
    }
    this.render();
  }

  private async payDeposit(): Promise<void> {
    const d = this.state.deposit;
    if (!d || this.state.depositPaying) return;
    this.state.depositPaying = true;
    this.state.depositPayError = null;
    this.updateFooterBtn();
    try {
      if (!isStripeEnabled()) throw new Error('Card entry is not available right now. Please use the text link or call us.');
      // A payer whose booking carries no agreement of record (portal — no
      // contact-step toggle) must tick the policy first; the server enforces
      // the same rule, this just says it before the card is charged.
      if (d.policy?.required && !this.state.depositPolicyAgreed) {
        throw new Error('Please agree to the cancellation policy above to continue.');
      }
      // A gift card number typed but never switched on used to be ignored in
      // silence — the whole deposit then landed on the card.
      if (this.state.depositGcOpen && this.state.depositGcNumber.trim() && !this.state.depositGcAgreed) {
        throw new Error('To use your gift card, turn on "Use this gift card for the deposit" above — or tap "I don\'t have a gift card".');
      }
      let pmId: string;
      try {
        pmId = await createPaymentMethod({});
      } catch (cardErr: any) {
        // With a gift card open, an empty card field reads as "why do you
        // need my card?" — say why before the card error.
        if (this.state.depositGcOpen && this.state.depositGcNumber.trim()) {
          throw new Error(`A credit or debit card is still needed to hold your appointment — it's only charged for anything your gift card doesn't cover. ${cardErr?.message || ''}`.trim());
        }
        throw cardErr;
      }
      const paid = await payDepositLink(this.cfg.apiBase!, d.token, pmId,
        this.state.depositGcAgreed ? this.state.depositGcNumber : '',
        d.policy?.required && this.state.depositPolicyAgreed);
      this.state.depositPaidSplit = paid && paid.giftCardApplied > 0
        ? { giftCard: paid.giftCardApplied || 0, card: paid.cardCharged || 0, tip: paid.tip || 0 } : null;
      if (d.kind === 'products') {
        this.state.productsPrepaid = true;
        trackWidgetEvent('products_prepaid', { amount: d.amount });
      } else {
        trackWidgetEvent('deposit_paid', { amount: d.amount, giftCard: !!(this.state.depositGcAgreed && this.state.depositGcNumber.trim()) });
      }
      this.state.depositPaying = false;
      this._clearDraft();
      this.state.setStep('book-success');
    } catch (err: any) {
      this.state.depositPaying = false;
      // 'Already paid' is not a failure: the FIRST attempt landed after all —
      // a phone that reloaded the page mid-payment (Google Pay's sheet over
      // Chrome, 2026-10-13) resumed the draft and paid again, and the refusal
      // read as 'your payment failed' in red when the deposit was in.
      if (err?.linkStatus === 'paid') {
        trackWidgetEvent('deposit_paid', { amount: d.amount, alreadyPaid: true });
        this._clearDraft();
        this.state.depositPaidSplit = null;
        this.state.setStep('book-success');
        this.render();
        return;
      }
      this.state.depositPayError = err?.linkStatus === 'expired'
        ? 'The hold on this appointment has ended and it has been removed from our calendar. Please book again.'
        : (err?.message || 'Payment failed. Please try again.');
    }
    this.render();
  }

  // ── Draft / resume / close gate ─────────────────────────────────────────

  private _clearDraft(): void {
    if (this.cfg.apiBase) clearDraft(this.cfg.apiBase);
    // A credit (package session, membership session, OFFER) rides on ONE
    // booking. It used to stay set after 'You're all set', so a second service
    // booked in the same portal visit carried the offer again and was refused
    // three times for 'already reserved for another appointment' (Franie,
    // 2026-10-31). Every end of a flow drops it.
    this.state.bookingCredit = null;
  }

  /** A saved draft → the resume screen. True when one was offered. */
  private _offerDraft(): boolean {
    if (!this.cfg.apiBase) return false;
    const d = loadDraft(this.cfg.apiBase);
    if (!d) return false;
    this.state.resumeDraft = d;
    this.state.resumeNotice = null;
    this.state.step = 'book-resume';
    this.state.stepHistory = [];
    trackWidgetEvent('draft_offered', { step: d.step, deposit: !!d.deposit });
    return true;
  }

  /** Close the panel the ordinary way — reset the screen, keep the draft. */
  private _closeNow(): void {
    this.state.closePanel();
    this.render();
    if (!this.state.isStandalone) {
      setTimeout(() => { this.state.resetBookingFlow(); this.render(); }, TIMING.PANEL_RESET_DELAY);
    }
  }

  /** Every way out of the panel comes through here. On the deposit step the
   *  booking already exists and the hold is ticking: ask, don't just close. */
  private _requestClose(): void {
    if (this.state.step === 'book-payment' && this.state.deposit && this.state.deposit.kind !== 'products'
        && !this.state.isStandalone) {
      this.state.leaveDepositModal = true;
      this.render();
      return;
    }
    this._closeNow();
  }

  /** "Resume booking": the server decides whether the draft still stands. */
  private async resumeDraft(): Promise<void> {
    const d = this.state.resumeDraft;
    if (!d || !this.cfg.apiBase || this.state.resumeChecking) return;
    this.state.resumeChecking = true;
    this.render();
    try {
      await this.ensureBookingData();
      if (d.deposit) {
        const live = await resolveDepositLink(this.cfg.apiBase, d.deposit.token);
        if (!live || live.status !== 'pending') {
          this._clearDraft();
          this.state.resumeDraft = null;
          this.state.startBooking(false);
          this.state.resumeNotice = live?.status === 'paid'
            ? 'That deposit was already paid — your appointment is confirmed. Book again below if you need another.'
            : 'That reservation is no longer on hold. Please choose a time again.';
          this.state.setStep('book-categories');
          trackWidgetEvent('draft_resumed', { outcome: live?.status || 'unreachable' });
          return;
        }
        applyDraft(this.state, d);
        if (live.payExpiresAt && this.state.deposit) this.state.deposit.expiresAt = live.payExpiresAt;
        this.state.resumeDraft = null;
        this.state.setStep('book-payment');
        trackWidgetEvent('draft_resumed', { outcome: 'deposit' });
        return;
      }
      applyDraft(this.state, d);
      this.state.resumeDraft = null;
      let step = d.step;
      if (d.selectedDate && d.selectedTime) {
        const first = this.state.items[0] || (this.state.curr.service ? this.state.curr : null);
        const svc = first?.service;
        if (svc) {
          let stillOpen = true;
          try {
            const res = await fetchAvailability(this.cfg.apiBase, d.selectedDate, svc.id,
              first?.provider?.id ?? null, (first?.selectedAddons || []).map(a => a.id));
            stillOpen = (res?.slots || []).some(sl => sl.time === d.selectedTime && sl.available !== false);
          } catch { stillOpen = true; }   // unreachable → let the server refuse at submit, as usual
          if (!stillOpen) {
            this.state.selectedTime = null;
            this.state.resumeNotice = 'That time has since been taken — please pick another.';
            step = 'book-datetime';
          }
        }
      }
      this.state.setStep(step);
      trackWidgetEvent('draft_resumed', { outcome: step });
    } finally {
      this.state.resumeChecking = false;
      this.render();
    }
  }

  private async textDeposit(goToEndScreen: boolean): Promise<void> {
    const d = this.state.deposit;
    if (!d || this.state.depositTextState === 'sending') return;
    this.state.depositTextState = 'sending';
    this.render();
    try {
      const res = await textDepositLink(this.cfg.apiBase!, d.token);
      this.state.depositTextState = res.smsSent ? 'sent' : 'failed';
      this.state.depositPhoneLast4 = res.phoneLast4 || '';
      if (res.smsSent) {
        trackWidgetEvent('deposit_link_texted', {});
        if (goToEndScreen) this.state.setStep('book-pending-text');
      }
    } catch {
      this.state.depositTextState = 'failed';
    }
    this.render();
  }

  private render(): void {
    if (!this.state) return;
    // Remember where they are (see draft.ts) — an accidental tap outside the
    // panel, a closed tab or a restart must not mean starting over.
    // A signed-in client's booking is recovered from their account, never
    // offered as 'Resume booking' to whoever opens this device's /book/.
    if (DRAFT_STEPS.has(this.state.step) && !this.state.clientId) saveDraft(this.state);
    this._applyPendingPromo();

    // Panel open/close
    this.panelEl.classList.toggle('open', this.state.panelOpen);
    this.backdropEl.classList.toggle('visible', this.state.panelOpen);
    // On a phone the slide-out panel follows the VISUAL viewport (viewport-fit.ts).
    if (this.state.panelOpen && !this.state.isStandalone && phoneLayout()) fitPanelToViewport(this.panelEl);
    else stopFittingPanel();

    const stepChanged = this.state.step !== this._prevStep;
    this._prevStep = this.state.step;

    // A field that has focus must have it again after the paint, or the
    // phone's keyboard goes away under the client (see focus-keep.ts).
    const focused = stepChanged ? null : captureFocus(this.panelEl);

    this.headerEl.innerHTML      = renderHeader(this.state);
    this.breadcrumbEl.innerHTML  = renderBreadcrumb(this.state);
    this.bodyEl.innerHTML        = renderBody(this.state);
    this.footerEl.innerHTML      = renderFooter(this.state);
    this.modalContainerEl.innerHTML = renderModal(this.state);

    // Scroll body back to top on step transitions (except AI chat — scroll to bottom)
    if (stepChanged) {
      if (this.state.step === 'book-ai') {
        this._scrollAiChat();
      } else {
        this.bodyEl.scrollTop = 0;
      }
      requestAnimationFrame(() => {
        const el = this.bodyEl.querySelector<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [data-action]'
        );
        if (el) el.focus({ preventScroll: true });
      });
    }
    if (focused) restoreFocus(this.panelEl, focused);

    // One step BEFORE a payment step: warm the Apple Pay / Google Pay button
    // up off screen, so the real one is not starting cold (see prewarmWallet).
    if (this.state.stripePublishableKey && stepChanged
        && ['book-contact', 'book-confirm', 'gc-details', 'pkg-contact'].includes(this.state.step)) {
      prewarmWallet();
    }

    // Mount Stripe card element on payment steps
    if (this.state.stripePublishableKey && stepChanged) {
      const step = this.state.step;
      const prefixMap: Record<string, string> = {
        'gc-payment': 'gc', 'pkg-payment': 'pkg', 'mem-payment': 'mem', 'book-payment': 'deposit',
      };
      // Card completeness lives inside Stripe's iframe, so it can't reach the
      // widget's input/click handlers — refresh the pay button when it changes.
      const refreshPayBtn = () => this.updateFooterBtn();
      const prefix = prefixMap[step];
      if (prefix) {
        mountCardElement(`pp-stripe-card-element-${prefix}`, this.cfg.accentColor, refreshPayBtn);
        this.mountWallet(prefix);
      } else {
        destroyWalletButton();
      }
      // Portal add-card
      if (this.state.portalPurchaseStep === 'add-card') {
        mountCardElement('pp-stripe-card-element-portal', this.cfg.accentColor);
      }
      const walletBox = this._walletCardBoxId();
      if (walletBox) mountCardElement(walletBox, this.cfg.accentColor, () => this.render());
    } else if (this.state.stripePublishableKey && !stepChanged) {
      // A re-render WITHIN a payment step (the deposit step's "Paying with a
      // gift card?" toggle and its agreement switch call render()) rebuilds
      // the DOM and takes Stripe's iframe with it — the client was left with
      // an empty card box that "wouldn't let her put in the card" (Sep 2026).
      // Mount again whenever the container came back empty.
      const prefix = ({ 'gc-payment': 'gc', 'pkg-payment': 'pkg', 'mem-payment': 'mem', 'book-payment': 'deposit' } as Record<string, string>)[this.state.step];
      const id = prefix ? `pp-stripe-card-element-${prefix}` : (this.state.portalPurchaseStep === 'add-card' ? 'pp-stripe-card-element-portal' : (this._walletCardBoxId() || ''));
      const box = id ? document.getElementById(id) : null;
      if (box && !box.firstChild) {
        mountCardElement(id, this.cfg.accentColor, () => this.updateFooterBtn());
      }
      const walletSlot = prefix ? document.querySelector(`#pp-stripe-wallet-${prefix} [data-wallet-slot]`) : null;
      if (walletSlot && !walletSlot.firstChild) this.mountWallet(prefix!);
    }
    // The portal's 'Open today' page hosts the same-day surface.
    this._syncTodaySurface(stepChanged);
    // The deposit screens carry a live "held for hh:mm" clock.
    this.syncHoldClock();
    // Portal offer banners count down to their expiry.
    this.syncOfferClocks();
  }

  private _offerTimer: ReturnType<typeof setInterval> | null = null;
  private syncOfferClocks(): void {
    const banners = this.panelEl.querySelectorAll<HTMLElement>('[data-offer-expires]');
    if (!banners.length) {
      if (this._offerTimer) { clearInterval(this._offerTimer); this._offerTimer = null; }
      return;
    }
    const tick = () => {
      let lapsed = false;
      this.panelEl.querySelectorAll<HTMLElement>('[data-offer-expires]').forEach(b => {
        const exp = b.dataset.offerExpires || '';
        if (!exp) return;
        const clock = b.querySelector<HTMLElement>('.pp-offer-countdown');
        const txt = offerCountdown(exp);
        if (clock) clock.textContent = txt || '00:00:00';
        if (!txt) lapsed = true;
      });
      if (lapsed) {
        // An offer just lapsed: redraw — portalOffers() drops it.
        if (this._offerTimer) { clearInterval(this._offerTimer); this._offerTimer = null; }
        this.render();
      }
    };
    if (!this._offerTimer) this._offerTimer = setInterval(tick, 1000);
  }

  // ── Busy overlay ──────────────────────────────────────────────────────
  // Covers the body + footer (the header and its close button stay usable).
  // It lives on the PANEL, outside the re-rendered body, so it survives
  // render() and can be lifted without rebuilding the payment fields.
  private busyEl: HTMLElement | null = null;

  private showBusy(text: string): void {
    if (!this.panelEl) return;
    if (!this.busyEl) {
      this.busyEl = document.createElement('div');
      this.busyEl.className = 'pp-busy';
      this.busyEl.setAttribute('role', 'status');
      this.busyEl.setAttribute('aria-live', 'polite');
      this.busyEl.innerHTML = '<div class="pp-spinner"></div><div class="pp-busy-text"></div>';
      // The slide-out panel is fixed; a hosted page's panel may sit in the
      // flow — the overlay needs a positioned parent either way.
      if (getComputedStyle(this.panelEl).position === 'static') this.panelEl.style.position = 'relative';
      this.panelEl.appendChild(this.busyEl);
    }
    this.busyEl.style.top = `${this.bodyEl?.offsetTop || 0}px`;
    const t = this.busyEl.querySelector('.pp-busy-text');
    if (t) t.textContent = text;
  }

  private hideBusy(): void {
    this.busyEl?.remove();
    this.busyEl = null;
  }

  /**
   * Show the deposit step only once it is complete: the card form and the
   * Apple Pay / Google Pay button load behind the overlay, so the screen
   * appears whole instead of the wallet popping in a second later. Capped —
   * paying by card is never held up by a slow wallet.
   */
  private async revealPaymentStep(): Promise<void> {
    this.showBusy('Getting your secure payment ready…');
    this.render();
    // 5 s, not 3: on a phone Google Pay often reported in just after the old
    // cap, so the screen appeared and THEN the button popped in. The warm-up a
    // step earlier (prewarmWallet) is what keeps this wait short in practice.
    await paymentUiReady(5000);
    this.hideBusy();
    this.updateFooterBtn();
  }

  /** What the wallet sheet shows as the total for the current payment step. */
  private walletAmount(): number {
    switch (this.state.step) {
      case 'book-payment': return this.state.deposit?.amount || 0;
      case 'gc-payment': return Number(this.state.gcAmount) || 0;
      case 'pkg-payment': return this.state.selectedPackage?.totalPrice || 0;
      default: return 0;
    }
  }

  /** Everything but the card is done (the wallet stands in for the card). */
  private walletReady(): boolean {
    return withCardAssumedComplete(() => {
      if (this.state.step === 'gc-payment') return this.state.canProceed && !this.state.gcTermsMissing;
      return this.state.canProceed;
    });
  }

  /**
   * Apple Pay / Google Pay button on a payment step. The sheet's payment
   * method goes through the step's ordinary Pay action — createPaymentMethod
   * hands it over instead of reading the card form — so deposits, gift cards
   * and packages are charged, saved and receipted exactly as a typed card.
   */
  private mountWallet(prefix: string): void {
    if (prefix === 'mem') return;
    mountWalletButton(`pp-stripe-wallet-${prefix}`, this.walletAmount(), {
      canStart: () => {
        if (this.walletReady()) return true;
        // Say what's missing instead of opening a sheet we'd then refuse.
        if (this.state.step === 'book-payment') {
          this.state.showBlockedSummary = true;
          this.render();
          const first = this.state.blockedReasons.find(r => r.field !== 'depositCard');
          if (first) this.scrollToMissing(first.field);
        } else if (this.state.step === 'gc-payment' && this.state.gcTermsMissing) {
          this.state.gcPayError = 'Please agree to the gift card terms to continue.';
          this.render();
        }
        return false;
      },
      onPaid: async () => {
        trackWidgetEvent('wallet_pay', { step: this.state.step });
        try { await this.handleFooterAction(); } finally { clearPendingWallet(); }
      },
    });
  }

  // Partial updates to avoid cursor jump on text input
  private updateErrors(...fields: string[]): void {
    fields.forEach(f => {
      const el = this.panelEl.querySelector<HTMLElement>(`[data-error="${f}"]`);
      if (el) el.textContent = this.state.formErrors[f] || this.state.gcErrors[f] || '';
    });
  }

  private updateFooterBtn(): void {
    // Rendered wholesale: the footer now carries the blocked-summary banner,
    // whose contents track every keystroke. It holds no inputs, so replacing
    // its HTML never steals focus.
    this.footerEl.innerHTML = renderFooter(this.state);
  }

  // ── Events ───────────────────────────────────────────────────────────

  private bindEvents(): void {
    this.panelEl.addEventListener('click',  e => this.onClick(e));
    this.panelEl.addEventListener('input',  e => this.onInput(e as InputEvent));
    this.panelEl.addEventListener('change', e => this.onChange(e));
    // Phone lookup on blur for the contact form phone field
    this.panelEl.addEventListener('focusout', e => {
      const el = e.target as HTMLInputElement;
      if (el.dataset?.field === 'phone' && this.state.step === 'book-contact') {
        this._checkPhoneLookup(el.value);
      }
    });
    this.panelEl.addEventListener('keydown', e => {
      if (e.key === 'Enter') {
        const target = e.target as HTMLElement;
        if (target.matches('[data-field="loginQuery"]') || target.matches('[data-field="otpCode"]') || target.matches('[data-field="loginBirthday"]')) {
          this.handleLogin();
        }
        if (target.matches('[data-field="aiInput"]')) {
          const sendBtn = this.panelEl.querySelector('[data-action="ai-send"]') as HTMLElement;
          if (sendBtn) sendBtn.click();
        }
      }
    });
    this.backdropEl.addEventListener('click', () => this._requestClose());
    this._escHandler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!this.state) return;
      // Close modal first, then panel
      if (this.state.modalSvc) {
        this.state.modalSvc = null;
        this.render();
      } else if (this.state.leaveDepositModal) {
        this.state.leaveDepositModal = false;
        this.render();
      } else if (this.state.panelOpen && !this.state.isStandalone) {
        this._requestClose();
      }
    };
    document.addEventListener('keydown', this._escHandler);
  }

  private async onClick(e: Event): Promise<void> {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!btn) return;
    const d = btn.dataset;
    const action = d.action!;

    switch (action) {
      // ── Panel / navigation ──────────────────────────────────────────
      case 'close-panel':
        trackWidgetEvent('widget_close', { lastStep: this.state.step, itemCount: this.state.items.length });
        if (this.state.step === 'gc-success' && this.state.clientId && this.state.entryMode === 'portal') {
          // Bought from the portal: back to their gift cards, refreshed.
          this.state.stepHistory = ['dashboard'];
          this.state.step = 'giftcards';
          this.state.wallet = null;
          this.render();
          await this.loadWalletIfNeeded();
          break;
        }
        this._requestClose();
        break;

      // ── Leaving the deposit step (the booking is real, the hold is ticking) ──
      case 'leave-continue':
        this.state.leaveDepositModal = false;
        this.render();
        break;
      case 'leave-text':
        this.state.leaveDepositBusy = true; this.render();
        try { await this.textDeposit(false); } finally { this.state.leaveDepositBusy = false; }
        this.state.leaveDepositModal = false;
        this._closeNow();
        break;
      case 'leave-cancel': {
        const tok = this.state.deposit?.token;
        this.state.leaveDepositBusy = true; this.render();
        try { if (tok && this.cfg.apiBase) await cancelDepositLink(this.cfg.apiBase, tok); } catch { /* the hold lapses on its own */ }
        this.state.leaveDepositBusy = false;
        this.state.leaveDepositModal = false;
        this._clearDraft();
        this._closeNow();
        break;
      }

      // ── Resume screen ──────────────────────────────────────────────
      case 'resume-start-over':
        this._clearDraft();
        this.state.resumeDraft = null;
        this.state.startBooking(false);
        this.render();
        if (!this.state.bookingDataLoaded) this.ensureBookingData();
        break;
      case 'resume-booking':
        await this.resumeDraft();
        break;
      case 'hold-pay':     await this.answerExistingHold('pay'); break;
      case 'hold-release': await this.answerExistingHold('release'); break;
      case 'hold-keep':    await this.answerExistingHold('keep'); break;

      case 'go-back':
        this.state.goBack();
        if (this.state.portalGroupBooking && !this.state.step.startsWith('book-')) this._endPortalGroupBooking();
        this.render();
        break;
      case 'return-today':
        this._returnToToday?.();
        break;

      case 'footer-action':
        if ((this.state.step === 'book-contact' || this.state.step === 'book-payment')
            && !this.state.canProceed && !this.state.bookingSubmitting && !this.state.depositPaying) {
          // The tap a disabled button would have swallowed: list every blocker
          // above the button, ring the fields, and take them to the first one.
          this.state.showBlockedSummary = true;
          this.render();
          const first = this.state.blockedReasons[0];
          if (first) this.scrollToMissing(first.field);
          break;
        }
        await this.handleFooterAction();
        break;

      case 'goto-missing':
        this.scrollToMissing(d.target || '');
        break;

      // ── Portal: login / OTP / sign-out ──────────────────────────────
      case 'login-submit':
        await this.handleLogin();
        break;

      case 'otp-resend':
        this.state.otpSent = false;
        this.state.otpCode = '';
        this.state.otpDebugCode = null;
        this.state.loginError = null;
        await this.handleLogin();
        break;

      case 'otp-back':
        this.state.otpSent = false;
        this.state.otpCode = '';
        this.state.otpDebugCode = null;
        this.state.loginError = null;
        this.state.loginQuery = '';
        this.state.loginBirthday = '';
        this.state.multiAccountClients = null;
        this.render();
        break;

      case 'pick-client':
        await this.handlePickClient(Number(d.clientid));
        break;

      case 'register-submit':
        await this.handleRegister();
        break;

      case 'go-dashboard': {
        const leavingBooking = this.state.step.startsWith('book-');
        if (this.state.portalGroupBooking) { this._clearDraft(); this._endPortalGroupBooking(); }
        this.state.entryMode = 'portal';
        this.state.step = 'dashboard';
        this.state.stepHistory = [];
        if (leavingBooking) { this.state.portalBookings = null; void this.prefetchDashboardData(); }
        this.render();
        break;
      }

      case 'sign-out':
        clearClientSession();
        if (this._manifestPersonalised && typeof document !== 'undefined') {
          const ml = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
          if (ml) ml.setAttribute('href', (ml.getAttribute('href') || '').split('?')[0]);
          this._manifestPersonalised = false;
        }
        this.state.signOut();
        this.render();
        break;

      // ── Portal: navigation to sections ──────────────────────────────
      case 'nav-bookings':
        this.state.navigate('bookings');
        this.render();
        await this.loadBookingsIfNeeded();
        break;

      case 'nav-sales':
        this.state.navigate('sales');
        this.render();
        await this.loadSalesIfNeeded();
        break;

      case 'nav-giftcards':
        this.state.navigate('giftcards');
        this.render();
        await this.loadWalletIfNeeded();
        break;

      case 'nav-packages':
        this.state.navigate('packages');
        this.render();
        // The templates on sale ride with the booking data — without them the
        // page only ever listed what the client already owns.
        await Promise.all([this.loadPackagesIfNeeded(), this.ensureBookingData()]);
        break;

      case 'nav-memberships':
        this.state.navigate('memberships');
        this.render();
        await Promise.all([this.loadMembershipsIfNeeded(), this.ensureBookingData()]);
        break;

      case 'nav-wallet':
        this.state.walletAddCardOpen = false;
        this.state.walletCardError = null;
        this.state.walletRemoveCardId = null;
        this.state.navigate('wallet');
        this.render();
        await Promise.all([this.loadWalletIfNeeded(), this.loadMembershipsIfNeeded()]);
        break;

      case 'nav-wallet-pay':
        await this.loadWalletIfNeeded();
        this.state.openWalletPay();
        this.render();
        break;
      case 'wallet-pay-preset':
        this.state.walletPayAmount = String(d.amount || '');
        this.state.walletPayError = null;
        this.render();
        break;
      case 'wallet-pay-card':
        this.state.walletPayCardId = d.id === 'new' ? 'new' : Number(d.id);
        this.state.walletPayError = null;
        this.render();
        break;
      case 'wallet-pay-toggle-save':
        this.state.walletPaySaveCard = !this.state.walletPaySaveCard;
        this.render();
        break;
      case 'wallet-pay-submit':
        await this.handleWalletPay();
        break;
      case 'wallet-pay-done':
        this.state.walletPaySuccess = null;
        this.state.stepHistory = ['dashboard'];
        this.state.step = 'wallet';
        this.render();
        await this.loadWalletIfNeeded();
        break;
      case 'wallet-card-default':
        await this.handleWalletCardDefault(Number(d.id));
        break;
      case 'wallet-card-remove':
        this.state.walletRemoveCardId = Number(d.id);
        this.state.walletCardError = null;
        this.render();
        break;
      case 'wallet-remove-cancel':
        this.state.walletRemoveCardId = null;
        this.render();
        break;
      case 'wallet-remove-confirm':
        if (this.state.walletRemoveCardId != null) await this.handleWalletCardRemove(this.state.walletRemoveCardId);
        break;
      case 'wallet-add-card-open':
        this.state.walletAddCardOpen = true;
        this.state.walletCardError = null;
        this.render();
        break;
      case 'wallet-add-card-cancel':
        this.state.walletAddCardOpen = false;
        this.state.walletCardError = null;
        this.render();
        break;
      case 'wallet-add-card-submit':
        await this.handleWalletAddCard();
        break;

      case 'portal-book-group':
        await this.handlePortalGroupBooking();
        break;

      case 'portal-buy-gift-card':
        this.state.openGiftCard();
        this.render();
        break;

      case 'pwa-install':
        await this.handleInstallApp();
        break;
      case 'pwa-dismiss':
        this.state.installDismissed = true;
        try { localStorage.setItem('pp_install_dismissed', '1'); } catch { /* no storage */ }
        this.render();
        break;

      case 'nav-preferences':
        void this.loadPushState();
        void this.loadPortalTags();
        this.state.detailsDraft = detailsDraftFrom(this.state.client);
        this.state.detailsSaved = false; this.state.detailsError = null;
        this.state.prefsDraft = this.state.client?.portalNotes || '';
        this.state.prefsSaved = false;
        this.state.prefsError = null;
        this.state.navigate('preferences');
        this.render();
        break;
      case 'prefs-save': {
        const st = this.state;
        if (!this.cfg.apiBase || !st.clientId || st.prefsSaving) break;
        st.prefsSaving = true; st.prefsError = null; st.prefsSaved = false; this.render();
        try {
          const res = await savePortalNotes(this.cfg.apiBase, st.clientId, st.prefsDraft.trim());
          if (st.client) st.client.portalNotes = res.portalNotes;
          st.prefsDraft = res.portalNotes;
          st.prefsSaved = true;
        } catch (err: any) {
          st.prefsError = err?.message || 'Could not save your notes.';
        }
        st.prefsSaving = false;
        this.render();
        break;
      }

      case 'details-save': {
        const st = this.state;
        if (!this.cfg.apiBase || !st.clientId || !st.client || st.detailsSaving) break;
        const changed: Record<string, string> = {};
        for (const k of PORTAL_DETAIL_FIELDS) {
          const val = (st.detailsDraft[k] ?? '').trim();
          if (val !== String((st.client as any)[k] ?? '')) changed[k] = val;
        }
        if (!Object.keys(changed).length) break;
        st.detailsSaving = true; st.detailsError = null; st.detailsSaved = false; this.render();
        try {
          const res = await savePortalDetails(this.cfg.apiBase, st.clientId, changed);
          Object.assign(st.client, res);
          st.detailsDraft = detailsDraftFrom(st.client);
          st.detailsSaved = true;
        } catch (err: any) {
          st.detailsError = err?.message || 'Could not save your details.';
        }
        st.detailsSaving = false;
        this.render();
        break;
      }

      case 'marketing-pref-toggle': {
        const st = this.state;
        const fields = ['marketingTexts', 'marketingEmails', 'apptTexts', 'apptEmails'] as const;
        const field = fields.includes(d.pref as any) ? (d.pref as typeof fields[number]) : 'marketingTexts';
        if (!this.cfg.apiBase || !st.clientId || !st.client || st.marketingPrefsSaving) break;
        // Appointment texts/emails default ON when the record never said.
        const current = field.startsWith('appt') ? st.client[field] !== false : !!st.client[field];
        const want = !current;
        st.marketingPrefsSaving = true; st.marketingPrefsError = null; this.render();
        try {
          const res = await saveMarketingPrefs(this.cfg.apiBase, st.clientId, { [field]: want });
          st.client.marketingTexts = res.marketingTexts;
          st.client.marketingEmails = res.marketingEmails;
          st.client.apptTexts = res.apptTexts;
          st.client.apptEmails = res.apptEmails;
        } catch (err: any) {
          st.marketingPrefsError = err?.message || 'Could not save your choice.';
        }
        st.marketingPrefsSaving = false;
        this.render();
        break;
      }

      case 'nav-today':
        this.state.navigate('today');
        this.render();
        break;
      case 'portal-pay-deposit':
        await this.handlePortalPayDeposit(Number(d.id));
        break;

      case 'toggle-policy-line':
        this.state.policyLineOpen = !this.state.policyLineOpen;
        this.render();
        break;

      case 'nav-message':
        this.state.messageText = '';
        this.state.messageSent = false;
        this.state.messageError = null;
        this.state.navigate('message');
        this.render();
        break;
      case 'portal-message-submit':
        await this.handlePortalMessage();
        break;
      case 'portal-message-done':
        this.state.stepHistory = [];
        this.state.step = 'dashboard';
        this.render();
        break;

      case 'portal-usual-rebook': {
        const usual = this.state.usualBooking;
        if (!usual) break;
        this.state.recentBookings = [usual];
        this.state.rebookSource = null;
        await this.handleRebookAppointment(usual.id);
        break;
      }
      case 'portal-fill-form':
        await this.handlePortalFillForm(Number(d.id));
        break;
      case 'portal-refresh-form':
        await this.handlePortalRefreshForm(Number(d.id));
        break;
      case 'portal-email-receipt':
        await this.handleEmailReceipt();
        break;

      case 'nav-forms':
        this.state.navigate('forms');
        this.render();
        await this.loadFormSubmissionsIfNeeded();
        break;

      case 'view-form':
        this.state.selectedFormId = Number(d.id);
        this.state.navigate('form-detail');
        this.render();
        await this.loadFormDetailIfNeeded(Number(d.id));
        break;

      case 'nav-recurring':
        this.state.navigate('recurring');
        this.render();
        await this.loadRecurringIfNeeded();
        break;

      case 'copy-referral':
        if (this.state.referralCode) {
          try {
            await navigator.clipboard.writeText(this.state.referralCode);
            const copyBtn = this.panelEl?.querySelector('[data-action="copy-referral"]');
            if (copyBtn) {
              copyBtn.textContent = 'Copied!';
              setTimeout(() => { this.render(); }, 1500);
            }
          } catch { /* clipboard API not available */ }
        }
        break;

      case 'copy-referral-link':
        if (this.state.referralShareUrl) {
          try {
            await navigator.clipboard.writeText(this.state.referralShareUrl);
            const b = this.panelEl?.querySelector('[data-action="copy-referral-link"]');
            if (b) { b.textContent = 'Copied!'; setTimeout(() => { this.render(); }, 1500); }
          } catch { /* clipboard API not available */ }
        }
        break;

      case 'toggle-referral-qr': {
        const st = this.state;
        st.referralQrOpen = !st.referralQrOpen;
        this.render();
        if (st.referralQrOpen && !st.referralQrUrl && this.cfg.apiBase) {
          const url = await fetchReferralQr(this.cfg.apiBase);
          if (url) { st.referralQrUrl = url; this.render(); }
          trackWidgetEvent('referral_qr_shown', {});
        }
        break;
      }

      case 'share-referral-link':
        if (this.state.referralShareUrl && typeof navigator !== 'undefined' && 'share' in navigator) {
          try {
            await (navigator as any).share({
              title: `${this.state.spaName} — a treat from me`,
              text: `Book at ${this.state.spaName} with my link and get ${this.state.referralDiscountLabel || 'a discount'}:`,
              url: this.state.referralShareUrl,
            });
            trackWidgetEvent('referral_shared', {});
          } catch { /* cancelled */ }
        }
        break;

      case 'push-toggle':
        await this.togglePush();
        break;

      case 'portal-tag-toggle': {
        const st = this.state;
        const id = Number(d.id);
        if (!this.cfg.apiBase || !id || st.portalTagsBusy) break;
        const current = st.portalTagGroups.flatMap(g => g.tags).find(t => t.id === id);
        if (!current) break;
        st.portalTagsBusy = true; st.portalTagsError = null; this.render();
        try {
          st.portalTagGroups = await setPortalTag(this.cfg.apiBase, id, !current.on);
          trackWidgetEvent('portal_tag_toggled', { tagId: id, on: !current.on });
        } catch (err: any) {
          st.portalTagsError = err?.message || 'Could not save that.';
        }
        st.portalTagsBusy = false;
        this.render();
        break;
      }

      case 'review-open':
        trackWidgetEvent('review_link_opened', {});
        break;    // the anchor opens the page; nothing else changes until they say so

      case 'review-later':
      case 'review-done': {
        const st = this.state;
        if (!this.cfg.apiBase || !st.client || st.reviewPromptBusy) break;
        st.reviewPromptBusy = true; this.render();
        try {
          const r = await postReviewPrompt(this.cfg.apiBase, action === 'review-done' ? 'done' : 'later');
          st.client.reviewPromptDoneAt = r.reviewPromptDoneAt;
          st.client.reviewPromptSnoozedUntil = r.reviewPromptSnoozedUntil;
          trackWidgetEvent(action === 'review-done' ? 'review_marked_done' : 'review_snoozed', {});
        } catch { /* the banner simply stays */ }
        st.reviewPromptBusy = false;
        this.render();
        break;
      }

      case 'push-test': {
        const st = this.state;
        if (!this.cfg.apiBase || st.pushBusy) break;
        st.pushBusy = true; st.pushNote = null; st.pushError = null; this.render();
        const r = await testClientPush(this.cfg.apiBase);
        st.pushBusy = false;
        if (r.ok) st.pushNote = 'Sent — it should pop up in a moment.';
        else st.pushError = r.error || 'Could not send a test.';
        this.render();
        break;
      }

      case 'generate-cal-sync':
        await this.generateCalendarSyncUrl();
        break;

      case 'copy-cal-sync':
        if (this.state.calSyncFeedUrl) {
          try {
            await navigator.clipboard.writeText(this.state.calSyncFeedUrl);
            const btn = this.panelEl?.querySelector('[data-action="copy-cal-sync"]');
            if (btn) {
              btn.textContent = 'Copied!';
              setTimeout(() => { this.render(); }, 1500);
            }
          } catch { /* clipboard API not available */ }
        }
        break;

      case 'revoke-cal-sync':
        await this.revokeCalendarSyncUrl();
        break;

      case 'view-recurring':
        this.state.selectedSeriesId = Number(d.id);
        this.state.navigate('recurring-detail');
        this.render();
        await this.loadRecurringDetailIfNeeded(Number(d.id));
        break;

      case 'skip-occurrence':
        await this.handleSkipOccurrence(Number(d.seriesId), d.date as string);
        break;

      case 'cancel-recurring':
        await this.handleCancelRecurring(Number(d.id));
        break;

      case 'view-booking':
        this.state.selectedBookingId = Number(d.id);
        this.state.navigate('booking-detail');
        this.render();
        break;

      case 'client-cancel-booking':
        await this.handleClientCancelBooking(Number(d.id));
        break;
      case 'client-confirm-booking':
        await this.handlePortalBookingAction(Number(d.id), 'confirm');
        break;
      case 'client-checkin-booking':
        await this.handlePortalBookingAction(Number(d.id), 'checkin');
        break;
      case 'portal-apply-offer':
        await this.handlePortalApplyOffer(Number(d.id), Number(d.booking));
        break;

      case 'client-reschedule-booking':
        this.handleClientRescheduleBooking(Number(d.id));
        break;

      case 'retry-slots':
        this.state.retrySlots();
        this.render();
        break;

      case 'reschedule-any-provider':
        this._rescheduleOpenToAll(true);
        break;
      case 'reschedule-requested-only':
        this._rescheduleOpenToAll(false);
        break;

      case 'add-to-calendar':
        await this.handleAddToCalendar(Number(d.id));
        break;

      case 'client-withdraw-request':
        await this.handleClientWithdrawRequest(Number(d.id));
        break;

      case 'view-sale':
        this.state.selectedSaleId = Number(d.id);
        this.state.receiptEmailState = 'idle';
        this.state.receiptEmailNote = '';
        this.state.navigate('sale-detail');
        this.render();
        await this.loadSaleDetailIfNeeded(Number(d.id));
        break;

      // ── Portal: one-click purchase / enrollment ────────────────────
      case 'portal-book-credit': {
        await this.handlePortalBookCredit(d.kind as 'package' | 'membership' | 'promotion', Number(d.id), String(d.group ?? ''));
        break;
      }
      case 'portal-book-credit-clear':
        this.state.bookingCredit = null;
        this.render();
        break;
      case 'cof-save':
        await this.handleCardOnFileSave();
        break;

      case 'portal-buy-pkg': {
        const tid = Number(d.id);
        // Ensure wallet data loaded (for card on file)
        await this.loadWalletIfNeeded();
        this.state.portalPurchaseStep = 'confirm';
        this.state.portalPurchaseType = 'package';
        this.state.portalPurchaseTemplateId = tid;
        this.state.portalPurchaseError = null;
        this.state.portalPurchaseAgreed = false;
        this.state.portalPurchaseSuccess = false;
        this.render();
        break;
      }

      case 'portal-enroll-mem': {
        const pid = Number(d.id);
        await this.loadWalletIfNeeded();
        this.state.portalPurchaseStep = 'confirm';
        this.state.portalPurchaseType = 'membership';
        this.state.portalPurchaseTemplateId = pid;
        this.state.portalPurchaseError = null;
        this.state.portalPurchaseAgreed = false;
        this.state.portalPurchaseSuccess = false;
        this.render();
        break;
      }

      case 'portal-toggle-agreement':
        this.state.portalPurchaseAgreed = !this.state.portalPurchaseAgreed;
        this.render();
        break;

      case 'portal-purchase-confirm':
        await this.handlePortalPurchaseConfirm();
        break;

      case 'portal-purchase-cancel':
        this.state.portalPurchaseStep = null;
        this.state.portalPurchaseType = null;
        this.state.portalPurchaseTemplateId = null;
        this.state.portalPurchaseError = null;
        this.state.portalPurchaseAgreed = false;
        this.state.portalPurchaseSuccess = false;
        this.render();
        break;

      case 'portal-purchase-done':
        this.state.portalPurchaseStep = null;
        this.state.portalPurchaseType = null;
        this.state.portalPurchaseTemplateId = null;
        this.state.portalPurchaseError = null;
        this.state.portalPurchaseAgreed = false;
        this.state.portalPurchaseSuccess = false;
        this.render();
        break;

      case 'portal-add-card-submit':
        await this.handlePortalAddCard();
        break;

      // ── Portal: booking entry ───────────────────────────────────────
      case 'book-now':
        await this.handleBookNowOrRebook();
        break;

      case 'book-for-other':
        await this.handleBookNow(true);
        break;

      case 'rebook-appointment':
        await this.handleRebookAppointment(Number(d.id));
        break;

      case 'goto-book-categories':
        this.state.rebookSource = null;
        this.state.startBooking(false);
        this.render();
        if (!this.state.bookingDataLoaded) await this.ensureBookingData();
        break;

      // ── Booking flow: categories ────────────────────────────────────
      case 'toggle-cat':
        if (this.state.step === 'book-enhancements') {
          this.state.step = 'book-categories';
        } else {
          this.state.toggleCat(d.catid!);
          trackWidgetEvent('category_select', { categoryId: d.catid });
        }
        this.render();
        break;

      case 'open-modal':
        this.state.openModal(d.svcid!);
        this.render();
        break;

      case 'close-modal':
        this.state.modalSvc = null;
        this.render();
        break;

      case 'pick-location':
        this.state.selectLocation(Number(d.id));
        this.render();
        this.reloadProvidersForLocation(Number(d.id));
        break;

      case 'pick-service':
        this.state.bookingError = null;   // clear any carried-in notice (e.g. rebook fallback)
        this.state.pickService(d.svcid!);
        if (this.state.curr.service) {
          trackWidgetEvent('service_select', this.state.memDim({
            serviceId: this.state.curr.service.id,
            serviceName: this.state.curr.service.name,
            coveredByPlanIds: coveredPlanIds(serviceVerdict(this.state.eligibility, this.state.curr.service.id)),
            categoryId: d.catid || '',
            price: this.state.curr.service.price,
          }));
        }
        this.render();
        break;

      // ── Booking flow: enhancements ──────────────────────────────────
      case 'toggle-addon': {
        const wasAdded = !this.state.curr.selectedAddons.some(a => String(a.id) === String(d.addonid));
        this.state.toggleAddon(d.grpid!, d.addonid!);
        trackWidgetEvent('enhancement_toggle', this.state.memDim({
          enhancementId: d.addonid, added: wasAdded,
          coveredByPlanIds: coveredPlanIds(enhancementVerdict(this.state.eligibility, d.addonid)),
        }));
        this.render();
        break;
      }

      // ── Booking flow: provider ──────────────────────────────────────
      case 'pick-provider':
        this.state.pickProvider(d.provid!);
        if (this.state.curr.provider) {
          trackWidgetEvent('provider_select', this.state.memDim({
            providerId: this.state.curr.provider.id,
            providerName: this.state.curr.provider.name,
            coveredByPlanIds: coveredPlanIds(providerVerdict(
              this.state.eligibility, this.state.curr.service?.id, this.state.curr.provider.id)),
          }));
        }
        this.render();
        break;

      // ── Booking flow: multi-service ─────────────────────────────────
      case 'remove-item': {
        const idx = Number(d.idx);
        const item = this.state.items[idx];
        if (item && !confirm(`Remove ${item.service.name}?`)) break;
        this.state.removeItem(idx);
        this.render();
        break;
      }

      case 'add-another':
        // When another PERSON is possible, ask who the next service is for
        // before diving into the menu; otherwise straight to it as always.
        if (this.state.canAddAnotherPerson) {
          this.state.addAnotherWho = true;
        } else {
          this.state.addAnotherService();
        }
        this.render();
        break;
      case 'add-for-me':
        this.state.addAnotherService();
        this.render();
        break;
      case 'add-for-other':
        this.state.addAnotherForNewGuest();
        this.render();
        break;

      case 'forget-device':
        this.state.forgetDevice();
        this.render();
        break;

      case 'gc-forget-device':
        this.state.forgetGcDevice();
        this.render();
        break;

      case 'gc-focus': {
        const target = d.target || '';
        const input = this.panelEl.querySelector<HTMLElement>(`[data-field="${target}"]`);
        if (input) { input.scrollIntoView({ block: 'center', behavior: 'smooth' }); input.focus({ preventScroll: true }); }
        break;
      }

      case 'not-me':
        // The number matched someone else's record (shared phone, typo):
        // reopen the full form. The lookup is not re-run for the same digits.
        this.state.knownClient = null;
        this.render();
        break;

      case 'deposit-text-me':
        await this.textDeposit(true);
        break;

      case 'deposit-gc-toggle':
        this.state.depositGcOpen = !this.state.depositGcOpen;
        if (!this.state.depositGcOpen) {
          this.state.depositGcNumber = '';
          this.state.depositGcAgreed = false;
        }
        this.render();
        break;

      case 'deposit-gc-agree':
        this.state.depositGcAgreed = (btn as HTMLInputElement).checked;
        this.render();
        break;

      case 'prepay-choice':
        this.state.productsPayNow = btn.dataset.value !== 'later';
        this.render();
        break;

      case 'prepay-skip':
        // The pickup is booked either way; nothing to cancel, nothing held.
        trackWidgetEvent('products_prepay_skipped', { amount: this.state.deposit?.amount || 0 });
        this._clearDraft();
        this.state.deposit = null;
        this.state.setStep('book-success');
        this.render();
        break;

      case 'deposit-pay-here':
        this.state.depositPayError = null;
        this.state.setStep('book-payment');
        this.revealPaymentStep();
        break;

      case 'no-more':
        this.state.setStep('book-datetime');
        this.state.initDatetime();
        this.render();
        break;

      case 'set-group-size':
        this.state.setGroupSize(Number(d.size));
        this.render();
        break;

      case 'next-person':
        this.state.continueToNextPerson();
        this.render();
        break;

      // ── Booking flow: datetime ──────────────────────────────────────
      case 'prev-week':
        this.state.prevWeek();
        this.render();
        break;

      case 'next-week':
        this.state.nextWeek();
        this.render();
        break;

      case 'set-date':
        this.state.showWaitlistPicker = false;
        this.state.waitlistMode = false;
        this.state.waitlistTimeBlock = '';
        this.state.setDate(d.date!);
        this.render();
        break;

      case 'toggle-period':
        this.state.togglePeriod(d.label!);
        this.render();
        break;

      case 'pick-time':
        this.state.pickTime(d.slot!);
        trackWidgetEvent('datetime_select', {
          date: this.state.selectedDate,
          offsetDays: this.state.dateOffsetDays(this.state.selectedDate),
          time: d.slot,
        });
        this.render();
        if (this.state.rescheduleBookingId) {
          // Reschedule mode: submit the reschedule after picking a time
          setTimeout(() => this.handleRescheduleSubmit(), TIMING.TIME_PICK_DELAY);
        } else if (this.state.entryMode === 'portal') {
          // Portal: go to confirm step after pick
          setTimeout(() => {
            this.state.navigate('book-confirm');
            this.render();
          }, TIMING.TIME_PICK_DELAY);
        } else {
          // Public: go to contact step
          setTimeout(() => {
            this.state.setStep('book-contact');
            this.render();
          }, TIMING.TIME_PICK_DELAY);
        }
        break;

      case 'show-waitlist-picker':
        this.state.showWaitlistPicker = true;
        this.render();
        break;

      case 'hide-waitlist-picker':
        this.state.showWaitlistPicker = false;
        this.state.waitlistMode = false;
        this.state.waitlistTimeBlock = '';
        this.render();
        break;

      case 'callback-not-you': {
        // Their details, their call: hand the fields back empty rather than
        // sending on behalf of whoever this device remembers.
        this.state.callbackTypeDetails = true;
        this.state.callbackName = '';
        this.state.callbackPhone = '';
        this.state.callbackEmail = '';
        this.render();
        break;
      }

      case 'pick-callback-pref': {
        const pref = (d.pref || 'either') as 'either' | 'text' | 'call';
        this.state.callbackPreference = pref;
        this.render();
        break;
      }

      case 'submit-callback': {
        void this.submitCallback();
        break;
      }

      case 'pick-waitlist-block': {
        const block = d.block || 'morning';
        this.state.waitlistMode = true;
        this.state.waitlistTimeBlock = block;
        trackWidgetEvent('waitlist_block_select', { date: this.state.selectedDate, block });
        this.render();
        setTimeout(() => {
          if (this.state.entryMode === 'portal') {
            this.state.navigate('book-confirm');
          } else {
            this.state.setStep('book-contact');
          }
          this.render();
        }, TIMING.TIME_PICK_DELAY);
        break;
      }

      // ── Booking flow: contact (public) ──────────────────────────────

      // ── Booking flow: confirm (portal) ──────────────────────────────
      case 'toggle-other-minor':
        this.state.otherIsMinor = !this.state.otherIsMinor;
        this.render();
        break;
      case 'toggle-responsible':
        this.state.responsibleAgreed = !this.state.responsibleAgreed;
        this.render();
        break;

      case 'confirm-booking':
        await this.handleConfirmBooking();
        break;

      case 'booking-done':
        this._clearDraft();
        this._endPortalGroupBooking();
        // Clear booking state and go back to dashboard (portal) or reset (public)
        if (this.state.clientId && this.state.entryMode === 'portal') {
          this.state.stepHistory = [];
          this.state.step = 'dashboard';
          this.state.portalBookings = null;
          this.prefetchDashboardData();
        } else {
          this.state.resetBookingFlow();
          this.state.step = 'book-categories';
          this.state.stepHistory = [];
        }
        this.render();
        break;

      case 'save-birthday':
        if (!this.state.birthdayPromptValue || !this.state.lastBookingId || !this.cfg.apiBase) break;
        this.state.birthdayPromptSaving = true;
        this.render();
        try {
          await saveBookingBirthday(this.cfg.apiBase, this.state.lastBookingId, {
            birthday: this.state.birthdayPromptValue,
            confirmationCode: this.state.lastBookingConfCode,
          });
          this.state.birthdayPromptSaved = true;
        } catch { /* silent */ }
        this.state.birthdayPromptSaving = false;
        this.render();
        break;

      // ── Returning client (from public booking → portal login) ───────
      case 'open-portal':
        this.state.entryMode = 'portal';
        this.state.navigate('login');
        this.render();
        break;

      // ── AI Concierge ──────────────────────────────────────────────
      case 'nav-ai-concierge':
        this.state.navigate('book-ai');
        this.render();
        this._scrollAiChat();
        break;

      case 'ai-send': {
        const msg = this.state.aiInput.trim();
        if (!msg || this.state.aiLoading) break;
        this.state.aiMessages.push({ role: 'user', text: msg });
        this.state.aiInput = '';
        this.state.aiLoading = true;
        this.render();
        this._scrollAiChat();
        const history = this.state.aiMessages
          .filter(m => !m.options)
          .map(m => ({ role: m.role, text: m.text }));
        const base = this.state.apiBase.replace(/\/+$/, '');
        const hdrs: Record<string, string> = { 'Content-Type': 'application/json' };
        if (this.state.clientToken) hdrs['Authorization'] = `ClientBearer ${this.state.clientToken}`;
        fetch(`${base}/api/v1/ai-booking/suggest/`, {
          method: 'POST',
          headers: hdrs,
          body: JSON.stringify({
            message: msg,
            locationId: this.state.selectedLocationId,
            conversationId: this.state.aiConversationId,
            conversationHistory: history.slice(0, -1),
          }),
        })
          .then(r => r.json())
          .then(data => {
            this.state.aiLoading = false;
            // Store conversation tracking IDs
            if (data.conversationId) this.state.aiConversationId = data.conversationId;
            if (data.sessionKey) this.state.aiSessionKey = data.sessionKey;
            // Show staff messages if any
            if (data.staffMessages?.length) {
              for (const sm of data.staffMessages) {
                if (sm.id > this.state.aiLastMsgId) {
                  this.state.aiMessages.push({ role: 'staff' as any, text: sm.body });
                  this.state.aiLastMsgId = sm.id;
                }
              }
            }
            // Handle response modes
            if (data.webChatOnly) {
              // Webchat-only: no AI message, just wait for staff
              // Don't push any assistant message — user just sees their own messages + staff replies
            } else if (data.aiPaused) {
              this.state.aiPaused = true;
              if (data.quickReplies?.length) {
                const pauseMsg: any = { role: 'assistant', text: 'A staff member is assisting you. You can resume the AI assistant anytime.', quickReplies: data.quickReplies };
                this.state.aiMessages.push(pauseMsg);
              }
            } else {
              this.state.aiPaused = false;
              const aiMsg: any = { role: 'assistant' as const, text: data.aiMessage || data.clarification || 'I had trouble processing that.' };
              if (data.options && data.options.length > 0) aiMsg.options = data.options;
              if (data.quickReplies && data.quickReplies.length > 0) aiMsg.quickReplies = data.quickReplies;
              this.state.aiMessages.push(aiMsg);
            }
            this.render();
            this._scrollAiChat();
            this.state.saveChatSession();
            // Start polling for staff replies if not already
            this._startAiPoll();
          })
          .catch(() => {
            this.state.aiLoading = false;
            this.state.aiMessages.push({ role: 'assistant', text: 'Sorry, something went wrong. Please try again.' });
            this.render();
          });
        break;
      }

      case 'ai-chip':
      case 'ai-quick-reply': {
        const replyText = btn.dataset.text || btn.dataset.value || '';
        // Handle gift card redirect
        if (replyText === 'purchase gift card') {
          this.state.openGiftCard();
          this.render();
          break;
        }
        this.state.aiInput = replyText;
        this.render();
        const sendBtn2 = this.panelEl.querySelector('[data-action="ai-send"]') as HTMLElement;
        if (sendBtn2) sendBtn2.click();
        break;
      }

      case 'ai-book-option': {
        const idx = parseInt(btn.dataset.index || '0', 10);
        const lastMsg = [...this.state.aiMessages].reverse().find(m => m.options);
        const opt = lastMsg?.options?.[idx] as any;
        if (!opt) break;
        // Include selected add-ons if any were toggled
        const selectedAddons = (opt._selectedAddons || []).map((a: any) => ({
          id: a.id, name: a.name, price: a.price, durationAdd: a.durationAdd || 0,
        }));
        const addonTotal = selectedAddons.reduce((s: number, a: any) => s + (a.price || 0), 0);
        // Populate items and date from the selected option
        // AI-assigned providers get 'any' preference (not 'requested') since the client
        // didn't explicitly pick them — unless the AI conversation shows they asked by name
        this.state.items = opt.items.map((it: any) => ({
          service: { id: it.serviceId, name: it.serviceName, price: it.price, duration: it.durationMins } as any,
          provider: { id: it.staffId, name: it.staffName, type: 'ai-assigned' } as any,
          selectedAddons: selectedAddons,
          startTime: it.startTime,
          totalPrice: it.price + addonTotal,
        }));
        this.state.selectedDate = opt.date;
        this.state.selectedTime = opt.items[0].startTime;
        // Jump to contact (public) or confirm (portal)
        if (this.state.entryMode === 'portal') {
          this.state.navigate('book-confirm');
        } else {
          // Pre-fill phone from chat if entered there
          const chatPhone = ((this.state as any).aiPhone || '').trim();
          if (chatPhone && !this.state.formData.phone) {
            this.state.formData.phone = chatPhone;
          }
          this.state.navigate('book-contact');
        }
        this.render();
        break;
      }

      case 'ai-pick-upgrade': {
        // Swap the service in this option with the selected upgrade
        const optIdx = parseInt(btn.dataset.optIndex || '0', 10);
        const lastMsgU = [...this.state.aiMessages].reverse().find(m => m.options);
        const optU = lastMsgU?.options?.[optIdx] as any;
        if (!optU) break;
        const upgradeServiceId = parseInt(btn.dataset.serviceId || '0', 10);
        const upgradeName = btn.dataset.serviceName || '';
        const upgradePrice = parseFloat(btn.dataset.price || '0');
        const upgradeDuration = parseInt(btn.dataset.duration || '60', 10);
        // Replace the first item's service with the upgrade
        if (optU.items?.[0]) {
          optU.items[0].serviceId = upgradeServiceId;
          optU.items[0].serviceName = upgradeName;
          optU.items[0].price = upgradePrice;
          optU.items[0].durationMins = upgradeDuration;
          // Recalculate totals
          optU.totalPrice = optU.items.reduce((s: number, it: any) => s + (it.price || 0), 0);
          optU.totalDuration = optU.items.reduce((s: number, it: any) => s + (it.durationMins || 0), 0);
          // Clear upgrades since we just picked one
          optU.suggestedUpgrades = [];
        }
        this.render();
        break;
      }

      case 'ai-toggle-addon': {
        // Toggle an add-on on/off for this option
        const optIdxA = parseInt(btn.dataset.optIndex || '0', 10);
        const lastMsgA = [...this.state.aiMessages].reverse().find(m => m.options);
        const optA = lastMsgA?.options?.[optIdxA] as any;
        if (!optA) break;
        const addonId = parseInt(btn.dataset.optionId || '0', 10);
        const addonName = btn.dataset.name || '';
        const addonPrice = parseFloat(btn.dataset.price || '0');
        const addonDurAdd = parseInt(btn.dataset.durationAdd || '0', 10);
        if (!optA._selectedAddonIds) optA._selectedAddonIds = [];
        if (!optA._selectedAddons) optA._selectedAddons = [];
        const idx2 = optA._selectedAddonIds.indexOf(addonId);
        if (idx2 >= 0) {
          // Remove
          optA._selectedAddonIds.splice(idx2, 1);
          optA._selectedAddons.splice(idx2, 1);
        } else {
          // Add
          optA._selectedAddonIds.push(addonId);
          optA._selectedAddons.push({ id: addonId, name: addonName, price: addonPrice, durationAdd: addonDurAdd });
        }
        // Recalculate total with addons
        const basePrice = optA.items.reduce((s: number, it: any) => s + (it.price || 0), 0);
        const addonTotal = optA._selectedAddons.reduce((s: number, a: any) => s + (a.price || 0), 0);
        optA.totalPrice = basePrice + addonTotal;
        this.render();
        break;
      }

      case 'ai-phone-toggle':
        (this.state as any).aiPhoneCollapsed = !(this.state as any).aiPhoneCollapsed;
        this.render();
        break;

      case 'ai-phone-submit': {
        const phone = ((this.state as any).aiPhone || '').replace(/\D/g, '');
        if (phone.length < 10) break;
        const phoneBase = this.state.apiBase.replace(/\/+$/, '');
        const phoneVal = encodeURIComponent((this.state as any).aiPhone);
        // First: lookup if this is an existing client
        fetch(`${phoneBase}/api/v1/ai-booking/poll/?conversationId=${this.state.aiConversationId}&sessionKey=${this.state.aiSessionKey}&phone=${phoneVal}&action=lookup`, {
          method: 'POST',
        }).then(r => r.json()).then(data => {
          if (data.found && data.clientId) {
            // Existing client found — show verify prompt
            (this.state as any).aiPhoneLookupName = data.maskedName;
            (this.state as any).aiPhoneLookupClientId = data.clientId;
            (this.state as any).aiPhoneShowVerify = true;
            this.render();
          } else {
            // New client — just save the phone
            fetch(`${phoneBase}/api/v1/ai-booking/poll/?conversationId=${this.state.aiConversationId}&sessionKey=${this.state.aiSessionKey}&phone=${phoneVal}`, {
              method: 'POST',
            }).catch(() => {});
            (this.state as any).aiPhoneSubmitted = true;
            this.state.saveChatSession();
            this.render();
          }
        }).catch(() => {
          // Fallback: just save
          fetch(`${phoneBase}/api/v1/ai-booking/poll/?conversationId=${this.state.aiConversationId}&sessionKey=${this.state.aiSessionKey}&phone=${phoneVal}`, {
            method: 'POST',
          }).catch(() => {});
          (this.state as any).aiPhoneSubmitted = true;
          this.render();
        });
        break;
      }

      case 'ai-phone-verify': {
        // Send OTP to verify the phone
        const verifyBase = this.state.apiBase.replace(/\/+$/, '');
        const verifyPhone = ((this.state as any).aiPhone || '').trim();
        fetch(`${verifyBase}/api/v1/client-auth/request-otp/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: verifyPhone }),
        }).then(r => r.json()).then((data) => {
          if (data && data.code === 'sms_stopped') {
            // Replied STOP: no code can reach them — say how to undo it.
            (this.state as any).aiPhoneOtpError = data.error;
            this.render();
            return;
          }
          (this.state as any).aiPhoneOtpSent = true;
          (this.state as any).aiPhoneOtpCode = '';
          // The challenge is created even if the SMS could not be delivered.
          (this.state as any).aiPhoneOtpError = data && data.delivered === false
            ? "We couldn't send your code. Please try again."
            : '';
          this.render();
        }).catch(() => {});
        break;
      }

      case 'ai-phone-otp-submit': {
        const otpBase = this.state.apiBase.replace(/\/+$/, '');
        const otpPhone = ((this.state as any).aiPhone || '').trim();
        const otpCode = ((this.state as any).aiPhoneOtpCode || '').trim();
        if (otpCode.length < 6) break;
        fetch(`${otpBase}/api/v1/client-auth/verify-otp/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: otpPhone, code: otpCode }),
        }).then(r => r.json()).then(data => {
          if (data.token) {
            // OTP verified — fetch client details and pre-fill
            const clientId = (this.state as any).aiPhoneLookupClientId;
            fetch(`${otpBase}/api/v1/ai-booking/poll/?conversationId=${this.state.aiConversationId}&sessionKey=${this.state.aiSessionKey}&phone=${encodeURIComponent(otpPhone)}&action=verify&clientId=${clientId}`, {
              method: 'POST',
            }).then(r2 => r2.json()).then(details => {
              if (details.ok) {
                // Pre-fill form data
                if (details.firstName) this.state.formData.firstName = details.firstName;
                if (details.lastName) this.state.formData.lastName = details.lastName;
                if (details.email) this.state.formData.email = details.email;
                if (details.phone) this.state.formData.phone = details.phone;
                // Store client token for portal-like auth
                this.state.clientId = clientId;
                this.state.clientToken = data.token;
              }
              (this.state as any).aiPhoneSubmitted = true;
              (this.state as any).aiPhoneShowVerify = false;
              (this.state as any).aiPhoneOtpSent = false;
              this.state.saveChatSession();
              this.render();
            });
          } else {
            (this.state as any).aiPhoneOtpError = 'Invalid code. Please try again.';
            this.render();
          }
        }).catch(() => {});
        break;
      }

      // ── Contact form verify (same OTP flow as chat phone bar) ───────
      case 'contact-verify-send': {
        const cvBase = this.state.apiBase.replace(/\/+$/, '');
        const cvPhone = (this.state.formData.phone as string || '').trim();
        fetch(`${cvBase}/api/v1/client-auth/request-otp/`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: cvPhone }),
        }).then(r => r.json()).then((data) => {
          if (data && data.code === 'sms_stopped') {
            (this.state as any)._contactVerifyError = data.error;
            this.render();
            return;
          }
          (this.state as any)._contactVerifyOtpSent = true;
          (this.state as any)._contactVerifyOtp = '';
          (this.state as any)._contactVerifyError = data && data.delivered === false
            ? "We couldn't send your code. Please try again."
            : '';
          this.render();
        }).catch(() => {});
        break;
      }

      case 'contact-verify-confirm': {
        const ccBase = this.state.apiBase.replace(/\/+$/, '');
        const ccPhone = (this.state.formData.phone as string || '').trim();
        const ccCode = ((this.state as any)._contactVerifyOtp || '').trim();
        if (ccCode.length < 6) break;
        fetch(`${ccBase}/api/v1/client-auth/verify-otp/`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: ccPhone, code: ccCode }),
        }).then(r => r.json()).then(data => {
          if (data.token) {
            const cid = (this.state as any)._contactVerifyClientId;
            this.state.clientId = cid;
            this.state.clientToken = data.token;
            // Fetch client details to pre-fill
            fetch(`${ccBase}/api/v1/clients/${cid}/`, {
              headers: { 'Authorization': `ClientBearer ${data.token}` },
            }).then(r2 => r2.json()).then(cl => {
              if (cl.firstName) this.state.formData.firstName = cl.firstName;
              if (cl.lastName) this.state.formData.lastName = cl.lastName;
              if (cl.email) this.state.formData.email = cl.email;
              if (cl.phone) this.state.formData.phone = cl.phone;
              (this.state as any)._contactVerifyShow = false;
              (this.state as any)._contactVerifyOtpSent = false;
              this.render();
            }).catch(() => {
              (this.state as any)._contactVerifyShow = false;
              (this.state as any)._contactVerifyOtpSent = false;
              this.render();
            });
          } else {
            (this.state as any)._contactVerifyError = 'Invalid code. Please try again.';
            this.render();
          }
        }).catch(() => {});
        break;
      }

      case 'contact-verify-skip':
        (this.state as any)._contactVerifyShow = false;
        (this.state as any)._contactVerifyOtpSent = false;
        this.render();
        break;

      // ── Checkout products ─────────────────────────────────────────────
      case 'cp-toggle':
        this.state.productPickerOpen = !this.state.productPickerOpen;
        if (this.state.productPickerOpen && !this.state.checkoutProductsLoaded) {
          const cpBase = this.state.apiBase.replace(/\/+$/, '');
          fetch(`${cpBase}/api/v1/checkout-products/`).then(r => r.json()).then(data => {
            this.state.checkoutProducts = data;
            this.state.checkoutProductsLoaded = true;
            this.render();
          }).catch(() => {});
        }
        this.render();
        break;

      case 'cp-remove': {
        const rmId = parseInt(btn.dataset.id || '0', 10);
        this.state.selectedProducts = this.state.selectedProducts.filter((p: any) => p.id !== rmId);
        this.render();
        break;
      }

      case 'cp-toggle-product': {
        const prodId = parseInt(btn.dataset.id || '0', 10);
        const exists = this.state.selectedProducts.find((p: any) => p.id === prodId);
        if (exists) {
          this.state.selectedProducts = this.state.selectedProducts.filter((p: any) => p.id !== prodId);
        } else {
          this.state.selectedProducts.push({
            id: prodId,
            name: btn.dataset.name || '',
            price: parseFloat(btn.dataset.price || '0'),
            quantity: 1,
          });
        }
        this.render();
        break;
      }

      case 'cp-group-by':
        this.state.productGroupBy = this.state.productGroupBy === 'category' ? 'brand' : 'category';
        this.render();
        break;

      case 'ai-phone-skip-verify':
        // Skip verification, just save the phone
        {
          const skipBase = this.state.apiBase.replace(/\/+$/, '');
          fetch(`${skipBase}/api/v1/ai-booking/poll/?conversationId=${this.state.aiConversationId}&sessionKey=${this.state.aiSessionKey}&phone=${encodeURIComponent((this.state as any).aiPhone)}`, {
            method: 'POST',
          }).catch(() => {});
          (this.state as any).aiPhoneSubmitted = true;
          (this.state as any).aiPhoneShowVerify = false;
          this.state.saveChatSession();
          this.render();
        }
        break;

      case 'ai-end-chat': {
        if (!this.state.aiConversationId) break;
        const endBase = this.state.apiBase.replace(/\/+$/, '');
        fetch(`${endBase}/api/v1/ai-booking/poll/?conversationId=${this.state.aiConversationId}&sessionKey=${this.state.aiSessionKey}&action=close`, {
          method: 'POST',
        }).catch(() => {});
        this.state.aiMessages.push({ role: 'assistant' as any, text: 'Chat ended. Thanks for reaching out!' });
        this.state.clearChatSession();
        this.render();
        break;
      }

      case 'ai-browse-manual':
        this.state.navigate('book-categories');
        this.render();
        break;

      // ── Gift card flow ──────────────────────────────────────────────
      case 'open-gift-card':
        this.state.openGiftCard();
        this.render();
        break;

      case 'gc-go-back':
        this.state.goBack();
        this.render();
        break;

      case 'gc-select-promo':
        this.state.selectGcPromotion(Number(d.promoid));
        this.render();
        break;

      case 'gc-clear-promo':
        this.state.clearGcPromotion();
        this.render();
        break;

      case 'gc-set-for':
        this.state.gcPurchaseFor = d.for as 'someone' | 'yourself';
        this.render();
        break;

      // ── Promo code ──────────────────────────────────────────────────
      case 'clear-promo':
        this.state.promoStatus = 'idle';
        this.state.promoOffer = null;
        this.state.promoError = '';
        this.state.formData['promo'] = '';
        this.render();
        return;

      case 'apply-promo': {
        const input = this.panelEl.querySelector<HTMLInputElement>('[data-field="promo"]');
        const code = (input?.value || '').trim();
        if (!code) return;
        await this.applyPromoCode(code, input);
        return;
      }

      // ── Package flow ────────────────────────────────────────────────
      case 'buy-package':
        this.state.openPackages();
        this.render();
        break;

      case 'pick-package':
        this.state.pickPackage(Number(d.pkgid));
        this.render();
        break;

      // ── Membership flow ─────────────────────────────────────────────
      case 'buy-membership':
        this.state.openMemberships();
        this.render();
        break;

      case 'set-mem-filter': {
        const planId = Number(d.planid);
        this.state.membershipFilterPlanId = planId;
        // Pinning a filter mid-flow would silently drop a service already
        // chosen; it is offered on the first step, so start clean.
        this.state.expandedCat = null;
        trackWidgetEvent('membership_filter_applied', { planId });
        this.render();
        break;
      }
      case 'change-provider': {
        trackWidgetEvent('membership_provider_switched', {});
        this.state.membershipProviderMismatch = false;
        this.state.setStep('book-provider');
        this.render();
        break;
      }
      case 'upsell-join': {
        const planId = Number(d.planid);
        trackWidgetEvent('membership_upsell_accepted', this.state.memDim({ planId }));
        this.cfg.openMemberships!(planId);
        break;
      }
      case 'upsell-dismiss': {
        trackWidgetEvent('membership_upsell_dismissed', this.state.memDim({}));
        this.state.upsellDismissed = true;
        this.render();
        break;
      }
      case 'clear-mem-filter': {
        const planId = this.state.membershipFilterPlanId;
        this.state.membershipFilterPlanId = null;
        trackWidgetEvent('membership_filter_cleared', { planId });
        this.render();
        break;
      }
      case 'pick-membership':
        this.state.pickMembership(Number(d.memid));
        this.render();
        break;
    }
  }

  private onInput(e: InputEvent): void {
    const el = e.target as HTMLInputElement;
    const field = el.dataset.field;
    if (!field) return;

    // ── Portal login fields (targeted update, no full re-render) ──────
    if (field === 'loginQuery') {
      this.state.loginQuery = el.value;
      this.state.loginError = null;
      this.state.loginResults = null;
      const btn = this.bodyEl.querySelector<HTMLButtonElement>('[data-action="login-submit"]');
      if (btn) btn.disabled = !this.state.loginQuery.trim();
      return;
    }

    if (field === 'loginBirthday') {
      this.state.loginBirthday = el.value;
      const btn = this.bodyEl.querySelector<HTMLButtonElement>('[data-action="login-submit"]');
      if (btn) btn.disabled = !(this.state.loginQuery.trim() && this.state.loginBirthday);
      return;
    }

    if (field === 'prefsDraft') {
      this.state.prefsDraft = el.value;
      this.state.prefsSaved = false;
      const btn = this.bodyEl.querySelector<HTMLButtonElement>('[data-action="prefs-save"]');
      if (btn) btn.disabled = this.state.prefsSaving || el.value.trim() === (this.state.client?.portalNotes || '');
      return;
    }

    if (field.startsWith('detail:')) {
      // 'My details': keep the draft, enable Save; a select re-renders (the
      // country decides whether State is a list), typing never does.
      this.state.detailsDraft[field.slice(7)] = el.value;
      this.state.detailsSaved = false;
      if (el.tagName === 'SELECT' && field === 'detail:country') { this.render(); return; }
      const btn = this.bodyEl.querySelector<HTMLButtonElement>('[data-action="details-save"]');
      if (btn) btn.disabled = false;
      return;
    }

    if (field === 'messageText') {
      this.state.messageText = el.value;
      const btn = this.bodyEl.querySelector<HTMLButtonElement>('[data-action="portal-message-submit"]');
      if (btn) btn.disabled = !el.value.trim() || this.state.messageSending;
      return;
    }

    if (field === 'walletPayAmount') {
      this.state.walletPayAmount = el.value;
      this.state.walletPayError = null;
      const amt = this.state.walletPayAmountNum;
      const btn = this.bodyEl.querySelector<HTMLButtonElement>('[data-action="wallet-pay-submit"]');
      if (btn) {
        btn.disabled = !(amt > 0 && this.state.walletPayCardId !== null && !this.state.walletPaying);
        btn.textContent = amt > 0 ? `Pay $${amt.toFixed(2)}` : 'Pay';
      }
      this.bodyEl.querySelectorAll<HTMLElement>('[data-action="wallet-pay-preset"]').forEach(b => {
        b.classList.toggle('selected', Number(b.dataset.amount) === amt);
      });
      return;
    }

    if (field === 'depositGcNumber') {
      // Stored only — never validated here (that would leak which numbers
      // exist). The footer button re-checks the agreement gate.
      this.state.depositGcNumber = el.value;
      this.updateFooterBtn();
      return;
    }

    // The date step's "get back to me" form: typing only toggles Send.
    if (field === 'callbackName' || field === 'callbackPhone' || field === 'callbackMessage' || field === 'callbackEmail') {
      if (field === 'callbackEmail') this.state.callbackEmail = el.value;
      else if (field === 'callbackName') this.state.callbackName = el.value;
      else if (field === 'callbackPhone') this.state.callbackPhone = el.value;
      else this.state.callbackMessage = el.value;
      if (this.state.callbackStatus === 'error') this.state.callbackStatus = 'idle';
      const btn = this.bodyEl.querySelector<HTMLButtonElement>('[data-action="submit-callback"]');
      if (btn && field !== 'callbackMessage') {
        btn.disabled = !(this.state.callbackName.trim()
          && this.state.callbackPhone.replace(/\D/g, '').length >= 10);
      }
      return;
    }

    if (field === 'birthdayPromptValue') {
      this.state.birthdayPromptValue = el.value;
      const btn = this.bodyEl.querySelector<HTMLButtonElement>('[data-action="save-birthday"]');
      if (btn) btn.disabled = !this.state.birthdayPromptValue || this.state.birthdayPromptSaving;
      return;
    }

    if (field === 'otpCode') {
      this.state.otpCode = el.value.replace(/\D/g, '').slice(0, 6);
      el.value = this.state.otpCode;
      const btn = this.bodyEl.querySelector<HTMLButtonElement>('[data-action="login-submit"]');
      if (btn) btn.disabled = this.state.otpCode.length < 6;
      return;
    }

    // ── Registration form fields (targeted update, no full re-render) ──
    if (field === 'regFirstName' || field === 'regLastName' || field === 'regEmail' || field === 'regPhone') {
      (this.state as any)[field] = el.value;
      this.state.regError = null;
      const btn = this.bodyEl.querySelector<HTMLButtonElement>('[data-action="register-submit"]');
      const phoneOk = !registrationNeedsPhone(this.state) || !!this.state.regPhone.trim();
      if (btn) btn.disabled = !(this.state.regFirstName.trim() && this.state.regLastName.trim() && this.state.regEmail.trim() && phoneOk);
      return;
    }
    if (field === 'regMarketingEmails' || field === 'regMarketingTexts') {
      (this.state as any)[field] = (el as HTMLInputElement).checked;
      return;
    }

    // ── AI concierge input (no re-render) ─────────────────────────────
    if (field === 'aiInput') {
      this.state.aiInput = el.value;
      return;
    }
    if (field === 'aiPhone') {
      (this.state as any).aiPhone = el.value;
      return;
    }
    if (field === 'productSearchQuery') {
      this.state.productSearchQuery = el.value;
      // Debounced render to avoid losing focus on every keystroke
      clearTimeout((this as any)._cpSearchTimer);
      (this as any)._cpSearchTimer = setTimeout(() => {
        // Update only the product list, not the search input
        const listEl = this.bodyEl.querySelector('.pp-cp-list');
        if (listEl) {
          // Re-render just the product list portion
          this.render();
          // Restore focus to search input
          const searchInput = this.bodyEl.querySelector<HTMLInputElement>('[data-field="productSearchQuery"]');
          if (searchInput) {
            searchInput.focus();
            searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
          }
        }
      }, 200);
      return;
    }
    if (field === 'aiPhoneOtpCode') {
      (this.state as any).aiPhoneOtpCode = el.value.replace(/\D/g, '').slice(0, 6);
      el.value = (this.state as any).aiPhoneOtpCode;
      return;
    }
    if (field === 'contactVerifyOtp') {
      (this.state as any)._contactVerifyOtp = el.value.replace(/\D/g, '').slice(0, 6);
      el.value = (this.state as any)._contactVerifyOtp;
      return;
    }

    // ── Book-for-other fields (portal confirm step) ───────────────────
    if (field === 'otherFirstName') { this.state.otherFirstName = el.value; }
    if (field === 'otherLastName')  { this.state.otherLastName = el.value; }
    if (field === 'otherEmail')     { this.state.otherEmail = el.value; }
    if (field === 'otherPhone')     { this.state.otherPhone = el.value; }

    if (field.startsWith('other') && this.state.bookForOther) {
      // The Confirm button stays tappable while blocked (it explains itself);
      // keep the banner + highlights honest as the fields fill in.
      if (this.state.showBlockedSummary) {
        const banner = this.bodyEl.querySelector<HTMLElement>('.pp-blocked-banner');
        if (banner) {
          const reasons = this.state.blockedReasons;
          if (this.state.otherValid || !reasons.length) banner.remove();
          else banner.innerHTML = '<div class="pp-blocked-title">Almost there — still needed:</div>'
            + reasons.map(r => `<button type="button" class="pp-blocked-item" data-action="goto-missing" data-target="${r.field}">${r.label}</button>`).join('');
        }
        el.classList.toggle('pp-f-missing', !el.value.trim());
      }
      return;
    }

    // ── Booking contact form / GC / Package fields via setField ───────
    const value = el.type === 'checkbox' ? el.checked : el.value;
    this.state.setField(field, value);

    // A referral code applied before the phone was typed: check it again.
    if ((field === 'phone' || field === 'otherPhone') && String(value || '').replace(/\D/g, '').length >= 10) this._recheckReferral();
    // Book for someone else: the responsibility toggle shows only while
    // their phone is blank (Tim, 2026-11-03).
    if (field === 'otherPhone' || field === 'phone' || field === 'otherResponsibility') {
      const noPhone = this.state.otherPhoneMissing;
      this.bodyEl.querySelectorAll<HTMLElement>('[data-only-without-other-phone]').forEach(r => { r.hidden = !noPhone; });
      const errEl = this.bodyEl.querySelector<HTMLElement>('[data-error="otherResponsibility"]');
      if (errEl && (!noPhone || this.state.formData.otherResponsibility)) errEl.textContent = '';
      this.updateFooterBtn();
    }

    const bookingTextFields = [
      'phone', 'email', 'otherPhone', 'otherEmail',
      'firstName', 'lastName', 'otherFirstName', 'otherLastName',
      'promo', 'comments',
    ];
    const gcTextFields = ['gcYourPhone', 'gcYourEmail', 'gcRecipientEmail', 'gcCardNumber', 'gcExpiry', 'gcCvc'];
    const pkgTextFields = ['pkgFirstName', 'pkgLastName', 'pkgPhone', 'pkgEmail', 'pkgCardNumber', 'pkgExpiry', 'pkgCvc'];
    const memTextFields = ['memFirstName', 'memLastName', 'memPhone', 'memEmail', 'memCardNumber', 'memExpiry', 'memCvc'];

    if (bookingTextFields.includes(field)) {
      this.updateErrors(field);
      this.updateFooterBtn();
    } else if (gcTextFields.includes(field)) {
      // Format card number / expiry
      if (field === 'gcCardNumber') {
        let v = (value as string).replace(/\D/g, '').slice(0, 16);
        v = v.replace(/(.{4})(?=.)/g, '$1 ');
        this.state.setField(field, v);
        el.value = v;
      } else if (field === 'gcExpiry') {
        let v = (value as string).replace(/\D/g, '').slice(0, 4);
        if (v.length > 2) v = v.slice(0, 2) + ' / ' + v.slice(2);
        this.state.setField(field, v);
        el.value = v;
      }
      this.updateErrors(field);
      this.updateFooterBtn();
      // Phone-first: the moment a full number is in, unfold name + email
      // (one re-render; the phone keeps focus via the step-change focus rule).
      if (field === 'gcYourPhone' && this.state.gcYourDetailsExpanded && !this.bodyEl.querySelector('[data-field="gcYourFirstName"]')) {
        this.render();
        const first = this.bodyEl.querySelector<HTMLInputElement>('[data-field="gcYourFirstName"]');
        if (first) first.focus({ preventScroll: true });
      }
    } else if (pkgTextFields.includes(field)) {
      if (field === 'pkgCardNumber') {
        let v = (value as string).replace(/\D/g, '').slice(0, 16);
        v = v.replace(/(.{4})(?=.)/g, '$1 ');
        this.state.setField(field, v);
        el.value = v;
      } else if (field === 'pkgExpiry') {
        let v = (value as string).replace(/\D/g, '').slice(0, 4);
        if (v.length > 2) v = v.slice(0, 2) + ' / ' + v.slice(2);
        this.state.setField(field, v);
        el.value = v;
      }
      this.updateFooterBtn();
    } else if (memTextFields.includes(field)) {
      if (field === 'memCardNumber') {
        let v = (value as string).replace(/\D/g, '').slice(0, 16);
        v = v.replace(/(.{4})(?=.)/g, '$1 ');
        this.state.setField(field, v);
        el.value = v;
      } else if (field === 'memExpiry') {
        let v = (value as string).replace(/\D/g, '').slice(0, 4);
        if (v.length > 2) v = v.slice(0, 2) + ' / ' + v.slice(2);
        this.state.setField(field, v);
        el.value = v;
      }
      this.updateFooterBtn();
    } else if (field.match(/^guest\d+/)) {
      // Guest fields: targeted update (no full re-render to preserve focus)
      this.updateErrors(field);
      this.updateFooterBtn();
    } else {
      // Text input in formData or other dynamic fields — no re-render to preserve focus
      this.updateFooterBtn();
    }
  }

  private onChange(e: Event): void {
    const el = e.target as HTMLInputElement;
    const field = el.dataset.field;
    if (!field) return;
    const value = el.type === 'checkbox' ? el.checked : el.value;
    this.state.setField(field, value);
    if (el.type === 'checkbox' || el.tagName === 'SELECT') {
      this.render();
    }
  }

  // ── Footer action dispatcher ─────────────────────────────────────────

  private async handleFooterAction(): Promise<void> {
    const step = this.state.step;

    if (step === 'gc-details' && this.state.gcShowMissing) {
      const ids = new Set(this.state.gcMissingDetails.map(([id]) => id));
      this.bodyEl.querySelectorAll<HTMLElement>('[data-field]').forEach(inp => {
        inp.classList.toggle('pp-f-input--missing', ids.has(inp.dataset.field || ''));
      });
    }

    if (step === 'book-enhancements') {
      // A required group is a question the service needs answered (often the
      // price): say which one is open instead of walking past it.
      const missing = this.state.missingRequiredEnhGroups;
      if (missing.length) {
        this.state.enhShowMissing = true;
        this.render();
        const el = this.panelEl.querySelector<HTMLElement>(`[data-enh-group="${missing[0].id}"]`);
        if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      this.state.enhShowMissing = false;
      this.state.navigate('book-provider');
      this.render();
      return;
    }

    if (step === 'book-payment') {
      await this.payDeposit();
      return;
    }

    if (step === 'book-contact') {
      await this.submitContactBooking();
      return;
    }


    if (step === 'gc-details') {
      const missing = this.state.gcMissingDetails;
      if (missing.length) {
        this.state.gcShowMissing = true;
        this.render();
        const first = this.panelEl.querySelector<HTMLElement>(`[data-field="${missing[0][0]}"]`);
        if (first) { first.scrollIntoView({ block: 'center', behavior: 'smooth' }); first.focus({ preventScroll: true }); }
        return;
      }
      this.state.gcShowMissing = false;
      trackWidgetEvent('gift_card_step', { step: 'payment', purchaseFor: this.state.gcPurchaseFor, amount: Number(this.state.gcAmount) || 0 });
      // Carry the buyer's name to the payment step (don't ask for it twice).
      this.state.prefillGcPaymentName();
      // Capture the buyer + a pending-purchase lead now (non-blocking) so a
      // stuck/abandoned checkout is still reachable.
      this.state.recordPendingGiftCardPurchase(this.cfg.apiBase);
      this.state.step = 'gc-payment';
      // Appear whole (card form + wallet button), like the deposit step.
      await this.revealPaymentStep();
      return;
    }

    if (step === 'gc-payment') {
      // Paint the disabled button BEFORE waiting on Stripe — until now the
      // screen only redrew after the purchase came back, so the button was
      // live (and re-tappable) for the whole round trip.
      //
      // FOOTER ONLY, never render(): Stripe is reading the card out of its
      // iframe at this moment, and render() rebuilds the body — the iframe
      // goes with it and the purchase dies with Stripe's "We could not
      // retrieve data from the specified Element" (a client could not buy a
      // gift card by typed card at all, 2026-09-19). Same rule as payDeposit.
      const purchase = this.state.submitGiftCardPayment(this.cfg.apiBase);
      this.updateFooterBtn();
      if (this.state.gcSubmitting) this.showBusy('Processing your payment…');
      try {
        await purchase;
      } finally {
        this.hideBusy();
      }
      this.render();
      return;
    }

    if (step === 'pkg-contact') {
      this.state.step = 'pkg-payment';
      await this.revealPaymentStep();
      return;
    }

    if (step === 'pkg-payment') {
      await this.state.submitPkgPayment(this.cfg.apiBase);
      this.render();
      return;
    }

    if (step === 'mem-contact') {
      this.state.step = 'mem-payment';
      this.render();
      return;
    }

    if (step === 'mem-payment') {
      await this.state.submitMemPayment(this.cfg.apiBase);
      this.render();
      return;
    }
  }

  // ── Portal: login / OTP ──────────────────────────────────────────────

  private looksLikePhoneOrEmail(input: string): boolean {
    if (input.includes('@')) return true;
    const digits = input.replace(/\D/g, '');
    if (digits.length >= 4) return true;
    return false;
  }

  private async handleLogin(): Promise<void> {
    const query = this.state.loginQuery.trim();
    if (!query || !this.cfg.apiBase) return;

    if (!this.looksLikePhoneOrEmail(query)) {
      this.state.loginError = 'Please enter a phone number or email address.';
      this.render();
      return;
    }

    // Birthday auth: verify phone + birthday in one step
    if (this.state.authMethod === 'birthday') {
      await this.handleVerifyBirthday();
      return;
    }

    // If OTP already sent, this is a code verification
    if (this.state.otpSent) {
      await this.handleVerifyOtp();
      return;
    }

    // Step 1: Request OTP
    this.state.loginLoading = true;
    this.state.loginError = null;
    this.render();

    try {
      const isEmail = query.includes('@');
      const otpData = isEmail ? { email: query } : { phone: query };
      const result = await requestOtp(this.cfg.apiBase, otpData);
      this.state.otpSent = true;
      this.state.otpDebugCode = result.debug_code || null;
      // The challenge is created even if delivery failed. Be honest so the
      // client isn't left waiting for a code that never arrives.
      this.state.otpNotice = result.delivered === false
        ? (result.message || "We couldn't send your code right now. Please try again or contact the business directly.")
        : null;
      this.state.loginLoading = false;
      this.state.loginError = null;
      this.render();
    } catch (err: any) {
      this.state.loginError = err?.code === 'sms_stopped' && err?.message
        ? err.message
        : 'Something went wrong. Please try again.';
      this.state.loginLoading = false;
      this.render();
    }
  }

  private async handleVerifyOtp(): Promise<void> {
    const query = this.state.loginQuery.trim();
    const code = this.state.otpCode.trim();
    if (!code || !this.cfg.apiBase) {
      this.state.loginError = 'Please enter the verification code.';
      this.render();
      return;
    }

    this.state.loginLoading = true;
    this.state.loginError = null;
    this.render();

    try {
      const isEmail = query.includes('@');
      const verifyData = isEmail ? { email: query, code } : { phone: query, code };
      const result = await verifyOtp(this.cfg.apiBase, verifyData);

      if (result.error) {
        this.state.loginError = result.error;
        this.state.loginLoading = false;
        this.render();
        return;
      }

      if (result.status === 'pick_account' && result.clients) {
        this.state.multiAccountClients = result.clients;
        this.state.loginLoading = false;
        this.render();
        return;
      }

      if (result.status === 'registration_required' && result.registrationToken) {
        this.state.registrationToken = result.registrationToken;
        this.state.loginLoading = false;
        this.state.navigate('register');
        this.render();
        return;
      }

      if (result.token && result.clientId) {
        setClientToken(result.token, result.clientId, result.expiresInDays);
        await this.loginAsClient(result.clientId);
      } else {
        this.state.loginError = 'Verification failed. Please try again.';
        this.state.loginLoading = false;
        this.render();
      }
    } catch (_err) {
      this.state.loginError = 'Something went wrong. Please try again.';
      this.state.loginLoading = false;
      this.render();
    }
  }

  private async handleVerifyBirthday(pickClientId?: number): Promise<void> {
    const query = this.state.loginQuery.trim();
    const birthday = this.state.loginBirthday.trim();
    if (!query || !birthday || !this.cfg.apiBase) {
      this.state.loginError = 'Please enter your phone number and birthday.';
      this.render();
      return;
    }

    this.state.loginLoading = true;
    this.state.loginError = null;
    this.render();

    try {
      const data: { phone: string; birthday: string; pickClientId?: number } = { phone: query, birthday };
      if (pickClientId) data.pickClientId = pickClientId;
      const result = await verifyBirthday(this.cfg.apiBase, data);

      if (result.error) {
        this.state.loginError = result.error;
        this.state.loginLoading = false;
        this.render();
        return;
      }

      if (result.status === 'registration_required' && result.registrationToken) {
        this.state.registrationToken = result.registrationToken;
        this.state.loginLoading = false;
        this.state.step = 'register' as any;
        this.render();
        return;
      }

      if (result.status === 'pick_account' && result.clients) {
        this.state.multiAccountClients = result.clients;
        this.state.loginLoading = false;
        this.render();
        return;
      }

      if (result.status === 'verified' && result.token && result.clientId) {
        setClientToken(result.token, result.clientId, result.expiresInDays);
        await this.loginAsClient(result.clientId);
      } else {
        this.state.loginError = result.error || 'Verification failed. Please try again.';
        this.state.loginLoading = false;
        this.render();
      }
    } catch (_err) {
      this.state.loginError = 'Something went wrong. Please try again.';
      this.state.loginLoading = false;
      this.render();
    }
  }

  private async handlePickClient(clientId: number): Promise<void> {
    if (!this.cfg.apiBase) return;

    // Multi-account pick: verify again with chosen clientId to get a token
    if (this.state.multiAccountClients) {
      // Birthday auth: re-verify with pickClientId
      if (this.state.authMethod === 'birthday') {
        await this.handleVerifyBirthday(clientId);
        return;
      }
      this.state.loginLoading = true;
      this.state.loginError = null;
      this.render();

      try {
        const query = this.state.loginQuery.trim();
        const code = this.state.otpCode.trim();
        const isEmail = query.includes('@');
        const verifyData = isEmail
          ? { email: query, code, clientId }
          : { phone: query, code, clientId };
        const result = await verifyOtp(this.cfg.apiBase, verifyData);

        if (result.error) {
          this.state.loginError = result.error;
          this.state.loginLoading = false;
          this.render();
          return;
        }

        if (result.token && result.clientId) {
          setClientToken(result.token, result.clientId, result.expiresInDays);
          this.state.multiAccountNote = 'This email belongs to multiple accounts. Use your phone number to log in faster next time.';
          await this.loginAsClient(result.clientId);
        } else {
          this.state.loginError = 'Verification failed. Please try again.';
          this.state.loginLoading = false;
          this.render();
        }
      } catch (_err) {
        this.state.loginError = 'Something went wrong. Please try again.';
        this.state.loginLoading = false;
        this.render();
      }
      return;
    }

    this.state.loginLoading = true;
    this.render();
    await this.loginAsClient(clientId);
  }

  private async handleRegister(): Promise<void> {
    if (!this.cfg.apiBase || !this.state.registrationToken) return;

    const firstName = this.state.regFirstName.trim();
    const lastName = this.state.regLastName.trim();
    const email = this.state.regEmail.trim();

    if (!firstName) { this.state.regError = 'First name is required.'; this.render(); return; }
    if (!lastName) { this.state.regError = 'Last name is required.'; this.render(); return; }
    if (!email) { this.state.regError = 'Email is required.'; this.render(); return; }
    if (!email.includes('@')) { this.state.regError = 'Please enter a valid email address.'; this.render(); return; }
    const needsPhone = registrationNeedsPhone(this.state);
    const phone = this.state.regPhone.trim();
    if (needsPhone && !phone) { this.state.regError = 'Mobile number is required.'; this.render(); return; }
    if (needsPhone && phone.replace(/\D/g, '').length < 10) { this.state.regError = 'Please enter a valid mobile number.'; this.render(); return; }

    this.state.regLoading = true;
    this.state.regError = null;
    this.render();

    try {
      const result = await registerClient(this.cfg.apiBase, {
        registrationToken: this.state.registrationToken,
        firstName,
        lastName,
        email,
        ...(needsPhone ? { phone } : {}),
        marketingEmails: this.state.regMarketingEmails,
        marketingTexts: this.state.regMarketingTexts,
      });

      if (result.error) {
        this.state.regError = result.error;
        this.state.regLoading = false;
        this.render();
        return;
      }

      if (result.token && result.clientId) {
        setClientToken(result.token, result.clientId, result.expiresInDays);
        await this.loginAsClient(result.clientId);
      } else {
        this.state.regError = 'Registration failed. Please try again.';
        this.state.regLoading = false;
        this.render();
      }
    } catch (_err) {
      this.state.regError = 'Something went wrong. Please try again.';
      this.state.regLoading = false;
      this.render();
    }
  }

  /**
   * A device that just booked may ask what memberships that client holds — and
   * nothing else. Kept apart from the portal session: it would fail everything
   * the session does, and sharing the key would have the silent restore wipe it.
   */
  private storeRecognition(resp: any): void {
    const rt = resp?.recognitionToken;
    if (!rt?.token || !rt?.clientId || !this.cfg.apiBase) return;
    saveRecognition(this.cfg.apiBase, rt.token, rt.clientId, rt.expiresInDays || 30);
  }

  /**
   * The recognised-visitor bar — OPT IN, two ways: a
   * `<div data-calendoula-welcome>` says where it goes, or the spa turns on
   * "Welcome back bar" (Settings → Online Booking) and it pins itself to the
   * top of the page. Never on the hosted pages where the widget IS the page
   * (/book/, /portal/).
   */
  private async _showWelcomeBanner(): Promise<void> {
    if (typeof document === 'undefined' || !this.cfg.apiBase) return;
    if (this.cfg.mode === 'standalone' && !this.cfg.catalog && !this.cfg.priceList) return;
    const host = document.querySelector<HTMLElement>('[data-calendoula-welcome]');
    if (!host && !this.cfg.welcomeBannerEnabled) return;
    if (dismissedToday()) return;
    const recognised = loadRecognition(this.cfg.apiBase);
    const token = this.state.clientToken || recognised?.token;
    if (!token) return;
    const data = await fetchBanner(this.cfg.apiBase, token);
    if (!data) return;
    trackWidgetEvent('welcome_banner_shown', {
      has: data.nextAppointment ? 'appointment' : data.expiredHold ? 'expired_hold' : 'none',
    });
    showBanner(data, {
      onBook: () => { trackWidgetEvent('welcome_banner_book', {}); this.cfg.open!(); },
      onPortal: () => { trackWidgetEvent('welcome_banner_portal', {}); this.cfg.openPortal!(); },
      onForget: () => {
        // A shared laptop must be able to stop being someone else.
        trackWidgetEvent('welcome_banner_forget', {});
        forgetRecognition(this.cfg.apiBase!);
        forgetRemembered(this.cfg.apiBase!);
        this.state.clientToken = null;
        this.state.clientId = null;
        removeBanner();
      },
    }, host);
  }

  /** Load the client's own plans from a recognition token, for the pills. */
  private async loadRecognisedMemberships(): Promise<void> {
    if (!this.cfg.apiBase || this.state.clientId) return;   // a portal session wins
    const r = loadRecognition(this.cfg.apiBase);
    if (!r) return;
    try {
      this.state.memberships = await fetchClientMemberships(this.cfg.apiBase, r.clientId, r.token);
      if (this.state.memberships.length) this.render();
    } catch { /* the pills simply stay in advertising mode */ }
  }

  /** From a 'Send appointment details' link: the appointment to open once signed in. */
  private _openAppointmentId = 0;
  /** …and, once opened, the hosted page's own openPortal() must not land on the dashboard over it. */
  private _magicOpenedDetail = false;

  private async loginFromMagicLink(): Promise<void> {
    if (!this.cfg.apiBase || typeof window === 'undefined') return;
    let magic = '';
    let apptId = 0;
    try {
      const qs = new URLSearchParams(window.location.search);
      magic = qs.get('magic') || '';
      apptId = Number(qs.get('appointment') || 0) || 0;     // 'Send appointment details' link: open THAT appointment
    } catch { return; }
    if (!magic) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('magic');
      url.searchParams.delete('c');   // a campaign's link marker — nothing to keep in the address bar
      url.searchParams.delete('appointment');
      window.history.replaceState(null, '', url.toString());
    } catch { /* leave the URL */ }
    try {
      const result = await magicLogin(this.cfg.apiBase, magic);
      if (result.status !== 'verified' || !result.token || !result.clientId) return;
      setClientToken(result.token, result.clientId, result.expiresInDays);
      this.supersedeRecognition();
      const profile = await fetchClientProfile(this.cfg.apiBase, result.clientId);
      this.state.loginSuccess(profile, result.token);
      // The standalone page lands on the dashboard AFTER this; the dashboard
      // prefetch opens the appointment once its rows are in.
      if (apptId) this._openAppointmentId = apptId;
    } catch {
      // A rotated or mistyped link: the normal sign-in shows instead.
      clearClientSession();
    }
  }

  private async restoreSession(_remoteCfg: WidgetApiConfig): Promise<void> {
    const saved = getStoredSession();
    if (!saved || !this.cfg.apiBase) return;
    try {
      setClientToken(saved.token, saved.clientId);
      const profile = await fetchClientProfile(this.cfg.apiBase, saved.clientId);
      this.state.loginSuccess(profile, saved.token);
    } catch {
      // Token expired or invalid on server — clear and show login
      clearClientSession();
    }
  }

  /** Signing in replaces recognition with proof — the narrow token is retired. */
  private supersedeRecognition(): void {
    if (this.cfg.apiBase) forgetRecognition(this.cfg.apiBase);
  }

  private async loginAsClient(clientId: number): Promise<void> {
    this.supersedeRecognition();
    try {
      const profile = await fetchClientProfile(this.cfg.apiBase!, clientId);
      this.state.loginLoading = false;
      this.state.loginSuccess(profile);
      this.render();
      this.prefetchDashboardData();
    } catch (_err) {
      this.state.loginError = 'Unable to load your account. Please try again.';
      this.state.loginLoading = false;
      this.render();
    }
  }

  // ── Portal: data pre-fetch / lazy load ───────────────────────────────

  private async prefetchDashboardData(): Promise<void> {
    if (!this.cfg.apiBase || !this.state.clientId) { this.state.dashboardHolding = false; return; }
    void this.personaliseManifest();
    this.state.refreshToday();
    const api = this.cfg.apiBase;
    const cid = this.state.clientId;
    // The FIRST load of this person's home holds on one loading screen until
    // every piece is in and then paints at once (Tim, 2026-11-03 — the review
    // banner, Open Today and the referral link popped in seconds after the
    // rest). A refresh later (back from a booking, a new day) keeps the screen
    // and fills in as before.
    const firstLoad = !this.state.dashboardLoaded;
    if (firstLoad) { this.state.dashboardHolding = true; if (this.state.step === 'dashboard') this.render(); }
    const paint = () => { if (this.state.step === 'dashboard' && !this.state.dashboardHolding) this.render(); };
    // Everything is asked for AT ONCE. The wallet carries the portal offers
    // (the banners at the top of the home screen): it used to wait for
    // appointments and memberships, then for packages, forms and Open Today,
    // so an offer pushed with a campaign text appeared last (Tim, 2026-10-31).
    // The referral link, loyalty, punch cards and calendar sync only STARTED
    // once all of those had finished — now they go out with them.
    const ps = this.state.portalSections;
    const tasks: Promise<unknown>[] = [
      this.state.wallet === null
        ? fetchWallet(api, cid).then(w => { this.state.wallet = w; paint(); }).catch(() => {})
        : Promise.resolve(),
      Promise.all([fetchBookings(api, cid), fetchClientMemberships(api, cid)]).then(([bookings, memberships]) => {
        this.state.portalBookings = bookings;
        this.state.memberships = memberships;
        // A 'Send appointment details' link (/portal/?magic=…&appointment=<id>)
        // opens that appointment's detail once the rows are in (Tim, 2026-10-18).
        const wanted = this._openAppointmentId;
        if (wanted && bookings.some(b => b.id === wanted)) {
          this._openAppointmentId = 0;
          this._magicOpenedDetail = true;
          this.state.selectedBookingId = wanted;
          this.state.navigate('booking-detail');
          this.render();
        } else paint();
      }).catch(() => {}),
      (ps.packages && this.state.portalPackages === null)
        ? fetchClientPackages(api, cid).then(p => { this.state.portalPackages = p; paint(); }).catch(() => {})
        : Promise.resolve(),
      // Forms to complete before the visit + the intake refresh prompt.
      this.state.formSubmissions === null
        ? fetchPortalForms(api, cid).then(f => {
            this.state.formSubmissions = f;
            // A deep link may already be on the appointment: draw its forms.
            if (this.state.step === 'booking-detail') this.render(); else paint();
          }).catch(() => {})
        : Promise.resolve(),
      // 'Open today': is there anything to offer right now?
      this._loadSameDayToday().then(paint).catch(() => {}),
      this.state.referralEnabled ? this.loadReferralCodeIfNeeded() : Promise.resolve(),
      this.state.loyaltyEnabled ? this.loadLoyaltyPointsIfNeeded() : Promise.resolve(),
      this.loadPunchCardsIfNeeded(),
      this.state.calSyncEnabled ? this.loadCalendarSyncToken() : Promise.resolve(),
    ];
    // One slow answer never holds the home for long: after the cap it paints
    // with what is in, and the rest fills in as it lands.
    await Promise.race([
      Promise.all(tasks),
      new Promise(resolve => setTimeout(resolve, BookingWidget.DASHBOARD_HOLD_MAX_MS)),
    ]);
    if (firstLoad && this.state.clientId === cid) {
      this.state.dashboardHolding = false;
      this.state.dashboardLoaded = true;
      if (this.state.step === 'dashboard') this.render();
    }
  }

  /** The longest the first home screen waits on its slowest piece. */
  static DASHBOARD_HOLD_MAX_MS = 6000;

  private async loadReferralCodeIfNeeded(): Promise<void> {
    if (this.state.referralCode || this.state.referralLoading || !this.cfg.apiBase) return;
    this.state.referralLoading = true;
    try {
      const data = await fetchReferralCode(this.cfg.apiBase);
      if (data && data.enabled) {
        this.state.referralCode = data.code;
        this.state.referralEnabled = true;
        this.state.referralDiscountLabel = data.discountLabel || '';
        this.state.referralRewardDescription = data.rewardDescription || '';
        this.state.referralTerms = Array.isArray(data.terms) ? data.terms : [];
        this.state.referralShareUrl = data.shareUrl || '';
        this.state.referralCount = Number(data.referredCount || 0);
        this.state.referralPending = Number(data.pendingCount || 0);
        this.state.referralEarned = Number(data.earned || 0);
        if (this.state.step === 'dashboard') this.render();
      }
    } catch { /* silent */ }
    this.state.referralLoading = false;
  }

  private async loadLoyaltyPointsIfNeeded(): Promise<void> {
    if (this.state.loyaltyLoading || !this.cfg.apiBase) return;
    this.state.loyaltyLoading = true;
    try {
      const data = await fetchLoyaltyPoints(this.cfg.apiBase);
      if (data && data.enabled) {
        this.state.loyaltyEnabled = true;
        this.state.loyaltyPoints = data.points;
        this.state.loyaltyLifetimePoints = data.lifetimePoints;
        this.state.loyaltyAvailableTiers = data.availableTiers || [];
        this.state.loyaltyNextTier = data.nextTier || null;
        this.state.loyaltyPointsToNextTier = data.pointsToNextTier || 0;
        if (this.state.step === 'dashboard') this.render();
      }
    } catch { /* silent */ }
    this.state.loyaltyLoading = false;
  }

  private async loadPunchCardsIfNeeded(): Promise<void> {
    if (this.state.punchCardsLoading || !this.cfg.apiBase) return;
    this.state.punchCardsLoading = true;
    try {
      const cards = await fetchPunchCards(this.cfg.apiBase);
      this.state.punchCards = cards.filter(c => c.status === 'active' || c.status === 'completed');
      if (this.state.step === 'dashboard') this.render();
    } catch { /* silent */ }
    this.state.punchCardsLoading = false;
  }

  private async loadCalendarSyncToken(): Promise<void> {
    if (this.state.calSyncFeedUrl || this.state.calSyncLoading || !this.cfg.apiBase) return;
    this.state.calSyncLoading = true;
    try {
      const clientTok = this.state.clientToken;
      if (!clientTok) { this.state.calSyncLoading = false; return; }
      const res = await fetch(`${this.cfg.apiBase}/api/v1/client-auth/calendar-token/`, {
        headers: { 'Authorization': `ClientBearer ${clientTok}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.feedUrl) {
          this.state.calSyncFeedUrl = data.feedUrl;
          if (this.state.step === 'dashboard') this.render();
        }
      }
    } catch { /* silent */ }
    this.state.calSyncLoading = false;
  }

  private async generateCalendarSyncUrl(): Promise<void> {
    if (!this.cfg.apiBase) return;
    this.state.calSyncLoading = true;
    this.render();
    try {
      const clientTok = this.state.clientToken;
      if (!clientTok) { this.state.calSyncLoading = false; this.render(); return; }
      const res = await fetch(`${this.cfg.apiBase}/api/v1/client-auth/calendar-token/`, {
        method: 'POST',
        headers: { 'Authorization': `ClientBearer ${clientTok}` },
      });
      if (res.ok) {
        const data = await res.json();
        this.state.calSyncFeedUrl = data.feedUrl || '';
      }
    } catch { /* silent */ }
    this.state.calSyncLoading = false;
    this.render();
  }

  private async revokeCalendarSyncUrl(): Promise<void> {
    if (!this.cfg.apiBase) return;
    this.state.calSyncLoading = true;
    this.render();
    try {
      const clientTok = this.state.clientToken;
      if (!clientTok) { this.state.calSyncLoading = false; this.render(); return; }
      await fetch(`${this.cfg.apiBase}/api/v1/client-auth/calendar-token/`, {
        method: 'DELETE',
        headers: { 'Authorization': `ClientBearer ${clientTok}` },
      });
      this.state.calSyncFeedUrl = '';
    } catch { /* silent */ }
    this.state.calSyncLoading = false;
    this.render();
  }

  private async loadBookingsIfNeeded(): Promise<void> {
    if (this.state.portalBookings !== null || !this.cfg.apiBase || !this.state.clientId) return;
    this.state.bookingsLoading = true;
    this.render();
    try {
      this.state.portalBookings = await fetchBookings(this.cfg.apiBase, this.state.clientId);
    } catch (_e) {
      this.state.portalBookings = [];
    }
    this.state.bookingsLoading = false;
    this.render();
  }

  private async loadSalesIfNeeded(): Promise<void> {
    if (this.state.sales !== null || !this.cfg.apiBase || !this.state.clientId) return;
    this.state.salesLoading = true;
    this.render();
    try {
      this.state.sales = await fetchSales(this.cfg.apiBase, this.state.clientId);
    } catch (_e) {
      this.state.sales = [];
    }
    this.state.salesLoading = false;
    this.render();
  }

  private async loadWalletIfNeeded(): Promise<void> {
    if (this.state.wallet !== null || !this.cfg.apiBase || !this.state.clientId) return;
    this.state.walletLoading = true;
    this.render();
    try {
      this.state.wallet = await fetchWallet(this.cfg.apiBase, this.state.clientId);
    } catch (_e) {
      this.state.wallet = { accountBalance: 0, balanceHistory: [], creditCards: [], giftCards: [] };
    }
    this.state.walletLoading = false;
    this.render();
  }

  private async loadPackagesIfNeeded(): Promise<void> {
    if (this.state.portalPackages !== null || !this.cfg.apiBase || !this.state.clientId) return;
    this.state.packagesLoading = true;
    this.render();
    try {
      this.state.portalPackages = await fetchClientPackages(this.cfg.apiBase, this.state.clientId);
    } catch (_e) {
      this.state.portalPackages = [];
    }
    this.state.packagesLoading = false;
    this.render();
  }

  private async loadMembershipsIfNeeded(): Promise<void> {
    if (this.state.memberships !== null || !this.cfg.apiBase || !this.state.clientId) return;
    this.state.membershipsLoading = true;
    this.render();
    try {
      this.state.memberships = await fetchClientMemberships(this.cfg.apiBase, this.state.clientId);
    } catch (_e) {
      this.state.memberships = [];
    }
    this.state.membershipsLoading = false;
    this.render();
  }

  private async loadSaleDetailIfNeeded(saleId: number): Promise<void> {
    if (!this.cfg.apiBase) return;
    const existing = this.state.sales?.find(s => s.id === saleId);
    if (existing?.items) return;
    this.state.saleDetailLoading = true;
    this.render();
    try {
      const detail = await fetchSaleDetail(this.cfg.apiBase, saleId);
      if (this.state.sales) {
        const idx = this.state.sales.findIndex(s => s.id === saleId);
        if (idx >= 0) this.state.sales[idx] = detail;
        else this.state.sales.push(detail);
      }
    } catch (_e) { /* leave items undefined */ }
    this.state.saleDetailLoading = false;
    this.render();
  }

  private async loadFormSubmissionsIfNeeded(): Promise<void> {
    if (this.state.formSubmissions !== null || !this.cfg.apiBase || !this.state.clientId) return;
    this.state.formSubmissionsLoading = true;
    this.render();
    try {
      this.state.formSubmissions = await fetchFormSubmissions(this.cfg.apiBase, this.state.clientId);
    } catch (_e) {
      this.state.formSubmissions = [];
    }
    this.state.formSubmissionsLoading = false;
    this.render();
  }

  private async loadFormDetailIfNeeded(submissionId: number): Promise<void> {
    if (!this.cfg.apiBase) return;
    this.state.formDetailLoading = true;
    this.render();
    try {
      this.state.selectedFormDetail = await fetchFormSubmissionDetail(this.cfg.apiBase, submissionId);
    } catch (_e) {
      this.state.selectedFormDetail = null;
    }
    this.state.formDetailLoading = false;
    this.render();
  }

  // ── Portal: recurring ──────────────────────────────────────────────

  private async loadRecurringIfNeeded(): Promise<void> {
    if (this.state.recurringSeries !== null || !this.cfg.apiBase || !this.state.clientId) return;
    this.state.recurringLoading = true;
    this.render();
    try {
      this.state.recurringSeries = await fetchRecurringSeries(this.cfg.apiBase);
    } catch (_e) {
      this.state.recurringSeries = [];
    }
    this.state.recurringLoading = false;
    this.render();
  }

  private async loadRecurringDetailIfNeeded(seriesId: number): Promise<void> {
    if (!this.cfg.apiBase) return;
    this.state.seriesDetailLoading = true;
    this.render();
    try {
      this.state.selectedSeriesDetail = await fetchRecurringDetail(this.cfg.apiBase, seriesId);
    } catch (_e) {
      this.state.selectedSeriesDetail = null;
    }
    this.state.seriesDetailLoading = false;
    this.render();
  }

  private async handleSkipOccurrence(seriesId: number, date: string): Promise<void> {
    if (!this.cfg.apiBase) return;
    try {
      await skipRecurringOccurrence(this.cfg.apiBase, seriesId, date);
      // Reload detail
      await this.loadRecurringDetailIfNeeded(seriesId);
    } catch (_e) { /* ignore */ }
  }

  private async handleCancelRecurring(seriesId: number): Promise<void> {
    if (!this.cfg.apiBase) return;
    try {
      await cancelRecurringSeries(this.cfg.apiBase, seriesId);
      // Reload list + detail
      this.state.recurringSeries = null;
      this.state.navigate('recurring');
      this.render();
      await this.loadRecurringIfNeeded();
    } catch (_e) { /* ignore */ }
  }

  // ── Portal: one-click purchase / enrollment ────────────────────────

  private async handlePortalPurchaseConfirm(): Promise<void> {
    if (!this.cfg.apiBase || !this.state.clientId) return;
    const s = this.state;

    // Validate agreement if required
    if (s.portalPurchaseType === 'package') {
      const tmpl = s.packages.find(t => t.id === s.portalPurchaseTemplateId);
      if (tmpl?.requireAgreement && !s.portalPurchaseAgreed) {
        s.portalPurchaseError = 'You must agree to the terms to continue.';
        this.render();
        return;
      }
    } else {
      const plan = s.membershipPlans.find(p => p.id === s.portalPurchaseTemplateId);
      if (plan?.requireAgreement && !s.portalPurchaseAgreed) {
        s.portalPurchaseError = 'You must agree to the terms to continue.';
        this.render();
        return;
      }
    }

    s.portalPurchaseStep = 'processing';
    s.portalPurchaseError = null;
    this.render();

    try {
      if (s.portalPurchaseType === 'package') {
        await portalPurchasePackage(this.cfg.apiBase, s.portalPurchaseTemplateId!);
        // Force reload purchased packages
        s.portalPackages = null;
        await this.loadPackagesIfNeeded();
      } else {
        await portalEnrollMembership(this.cfg.apiBase, s.portalPurchaseTemplateId!, s.portalPurchaseAgreed);
        // Force reload memberships
        s.memberships = null;
        await this.loadMembershipsIfNeeded();
      }
      s.portalPurchaseStep = 'confirm';
      s.portalPurchaseSuccess = true;
    } catch (err: any) {
      s.portalPurchaseStep = 'confirm';
      s.portalPurchaseError = err.message || 'Something went wrong. Please try again.';
    }
    this.render();
  }

  /** The Wallet's Stripe card box on the current screen, if one is showing:
   *  the pay screen's "new card" field, or the Wallet's own "Add a card". */
  private _walletCardBoxId(): string {
    const st = this.state;
    if (st.step === 'wallet-pay' && st.walletPayCardId === 'new' && !st.walletPaySuccess) return 'pp-stripe-card-element-wallet';
    if (st.step === 'wallet' && st.walletAddCardOpen) return 'pp-stripe-card-element-walletadd';
    if (st.step === 'book-card-on-file') return 'pp-stripe-card-element-cof';
    return '';
  }

  /** A new card from the manual (no Stripe) fields: brand/last4/expiry. */
  private _manualCardFromFields(): { last4: string; brand: string; expMonth: number; expYear: number } | string {
    const root = this.bodyEl;
    const cardNumber = (root.querySelector('[data-field="cardNumber"]') as HTMLInputElement)?.value?.replace(/\s/g, '') || '';
    const expiry = (root.querySelector('[data-field="expiry"]') as HTMLInputElement)?.value || '';
    const cvc = (root.querySelector('[data-field="cvc"]') as HTMLInputElement)?.value || '';
    if (cardNumber.length < 13) return 'Please enter a valid card number.';
    const expiryMatch = expiry.match(/^(\d{2})\/(\d{2})$/);
    if (!expiryMatch) return 'Please enter expiry as MM/YY.';
    if (cvc.length < 3) return 'Please enter a valid CVC.';
    const first = cardNumber[0];
    return {
      last4: cardNumber.slice(-4),
      brand: first === '4' ? 'Visa' : first === '5' ? 'Mastercard' : first === '3' ? 'Amex' : 'Card',
      expMonth: parseInt(expiryMatch[1], 10), expYear: 2000 + parseInt(expiryMatch[2], 10),
    };
  }

  /** Wallet → "Add a card" (Stripe when configured, else the manual fields). */
  /** 'Book with this package / membership': the portal booking flow with
   *  the session attached — menu narrowed to what it covers, no deposit on
   *  that service, the server reserves the session at booking. */
  private async handlePortalBookCredit(kind: 'package' | 'membership' | 'promotion', id: number, groupId: string): Promise<void> {
    const st = this.state;
    let credit: BookingCredit | null = null;
    if (kind === 'promotion') {
      // An OFFER on the client's record (Tim, 2026-10-16): the server reserves
      // it at booking and the deposit is on the discounted price.
      const p = (st.wallet?.promotions || []).find(x => Number(x.id) === id);
      if (!p || !promotionCanBook(p, st.todayStr)) return;
      const menu = promotionMenuScope(p);
      const addonOnly = p.scope === 'specific' && !(p.serviceIds || []).length && !(p.categoryIds || []).length && (p.enhancementOptionIds || []).length > 0;
      credit = {
        kind, id, groupId: null, name: p.label, remaining: 1,
        staffIds: (p.terms?.staffIds || []).map(Number),
        serviceIds: menu.serviceIds, categoryIds: menu.categoryIds,
        discountType: p.discountType, discountValue: Number(p.discountValue || 0),
        rewardKind: p.rewardKind || '',
        ...(addonOnly ? { enhancementOptionIds: (p.enhancementOptionIds || []).map(String) } : {}),
      };
    } else if (kind === 'package') {
      const pkg = (st.portalPackages || []).find(p => Number(p.id) === id);
      const g = (pkg?.groups || []).find(x => String(x.groupId) === groupId);
      if (pkg && g) credit = { kind, id, groupId: g.groupId, name: pkg.name, serviceIds: (g.serviceIds || []).map(Number), staffIds: [], remaining: Number(g.remaining ?? 0) };
    } else {
      const m = (st.memberships || []).find(x => Number(x.id) === id);
      const g = (m?.groups || []).find(x => String(x.groupId) === groupId);
      if (m && g) credit = { kind, id, groupId: g.groupId, name: m.planName, serviceIds: (g.serviceIds || []).map(Number), staffIds: (m.planStaffIds || []).map(Number), remaining: Number(g.remaining ?? 0) };
    }
    if (!credit || credit.remaining < 1) return;
    trackWidgetEvent('portal_book_with_credit', { kind });
    st.startBookingWithCredit(credit);
    this.render();
    if (!st.bookingDataLoaded) await this.ensureBookingData();
    // One covered service: skip the menu and go straight to it.
    if (credit.serviceIds.length === 1) {
      const sid = credit.serviceIds[0];
      const cat = st.categories.find(c => c.services.some(sv => Number(sv.id) === sid));
      if (cat) { st.expandedCat = cat.id; this.render(); }
    }
  }

  /** The card-on-file step after a booking made with a package / membership
   *  session: save a card without charging it, then 'You're all set'. */
  private async handleCardOnFileSave(): Promise<void> {
    const st = this.state;
    if (!this.cfg.apiBase || !st.clientId || st.cardOnFileBusy) return;
    if (!(st.stripePublishableKey && isStripeEnabled())) { st.cardOnFileError = 'Card entry is not available right now.'; this.render(); return; }
    st.cardOnFileBusy = true; st.cardOnFileError = null; this.render();
    try {
      const pmId = await createPaymentMethod({});
      await addCreditCard(this.cfg.apiBase, st.clientId, { stripePaymentMethodId: pmId });
      st.wallet = null;
      trackWidgetEvent('card_on_file_saved', { after: 'credit_booking' });
      st.cardOnFileBusy = false;
      this._clearDraft();
      st.setStep('book-success');
    } catch (err: any) {
      st.cardOnFileBusy = false;
      st.cardOnFileError = err?.message || 'Could not save the card. Please check the details and try again.';
    }
    this.render();
  }

  private async handleWalletAddCard(): Promise<void> {
    if (!this.cfg.apiBase || !this.state.clientId || this.state.walletCardBusy) return;
    let body: Parameters<typeof addCreditCard>[2];
    if (this.state.stripePublishableKey && isStripeEnabled()) {
      this.state.walletCardBusy = true; this.state.walletCardError = null; this.render();
      try {
        const pmId = await createPaymentMethod({});
        body = { stripePaymentMethodId: pmId };
      } catch (err: any) {
        this.state.walletCardBusy = false;
        this.state.walletCardError = err?.message || 'Please check the card details.';
        this.render();
        return;
      }
    } else {
      const m = this._manualCardFromFields();
      if (typeof m === 'string') { this.state.walletCardError = m; this.render(); return; }
      body = m;
      this.state.walletCardBusy = true; this.state.walletCardError = null; this.render();
    }
    try {
      await addCreditCard(this.cfg.apiBase, this.state.clientId, body);
      this.state.walletAddCardOpen = false;
      this.state.wallet = null;
      await this.loadWalletIfNeeded();
    } catch (err: any) {
      this.state.walletCardError = err?.message || 'Could not save the card. Please try again.';
    }
    this.state.walletCardBusy = false;
    this.render();
  }

  private async handleWalletCardDefault(cardId: number): Promise<void> {
    if (!this.cfg.apiBase || !this.state.clientId || this.state.walletCardBusy) return;
    this.state.walletCardBusy = true; this.state.walletCardError = null; this.render();
    try {
      await setDefaultCreditCard(this.cfg.apiBase, this.state.clientId, cardId);
      if (this.state.wallet) {
        this.state.wallet.creditCards = this.state.wallet.creditCards.map(c => ({ ...c, isDefault: c.id === cardId }));
      }
    } catch (err: any) {
      this.state.walletCardError = err?.message || 'Could not update the card.';
    }
    this.state.walletCardBusy = false;
    this.render();
  }

  private async handleWalletCardRemove(cardId: number): Promise<void> {
    if (!this.cfg.apiBase || !this.state.clientId || this.state.walletCardBusy) return;
    const card = this.state.wallet?.creditCards.find(c => c.id === cardId);
    if (!card) { this.state.walletRemoveCardId = null; this.render(); return; }
    this.state.walletCardBusy = true; this.state.walletCardError = null; this.render();
    try {
      await deleteCreditCard(this.cfg.apiBase, this.state.clientId, cardId);
      if (this.state.wallet) this.state.wallet.creditCards = this.state.wallet.creditCards.filter(c => c.id !== cardId);
      this.state.walletRemoveCardId = null;
    } catch (err: any) {
      this.state.walletRemoveCardId = null;
      this.state.walletCardError = err?.message || 'Could not remove the card.';
    }
    this.state.walletCardBusy = false;
    this.render();
  }

  /** Wallet → Pay: fund the balance or settle what is owed. The server
   *  charges, writes a closed sale + the ledger line, and returns the new
   *  balance; nothing is credited without a charge. */
  private async handleWalletPay(): Promise<void> {
    const st = this.state;
    if (!this.cfg.apiBase || !st.clientId || st.walletPaying) return;
    const amount = st.walletPayAmountNum;
    if (!amount) { st.walletPayError = 'Enter an amount above zero.'; this.render(); return; }
    if (amount > 5000) { st.walletPayError = 'Online payments are limited to $5,000 at a time.'; this.render(); return; }
    if (st.walletPayCardId === null) { st.walletPayError = 'Choose a card.'; this.render(); return; }
    const body: Parameters<typeof payAccountBalance>[2] = { amount, attemptId: st.walletPayAttemptId };
    if (st.walletPayCardId === 'new') {
      if (!(st.stripePublishableKey && isStripeEnabled())) {
        st.walletPayError = 'Online card payments are not set up. Please call us.'; this.render(); return;
      }
      st.walletPaying = true; st.walletPayError = null; this.render();
      try {
        body.paymentMethodToken = await createPaymentMethod({});
      } catch (err: any) {
        st.walletPaying = false; st.walletPayError = err?.message || 'Please check the card details.'; this.render(); return;
      }
      body.saveCard = st.walletPaySaveCard;
    } else {
      body.cardId = st.walletPayCardId;
      st.walletPaying = true; st.walletPayError = null; this.render();
    }
    this.showBusy('Processing your payment…');
    try {
      const res = await payAccountBalance(this.cfg.apiBase, st.clientId, body);
      st.walletPaySuccess = { amount: res.amount, saleNumber: res.saleNumber, accountBalance: res.accountBalance, cardBrand: res.cardBrand, cardLast4: res.cardLast4 };
      if (st.client) st.client.accountBalance = res.accountBalance;
      st.wallet = null;
      st.sales = null;
      trackWidgetEvent('portal_balance_paid', { amount: res.amount });
    } catch (err: any) {
      st.walletPayError = err?.message || 'The payment could not be completed.';
      // A DEFINITIVE refusal (declined, bad input) gets a fresh id so the next
      // tap is a new charge. A dropped connection or a 5xx keeps the id: the
      // charge may have gone through, and the same key makes the provider
      // replay it rather than charge twice (the server then credits once).
      const status = Number(err?.status || 0);
      if (status && status < 500 && status !== 429) {
        st.walletPayAttemptId = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
      }
    }
    this.hideBusy();
    st.walletPaying = false;
    this.render();
    if (st.walletPaySuccess) await this.loadWalletIfNeeded();
  }

  /** The hosted portal is installable (manifest + service worker beside the
   *  page): remember Chrome's install prompt for the home-screen banner, know
   *  when we ARE the installed app, and spot an iPhone, which has no prompt. */
  private _watchInstallability(): void {
    if (!this.state.isStandalone || this.cfg.entryMode !== 'portal') return;
    const st = this.state;
    try {
      const nav = navigator as Navigator & { standalone?: boolean };
      st.runningAsApp = !!(window.matchMedia?.('(display-mode: standalone)').matches || nav.standalone);
      st.isIosBrowser = /iPhone|iPad|iPod/i.test(navigator.userAgent) && !st.runningAsApp;
      st.installDismissed = localStorage.getItem('pp_install_dismissed') === '1';
    } catch { /* storage or matchMedia unavailable — no banner */ }
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      st.installPrompt = e as unknown as typeof st.installPrompt;
      if (st.step === 'dashboard') this.render();
    });
    // A booking made on the portal's 'Open today' surface: the list, the
    // next-visit card and the deposit banner must follow.
    // Back to the app after a while: 'today' moves on and the home refreshes.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' || !this.state.clientId) return;
      const before = this.state.todayStr;
      this.state.refreshToday();
      if (this.state.todayStr !== before) { this.state.portalBookings = null; void this.prefetchDashboardData(); }
    });
    window.addEventListener('pp-quick-book-booked', () => {
      this.state.portalBookings = null;
      this.state.sameDayToday = null;
      void this.prefetchDashboardData();
    });
    window.addEventListener('appinstalled', () => {
      st.installDone = true;
      st.installPrompt = null;
      trackWidgetEvent('portal_app_installed', {});
      if (st.step === 'dashboard') this.render();
    });
  }

  /** Can THIS page subscribe to push? Needs the portal's service worker
   *  (the hosted page registers it) and the Push API. */
  private async _pushRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) return null;
    try {
      const reg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<null>(res => setTimeout(() => res(null), 3000)),
      ]);
      return (reg as ServiceWorkerRegistration | null) || null;
    } catch { return null; }
  }

  private async loadPortalTags(): Promise<void> {
    if (!this.cfg.apiBase || !this.state.clientId) return;
    this.state.portalTagGroups = await fetchPortalTags(this.cfg.apiBase);
    if (this.state.step === 'preferences') this.render();
  }

  private async loadPushState(): Promise<void> {
    const st = this.state;
    if (!this.cfg.apiBase || !st.clientId) return;
    const reg = await this._pushRegistration();
    st.pushSupported = !!reg && typeof Notification !== 'undefined';
    let local: PushSubscription | null = null;
    try { local = reg ? await reg.pushManager.getSubscription() : null; } catch { local = null; }
    const remote = await fetchClientPushStatus(this.cfg.apiBase);
    st.pushDevices = remote?.devices || 0;
    // 'On' here means THIS device is subscribed and the server knows it.
    st.pushEnabled = !!local && !!remote?.enabled;
    st.pushLoaded = true;
    if (st.step === 'preferences') this.render();
  }

  /** Switch notifications on or off for this device (Tim, 2026-11-02). */
  private async togglePush(): Promise<void> {
    const st = this.state;
    if (!this.cfg.apiBase || st.pushBusy) return;
    st.pushBusy = true; st.pushError = null; st.pushNote = null; this.render();
    try {
      const reg = await this._pushRegistration();
      if (!reg) throw new Error('Notifications are not available here.');
      if (st.pushEnabled) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const r = await unsubscribeClientPush(this.cfg.apiBase, sub.endpoint);
          st.pushDevices = r.devices;
          try { await sub.unsubscribe(); } catch { /* already gone */ }
        }
        st.pushEnabled = false;
      } else {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') throw new Error(perm === 'denied'
          ? 'Notifications are blocked for this site in your browser settings.'
          : 'Notifications were not allowed.');
        const status = await fetchClientPushStatus(this.cfg.apiBase);
        if (!status?.vapidKey) throw new Error('Notifications are not set up for this business yet.');
        let prev: string | null = null;
        try { prev = localStorage.getItem('pp_push_endpoint'); } catch { prev = null; }
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: vapidKeyBytes(status.vapidKey).buffer as ArrayBuffer });
        const r = await subscribeClientPush(this.cfg.apiBase, sub.toJSON(), prev);
        try { localStorage.setItem('pp_push_endpoint', sub.endpoint); } catch { /* no storage */ }
        st.pushDevices = r.devices;
        st.pushEnabled = true;
        trackWidgetEvent('push_enabled', {});
      }
    } catch (err: any) {
      st.pushError = err?.message || 'Could not change notifications.';
    }
    st.pushBusy = false;
    this.render();
  }

  /** The manifest's start URL carries the client's standing link once they are
   *  signed in, so an app added to the home screen opens signed in (its storage
   *  is its own on an iPhone — Tim, 2026-11-02). Browsers read the manifest at
   *  install time, so the link is swapped as soon as the session exists. */
  private _manifestPersonalised = false;
  private async personaliseManifest(): Promise<void> {
    if (this._manifestPersonalised || !this.cfg.apiBase || !this.state.clientId || !this.state.isStandalone) return;
    if (typeof document === 'undefined') return;
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return;
    this._manifestPersonalised = true;
    const own = await fetchOwnPortalLink(this.cfg.apiBase);
    if (!own?.token) return;
    const href = link.getAttribute('href') || '';
    link.setAttribute('href', `${href.split('?')[0]}?magic=${encodeURIComponent(own.token)}`);
  }


  /** Validate and apply a promo / referral code to the cart (the Apply button,
   *  and a `?ref=` link once the contact step is reached). */
  private async applyPromoCode(code: string, input?: HTMLInputElement | null): Promise<void> {
    if (!this.cfg.apiBase) return;

    // A gift card NUMBER typed as a promo code (all digits, 6+): never sent
    // to the promo check — it is moved to the deposit step's gift card
    // field and the visitor is told where it went (Tim, 2026-09-27).
    if (/^\d{6,}$/.test(code.replace(/[\s-]/g, ''))) {
      this.state.promoStatus = 'invalid';
      if (this.state.depositGiftCard?.enabled) {
        this.state.depositGcNumber = code.replace(/[\s-]/g, '');
        this.state.depositGcOpen = true;
        this.state.promoError = "That looks like a gift card number. Gift cards go on the payment step, right after this one — we've put it there for you.";
      } else {
        this.state.promoError = "That looks like a gift card number — this box is for promo codes. Please bring your gift card to your appointment.";
      }
      this.state.formData['promo'] = '';
      if (input) input.value = '';
      this.render();
      return;
    }
    const totalAmount = this.state.items.reduce((sum, it) => sum + it.totalPrice, 0);
    this.state.promoStatus = 'loading';
    this.render();
    // Each line's add-ons ride along so a discount scoped to specific
    // ENHANCEMENTS can be priced on just those add-ons.
    const promoItems = this.state.items.map(it => ({
      serviceId: it.service.id, price: it.totalPrice,
      addons: (it.selectedAddons || []).map(a => ({ id: a.id, name: a.name, price: Number(a.price) || 0 })),
    }));
    // For someone else's visit, their phone (the referral is theirs to redeem).
    const fd = this.state.formData;
    const forOther = !!(fd.forOther || this.state.bookForOther);
    const otherPhone = this.state.bookForOther ? this.state.otherPhone : String(fd.otherPhone || '');
    const who = forOther ? otherPhone : String(fd.phone || '');
    const result = await validatePromoCode(this.cfg.apiBase, code, totalAmount, promoItems, who, forOther);
    this._promoWasReferral = !!result.isReferral;
    if (result.valid) {
      this.state.promoStatus = 'valid';
      this.state.promoOffer = {
        offerId: result.offerId!,
        offerName: result.offerName!,
        discountAmount: result.discountAmount!,
        discountLabel: result.discountLabel!,
      };
    } else {
      this.state.promoStatus = 'invalid';
      this.state.promoError = result.error || 'Invalid promo code.';
      this.state.promoOffer = null;
    }
    this.render();
  }

  /** The applied code is a referral: a phone typed afterwards re-checks it
   *  (new clients only / once — the client is known only once the phone is). */
  private _promoWasReferral = false;
  private _recheckReferralTimer: ReturnType<typeof setTimeout> | null = null;
  private _recheckReferral(): void {
    const code = String(this.state.formData['promo'] || '').trim();
    if (!code || !this._promoWasReferral) return;
    if (this._recheckReferralTimer) clearTimeout(this._recheckReferralTimer);
    this._recheckReferralTimer = setTimeout(() => { void this.applyPromoCode(code); }, 600);
  }

  /** A `?ref=` code waiting for the cart: applied the first time the contact step shows. */
  private _pendingPromo = '';
  private _applyPendingPromo(): void {
    if (!this._pendingPromo || this.state.step !== 'book-contact' || !this.state.items.length) return;
    const code = this._pendingPromo;
    this._pendingPromo = '';
    this.state.formData['promo'] = code;
    void this.applyPromoCode(code);
  }

  private async handleInstallApp(): Promise<void> {
    const st = this.state;
    const p = st.installPrompt;
    if (!p) { st.installHelpOpen = !st.installHelpOpen; this.render(); return; }
    try {
      await p.prompt();
      const choice = await p.userChoice;
      if (choice?.outcome === 'accepted') st.installDone = true;
    } catch { /* the prompt can only be used once; nothing to do */ }
    st.installPrompt = null;
    this.render();
  }

  /** The same-day state for the portal home card: a green light when
   *  something is free right now, a red light when nothing is; no card at all
   *  while Open Today is off. Tim (2026-10-13) wants no counts and no times here. */
  private async _loadSameDayToday(): Promise<void> {
    try {
      const base = (this.cfg.apiBase || '').replace(/\/+$/, '');
      if (!base) return;
      const params = new URLSearchParams();
      if (this.cfg.locationId) params.set('location_id', String(this.cfg.locationId));
      const resp = await fetch(`${base}/api/v1/quick-book/today/${params.toString() ? '?' + params : ''}`);
      if (!resp.ok) { this.state.sameDayToday = null; return; }
      const d = await resp.json();
      if (!d?.enabled) { this.state.sameDayToday = null; return; }
      const slots = Array.isArray(d?.slots) ? d.slots : [];
      this.state.sameDayToday = { open: slots.length > 0 };
    } catch { this.state.sameDayToday = null; }
  }

  /** The portal's 'Open today' page: the same-day surface mounted inside the
   *  panel body. Its 'see all times' runs the ordinary portal booking. */
  private _todayHost: HTMLElement | null = null;
  private _syncTodaySurface(_stepChanged: boolean): void {
    const host = this.bodyEl.querySelector<HTMLElement>('#pp-portal-today-host');
    if (this.state.step !== 'today' || !host) {
      if (this._todayHost) { try { window.BookingWidgetQuickBook?.unmount?.(this._todayHost); } catch { /* gone */ } this._todayHost = null; }
      return;
    }
    if (host === this._todayHost && host.firstChild) return;
    // render() rebuilt the body: the old host is detached — stop its clocks
    // and fetches before mounting into the new one.
    if (this._todayHost && this._todayHost !== host) { try { window.BookingWidgetQuickBook?.unmount?.(this._todayHost); } catch { /* gone */ } }
    this._todayHost = host;
    void this._loadQuickBook().then(mod => {
      if (!mod || this.state.step !== 'today') return;
      mod.mount(host, {
        apiBase: this.cfg.apiBase || '', spaName: this.state.spaName,
        businessPhone: this.state.businessPhone || this.cfg.businessPhone || '',
        accentColor: this.cfg.accentColor, analyticsSessionId: getAnalyticsSessionId(),
        locationId: this.cfg.locationId ?? null, layout: 'full', surface: 'inline',
        onOpenFull: () => { void this.handleBookNow(false); },
      });
    });
  }

  /** The home banner's 'Pay deposit': the ordinary in-portal deposit step on
   *  that booking (what the 'you already have a time held' screen does). */
  private async handlePortalPayDeposit(bookingId: number): Promise<void> {
    const b = this.state.pendingDepositBooking?.id === bookingId ? this.state.pendingDepositBooking : null;
    const link = b?.deposit ? depositFromResponse({ status: 'pending_deposit', ...(b.deposit as any) }) : null;
    if (!b || !link) {
      this.state.portalBookings = null;
      await this.loadBookingsIfNeeded();
      alert('That deposit link is no longer available — the hold may have lapsed.');
      this.render();
      return;
    }
    this.state.lastBookingId = b.id;
    this.state.lastBookingConfCode = b.confirmationCode || '';
    this.state.beginDepositStep(link);
    await this.revealPaymentStep();
  }

  /** 'Send us a message': the same inbox thread as the website's 'get back to
   *  me' form; the spa replies by text. */
  private async handlePortalMessage(): Promise<void> {
    const st = this.state;
    if (!this.cfg.apiBase || !st.clientId || st.messageSending) return;
    const text = st.messageText.trim();
    if (!text) { st.messageError = 'Write a message first.'; this.render(); return; }
    st.messageSending = true; st.messageError = null; this.render();
    try {
      await sendBookingCallback(this.cfg.apiBase, {
        name: st.client?.name || '', phone: st.client?.phone || '', message: text,
        preference: st.callbackPreference,
      });
      st.messageSent = true;
      st.messageText = '';
      trackWidgetEvent('portal_message_sent', {});
    } catch (err: any) {
      st.messageError = /429/.test(String(err?.message)) ? 'Please wait a little while before sending another message.' : 'Your message could not be sent. Please try again or call us.';
    }
    st.messageSending = false;
    this.render();
  }

  /** Open the fill page of one of the client's outstanding forms. */
  private async handlePortalFillForm(submissionId: number): Promise<void> {
    if (!this.cfg.apiBase || this.state.portalFormBusy) return;
    this.state.portalFormBusy = true; this.render();
    try {
      const url = await fetchFormFillLink(this.cfg.apiBase, submissionId);
      if (url) window.open(url, '_blank', 'noopener');
      // When they come back the form may be done: reload the list.
      this.state.formSubmissions = null;
    } catch (err: any) {
      alert(err?.message || 'Could not open the form.');
    }
    this.state.portalFormBusy = false; this.render();
  }

  /** 'Update it': a fresh copy of an intake form filled in over a year ago. */
  private async handlePortalRefreshForm(templateId: number): Promise<void> {
    const st = this.state;
    if (!this.cfg.apiBase || !st.clientId || st.portalFormBusy) return;
    st.portalFormBusy = true; this.render();
    try {
      const created = await createPortalFormSubmission(this.cfg.apiBase, templateId, st.clientId, st.client?.name || '');
      const url = await fetchFormFillLink(this.cfg.apiBase, created.id);
      if (url) window.open(url, '_blank', 'noopener');
      st.formSubmissions = null;
    } catch (err: any) {
      alert(err?.message || 'Could not start the form.');
    }
    st.portalFormBusy = false; this.render();
  }

  private async handleEmailReceipt(): Promise<void> {
    const st = this.state;
    const sale = st.selectedSale;
    if (!this.cfg.apiBase || !sale || st.receiptEmailState === 'sending') return;
    st.receiptEmailState = 'sending'; st.receiptEmailNote = ''; this.render();
    try {
      const res = await emailMyReceipt(this.cfg.apiBase, sale.id);
      st.receiptEmailState = res.emailSent ? 'sent' : 'error';
      st.receiptEmailNote = res.emailSent ? `Sent to ${res.sentTo || 'your email'}` : 'The email could not be sent.';
    } catch (err: any) {
      st.receiptEmailState = 'error';
      st.receiptEmailNote = err?.message || 'The email could not be sent.';
    }
    this.render();
  }

  /** "Book for me & someone else": the public flow (groups, several
   *  services, guest details) for the signed-in client. */
  private async handlePortalGroupBooking(): Promise<void> {
    if (!this.cfg.apiBase) return;
    this.state.portalGroupBooking = true;
    this.state.entryMode = 'booking';
    await this.handleBookNow(false);
    if (this.state.allowGroupBookings && this.state.maxGroupSize >= 2) this.state.setGroupSize(2);
    this.render();
  }

  /** Leaving the group flow (done, or back out of it): the portal again. */
  private _endPortalGroupBooking(): void {
    if (!this.state.portalGroupBooking) return;
    this.state.portalGroupBooking = false;
    this.state.entryMode = 'portal';
    this.state.resetBookingFlow();
  }

  private async handlePortalAddCard(): Promise<void> {
    if (!this.cfg.apiBase || !this.state.clientId) return;
    // Stripe configured: the card lives in Stripe's field — tokenize it and
    // attach the payment method. The manual fields below only exist without
    // Stripe, so reading them here used to fail with 'enter a valid card
    // number' for every spa that takes cards online.
    if (this.state.stripePublishableKey && isStripeEnabled()) {
      this.state.portalPurchaseStep = 'processing';
      this.state.portalPurchaseError = null;
      this.render();
      try {
        const pmId = await createPaymentMethod({});
        await addCreditCard(this.cfg.apiBase, this.state.clientId, { stripePaymentMethodId: pmId });
        this.state.wallet = null;
        await this.loadWalletIfNeeded();
        this.state.portalPurchaseStep = 'confirm';
      } catch (err: any) {
        this.state.portalPurchaseStep = 'add-card';
        this.state.portalPurchaseError = err?.message || 'Could not save card. Please try again.';
      }
      this.render();
      return;
    }
    const root = this.bodyEl;

    const cardNumber = (root.querySelector('[data-field="cardNumber"]') as HTMLInputElement)?.value?.replace(/\s/g, '') || '';
    const expiry = (root.querySelector('[data-field="expiry"]') as HTMLInputElement)?.value || '';
    const cvc = (root.querySelector('[data-field="cvc"]') as HTMLInputElement)?.value || '';

    // Basic validation
    if (cardNumber.length < 13) {
      this.state.portalPurchaseError = 'Please enter a valid card number.';
      this.render();
      return;
    }
    const expiryMatch = expiry.match(/^(\d{2})\/(\d{2})$/);
    if (!expiryMatch) {
      this.state.portalPurchaseError = 'Please enter expiry as MM/YY.';
      this.render();
      return;
    }
    if (cvc.length < 3) {
      this.state.portalPurchaseError = 'Please enter a valid CVC.';
      this.render();
      return;
    }

    const expMonth = parseInt(expiryMatch[1], 10);
    const expYear = 2000 + parseInt(expiryMatch[2], 10);
    const last4 = cardNumber.slice(-4);
    // Determine brand from first digit
    const first = cardNumber[0];
    const brand = first === '4' ? 'Visa' : first === '5' ? 'Mastercard' : first === '3' ? 'Amex' : 'Card';

    this.state.portalPurchaseStep = 'processing';
    this.state.portalPurchaseError = null;
    this.render();

    try {
      await addCreditCard(this.cfg.apiBase, this.state.clientId, { last4, brand, expMonth, expYear });
      // Refresh wallet to pick up the new card
      this.state.wallet = null;
      await this.loadWalletIfNeeded();
      // Return to confirm step
      this.state.portalPurchaseStep = 'confirm';
    } catch (err: any) {
      this.state.portalPurchaseStep = 'add-card';
      this.state.portalPurchaseError = err.message || 'Could not save card. Please try again.';
    }
    this.render();
  }

  // ── Portal: client cancel / reschedule ─────────────────────────────

  /** Confirm / 'I'm here' from the appointment details (Tim, 2026-10-18). */
  private async handlePortalBookingAction(bookingId: number, kind: 'confirm' | 'checkin'): Promise<void> {
    const st = this.state;
    if (!this.cfg.apiBase || !st.clientId || st.portalActionBusy) return;
    st.portalActionBusy = true; this.render();
    try {
      const res = kind === 'confirm'
        ? await clientConfirmBooking(this.cfg.apiBase, bookingId)
        : await clientCheckinBooking(this.cfg.apiBase, bookingId);
      if (!res.ok) { alert(res.error || 'Something went wrong — please call or text us.'); return; }
      const b = (st.portalBookings || []).find(x => x.id === bookingId);
      if (b && res.status) b.status = res.status as typeof b.status;
      trackWidgetEvent(kind === 'confirm' ? 'portal_confirm' : 'portal_checkin', {});
    } finally {
      st.portalActionBusy = false; this.render();
    }
  }

  /** A portal offer applied to an appointment already on the books, from
   *  the home banner (Tim, 2026-10-18). */
  private async handlePortalApplyOffer(promotionId: number, bookingId: number): Promise<void> {
    const st = this.state;
    if (!this.cfg.apiBase || !st.clientId || st.portalActionBusy) return;
    const p = (st.wallet?.promotions || []).find(x => Number(x.id) === promotionId);
    const b = (st.portalBookings || []).find(x => x.id === bookingId);
    if (!p || !b) return;
    if (!confirm(`Apply "${p.label}" to your ${formatDate(b.date)} appointment?`)) return;
    st.portalActionBusy = true; this.render();
    try {
      const res = await clientApplyOffer(this.cfg.apiBase, bookingId, promotionId);
      if (!res.ok) { alert(res.error || 'Something went wrong — please call or text us.'); return; }
      trackWidgetEvent('portal_apply_offer', {});
      // Re-read the wallet and bookings so the banner goes and the detail says it
      // (the prefetch keeps a wallet it already holds).
      st.wallet = null;
      await this.prefetchDashboardData();
      alert(`Done — ${p.label} is applied to your ${formatDate(b.date)} appointment${res.discountAmount ? ` (${formatCurrency(res.discountAmount)} off)` : ''}.`);
    } finally {
      st.portalActionBusy = false; this.render();
    }
  }

  private async handleClientCancelBooking(bookingId: number): Promise<void> {
    if (!this.cfg.apiBase || !this.state.clientId) return;
    // The server says whether this is a cancel or a REQUEST to cancel, and
    // what it costs this close to the appointment — said before they confirm.
    const terms = this.state.selectedBooking?.selfService?.cancel;
    const asking = terms?.mode === 'request';
    const cost = [terms ? lateChangeNote(terms, 'Cancelling') : '', terms ? depositRefundNote(terms) : '']
      .filter(Boolean).join('\n\n');
    const ask = asking
      ? 'Send us a request to cancel this appointment? It stays booked until we approve it.'
      : 'Are you sure you want to cancel this booking?';
    if (!confirm(cost ? `${ask}\n\n${cost}` : ask)) return;
    let message = '';
    if (asking) message = (prompt('Anything we should know? (optional)') || '').trim();
    this.state.cancelLoading = true;
    this.render();
    try {
      const result = asking
        ? await clientRequestChange(this.cfg.apiBase, bookingId, { kind: 'cancel', message })
        : await clientCancelBooking(this.cfg.apiBase, bookingId);
      if (result.ok) {
        // Refresh bookings list
        this.state.portalBookings = null;
        this.state.goBack();
        this.render();
        await this.loadBookingsIfNeeded();
        if (asking) this._showPortalError('Request sent — we will be in touch shortly.');
      } else {
        this._showPortalError(result.error || 'Could not cancel booking.');
        // The rules changed under them (e.g. the no-changes window started) — show the current options.
        this.state.portalBookings = null;
        await this.loadBookingsIfNeeded();
      }
    } catch (_e) {
      this._showPortalError('Could not cancel booking. Please try again.');
    }
    this.state.cancelLoading = false;
    this.render();
  }

  private async handleClientWithdrawRequest(bookingId: number): Promise<void> {
    if (!this.cfg.apiBase) return;
    if (!confirm('Withdraw your request? Your appointment stays as it is.')) return;
    await clientWithdrawRequest(this.cfg.apiBase, bookingId);
    this.state.portalBookings = null;
    this.state.goBack();
    this.render();
    await this.loadBookingsIfNeeded();
  }

  private async handleClientRescheduleBooking(bookingId: number): Promise<void> {
    // Navigate into the booking flow to pick a new date/time. The home
    // screen's Reschedule names the booking by id with nothing selected yet
    // (it used to read selectedBooking and do nothing — Tim, 2026-11-02).
    if (!this.cfg.apiBase) return;
    this.state.selectedBookingId = bookingId;
    const booking = this.state.selectedBooking;
    if (!booking) return;

    const terms = booking.selfService?.reschedule;
    const asking = terms?.mode === 'request';
    const cost = terms ? lateChangeNote(terms, 'Rescheduling') : '';
    if (asking || cost) {
      const lead = asking
        ? 'Pick the time you would like and we will send it to us as a request. Your appointment stays where it is until we approve it.'
        : 'Pick a new time for this appointment.';
      if (!confirm(cost ? `${lead}\n\n${cost}` : lead)) return;
    }

    // Load booking data if not already loaded
    if (!this.state.bookingDataLoaded) await this.ensureBookingData();

    // The WHOLE appointment goes into the cart, matched by id (a name on the
    // row can differ from the menu's — a staff override, a display name — and
    // a miss left a stale `curr` behind: the provider step crashed on a null
    // service and the date grid was drawn for the wrong thing, 2026-11-02).
    // The grid then chains every service exactly as the server will move them.
    // A REQUEST is approved by staff on the same provider, so it stays pinned;
    // otherwise only a therapist they asked for is kept (Tim, 2026-11-02).
    const cart = this._rescheduleCart(booking, asking, false);
    if (!cart.length) {
      this._showPortalError('We could not load this appointment for rescheduling — please call or text us and we will move it for you.');
      this.render();
      return;
    }
    this.state.startBooking(false);
    this.state.rescheduleAsRequest = asking;
    this.state.rescheduleBookingId = bookingId;
    this.state.rescheduleAnyProvider = false;
    this.state.rescheduleRequestedNames = asking ? [] : [...new Set(booking.items
      .filter(it => (it.providerPreference || '') === 'requested' && it.staffName).map(it => it.staffName))];
    this.state.items = cart;
    this.state.curr = { service: cart[0].service, selectedAddons: cart[0].selectedAddons, provider: cart[0].provider, totalPrice: cart[0].totalPrice };
    if (booking.locationId) this.state.selectedLocationId = booking.locationId;
    this.state.initDatetime();
    this.state.navigate('book-datetime');
    this.render();
  }

  /** The visit as a cart for the reschedule grid, with filter providers
   *  ('Anyone', female / male) carrying the working days of the people they
   *  stand for on that service — the same as the provider step makes them. */
  private _rescheduleCart(booking: import('./types').Booking, pinAll: boolean, anyProvider: boolean) {
    const cart = rescheduleCart(booking.items, this.state.categories, this.state.providers, this.state.enhGroups,
      { pinAll, anyProvider });
    const keep = this.state.curr;
    for (const it of cart) {
      if (!it.provider?.type?.startsWith('filter')) continue;
      this.state.curr = { service: it.service, selectedAddons: it.selectedAddons, provider: null, totalPrice: it.totalPrice };
      const p = this.state.providersForStep.find(x => String(x.id) === String(it.provider.id));
      if (p) it.provider = p;
    }
    this.state.curr = keep;
    return cart;
  }

  /** Open (or close) a requested therapist's visit to everyone's times. */
  private _rescheduleOpenToAll(open: boolean): void {
    const st = this.state;
    const booking = st.portalBookings?.find(b => b.id === st.rescheduleBookingId);
    if (!booking) return;
    const cart = this._rescheduleCart(booking, false, open);
    if (!cart.length) return;
    st.rescheduleAnyProvider = open;
    st.items = cart;
    st.curr = { service: cart[0].service, selectedAddons: cart[0].selectedAddons, provider: cart[0].provider, totalPrice: cart[0].totalPrice };
    st.selectedTime = null;
    if (st.selectedDate) st.setDate(st.selectedDate);
    trackWidgetEvent(open ? 'reschedule_opened_to_all' : 'reschedule_requested_only', {});
    this.render();
  }

  /** 'Add to my calendar' on an appointment: the server's .ics for THAT
   *  booking, handed to the browser as a download (the phone's calendar app
   *  opens it). */
  private async handleAddToCalendar(bookingId: number): Promise<void> {
    if (!this.cfg.apiBase) return;
    const text = await fetchBookingIcs(this.cfg.apiBase, bookingId);
    if (!text) {
      this._showPortalError('We could not build the calendar file — please try again.');
      this.render();
      return;
    }
    const code = this.state.portalBookings?.find(b => b.id === bookingId)?.confirmationCode || 'appointment';
    const blob = new Blob([text], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${code}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    trackWidgetEvent('portal_add_to_calendar', { bookingId });
  }

  private async handleRescheduleSubmit(): Promise<void> {
    const bookingId = this.state.rescheduleBookingId;
    if (!bookingId || !this.cfg.apiBase || !this.state.selectedDate || !this.state.selectedTime) return;

    this.state.rescheduleLoading = true;
    this.render();
    try {
      const asking = this.state.rescheduleAsRequest;
      const result = asking
        ? await clientRequestChange(this.cfg.apiBase, bookingId, {
            kind: 'reschedule', date: this.state.selectedDate, time: this.state.selectedTime })
        : await clientRescheduleBooking(
            this.cfg.apiBase, bookingId,
            this.state.selectedDate, this.state.selectedTime,
            this.state.rescheduleAnyProvider,
          );
      if (result.ok) {
        this.state.rescheduleBookingId = null;
        this.state.rescheduleAsRequest = false;
        this.state.items = [];
        this.state.curr = { service: null, selectedAddons: [], provider: null, totalPrice: 0 };
        this.state.rescheduleAnyProvider = false;
        this.state.rescheduleRequestedNames = [];
        if (asking) setTimeout(() => this._showPortalError('Request sent — we will be in touch shortly.'), 0);
        // A therapist was not free at the new time: say who it is with now.
        const changes = (result as { providerChanges?: { serviceName: string; to: string }[] }).providerChanges || [];
        if (!asking && changes.length) {
          const line = changes.map(c => `${c.serviceName} is now with ${c.to}`).join('; ');
          setTimeout(() => this._showPortalError(`Moved. ${line}.`), 0);
        }
        this.state.portalBookings = null;
        // Go back to bookings list
        this.state.stepHistory = [];
        this.state.step = 'bookings';
        this.render();
        await this.loadBookingsIfNeeded();
      } else {
        this._showPortalError(result.error || 'Could not reschedule booking.');
      }
    } catch (_e) {
      this._showPortalError('Could not reschedule booking. Please try again.');
    }
    this.state.rescheduleLoading = false;
    this.render();
  }

  private _showPortalError(msg: string): void {
    this.state.portalError = msg;
    this.render();
    setTimeout(() => {
      if (this.state.portalError === msg) {
        this.state.portalError = null;
        this.render();
      }
    }, 5000);
  }

  // ── Portal: booking ──────────────────────────────────────────────────

  private async handleBookNowOrRebook(): Promise<void> {
    await this.loadBookingsIfNeeded();
    const bookings = this.state.portalBookings || [];
    const pastBookings = bookings
      .filter(b => b.status !== 'cancelled')
      .sort((a, b) => (b.date > a.date ? 1 : b.date < a.date ? -1 : 0))
      .slice(0, 3);

    if (pastBookings.length > 0) {
      this.state.recentBookings = pastBookings;
      this.state.rebookSource = null;
      this.state.navigate('book-rebook-pick');
      this.render();
      if (!this.state.bookingDataLoaded) this.ensureBookingData();
    } else {
      await this.handleBookNow(false);
    }
  }

  private async handleBookNow(forOther = false): Promise<void> {
    if (!this.cfg.apiBase) return;
    if (!this.state.bookingDataLoaded) {
      this.state.bookingDataLoading = true;
      this.state.startBooking(forOther);
      this.render();
      await this.ensureBookingData();
    } else {
      this.state.startBooking(forOther);
      this.render();
    }
  }

  private async handleRebookAppointment(bookingId: number): Promise<void> {
    const booking = this.state.recentBookings.find(b => b.id === bookingId);
    if (!booking || !booking.items.length) return;

    await this.ensureBookingData();

    const firstItem = booking.items[0] as any;

    // Find the service — by id first (names drift: renamed services, "60 Minute
    // Massage" vs "60 Minute Massage (Amber and Sage)"), then case-insensitive
    // name. Only online-bookable services are in the catalogue.
    const wantId = firstItem.serviceId != null ? Number(firstItem.serviceId) : null;
    const wantName = String(firstItem.serviceName || '').trim().toLowerCase();
    let matchedService: Service | null = null;
    for (const cat of this.state.categories) {
      for (const svc of cat.services) {
        if ((wantId != null && Number(svc.id) === wantId) ||
            (!!wantName && String(svc.name || '').trim().toLowerCase() === wantName)) {
          matchedService = svc;
          break;
        }
      }
      if (matchedService) break;
    }

    if (!matchedService) {
      // Say why instead of silently dropping the client at the top of the menu.
      this.state.rebookSource = null;
      this.state.startBooking(false);
      this.state.bookingError = `"${firstItem.serviceName || 'That service'}" isn't available to book online right now — please choose from the menu or call us.`;
      this.render();
      return;
    }

    // Find the provider — by staff id, then name; if they no longer take online
    // bookings, fall back to "Anyone" (or any provider) rather than bailing out.
    const wantStaffId = firstItem.staffId != null ? Number(firstItem.staffId) : null;
    const wantStaff = String(firstItem.staffName || '').trim().toLowerCase();
    let matchedProvider = this.state.providers.find(p =>
      (wantStaffId != null && Number(p.id) === wantStaffId) ||
      (!!wantStaff && String(p.name || '').trim().toLowerCase() === wantStaff)) || null;
    if (matchedProvider && Array.isArray((matchedProvider as any).serviceIds) &&
        (matchedProvider as any).serviceIds.length &&
        !(matchedProvider as any).serviceIds.map(Number).includes(Number(matchedService.id))) {
      matchedProvider = null;   // they exist but no longer offer this service online
    }
    if (!matchedProvider) {
      matchedProvider = this.state.providers.find(p => p.type === 'filter-any')
        || this.state.providers.find(p => !p.type && Array.isArray((p as any).serviceIds) &&
                                           (p as any).serviceIds.map(Number).includes(Number(matchedService!.id)))
        || null;
    }
    if (!matchedProvider) {
      this.state.rebookSource = null;
      this.state.startBooking(false);
      this.state.bookingError = `No provider is available online for "${matchedService.name}" right now — please call us to rebook.`;
      this.render();
      return;
    }

    // Resolve addons
    const addonIds = firstItem.addonIds || [];
    const selectedAddons: Addon[] = [];
    for (const aid of addonIds) {
      for (const gid of Object.keys(this.state.enhGroups)) {
        const grp = this.state.enhGroups[gid];
        const addon = grp.addOns.find(a => String(a.id) === String(aid));
        if (addon) selectedAddons.push(addon);
      }
    }

    const addonSum = selectedAddons.reduce((s, a) => s + a.price, 0);

    // Set up rebook state — carry forward the original location
    this.state.rebookSource = booking;
    this.state.selectedLocationId = booking.locationId ?? null;
    this.state.curr = {
      service: matchedService,
      selectedAddons,
      provider: matchedProvider,
      totalPrice: matchedService.price + addonSum,
    };
    // Commit the rebook item so _buildUnionWorkDays() has provider workDays
    this.state.items = [{
      service: matchedService,
      selectedAddons,
      provider: matchedProvider,
      totalPrice: matchedService.price + addonSum,
      guestIndex: 0,
    }];
    this.state.bookForOther = false;
    this.state.bookingSubmitting = false;
    this.state.bookingError = null;
    this.state.initDatetime();

    // Navigate to datetime (skip categories/enhancements/provider) — unless
    // the visit being rebooked never answered a REQUIRED add-on group (booked
    // before that was enforced): ask now, the server refuses it otherwise.
    this.state.navigate(this.state.missingRequiredEnhGroups.length ? 'book-enhancements' : 'book-datetime');
    this.render();
  }

  private async handleConfirmBooking(): Promise<void> {
    if (!this.cfg.apiBase || this.state.bookingSubmitting) return;
    // Book-for-other: a blocked Confirm explains itself instead of sitting dead.
    if (this.state.bookForOther && !this.state.otherValid) {
      this.state.showBlockedSummary = true;
      this.render();
      const first = this.state.blockedReasons[0];
      if (first) this.scrollToMissing(first.field);
      return;
    }
    this.state.bookingSubmitting = true;
    this.state.bookingError = null;
    this.render();
    try {
      // Booking for someone else: the typed name/phone/email ride on the
      // payload and the SERVER finds or creates that person by phone (the
      // same rule as the public flow). The widget used to search the whole
      // client base from the client's own token and attach an alias to
      // whichever record matched — a stranger's (2026-10-12 review).

      const payload = this.state.buildBookingPayload() as Record<string, unknown>;
      trackWidgetEvent('booking_submit', { itemCount: this.state.items.length });
      const bookingResult2 = await submitBooking(this.cfg.apiBase, payload);
      if (bookingResult2) {
        this.state.lastBookingId = bookingResult2.id || null;
        this.state.lastBookingConfCode = bookingResult2.confirmationCode || '';
        this.state.lastBookingClientId = bookingResult2.clientId || null;
      }
      trackWidgetEvent('booking_success', { itemCount: this.state.items.length });
      this.state.bookingSubmitting = false;
      this.state.stepHistory = ['dashboard'];
      if (await this.tryDepositStep(bookingResult2)) { this.render(); return; }
      if (bookingResult2?.cardOnFileNeeded || this._lastOutcomeCardNeeded) {
        // Deposit waived by the reserved session; the spa still wants a card.
        this._lastOutcomeCardNeeded = false;
        this.state.cardOnFileError = null;
        this.state.setStep('book-card-on-file');
        this.render();
        return;
      }
      this._clearDraft();
      this.state.setStep('book-success');
      this.render();
    } catch (err) {
      if (this._onBookingRefused(err)) return;
      this.state.bookingError = (err as Error).message || 'Booking could not be submitted. Please try again.';
      this.state.bookingSubmitting = false;
      this.render();
    }
  }
}

// ── Bootstrap ──────────────────────────────────────────────────────────────

import { catalogChunkUrl, deriveApiBaseFromScriptUrl, findWidgetScriptSrc, priceListChunkUrl, quickBookChunkUrl } from './bootstrap-utils';
import { initSentry } from './sentry';

// Initialise error monitoring before anything else (no-op unless configured).
initSentry();

const cfg: WidgetConfig =
  (typeof window !== 'undefined' && window.BookingWidget) || {};

if (!cfg.apiBase) {
  const derived = deriveApiBaseFromScriptUrl(findWidgetScriptSrc());
  if (derived) cfg.apiBase = derived;
}

// ONE widget per page. A site that includes the script twice (a snippet pasted
// into both the header and a page block) used to get two complete widgets —
// two panels, two sets of click handlers, two Stripe card fields sharing the
// same element ids. The second copy stands down; the first keeps the page.
// (Defensive: no tenant is known to do this. A tag inside an HTML comment does
// not count — browsers never run it.)
const _w = window as unknown as { __calendoulaWidgetBooted?: boolean };
if (_w.__calendoulaWidgetBooted) {
  console.warn('[Calendoula] The booking widget script is on this page more than once — the extra copy was ignored. Remove the duplicate <script> tag.');
} else {
  _w.__calendoulaWidgetBooted = true;
  window.BookingWidget = cfg;

  const widget = new BookingWidget(cfg);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => widget.init());
  } else {
    widget.init();
  }
}
